import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Plus, FlaskConical, Trash2, ChevronLeft, ChevronRight, ChevronDown, X, Link2, FileDown, Copy, FileText, RefreshCw, CheckCheck, Lightbulb, EyeOff, Eye, Table, Mail, Send, AlertTriangle, Save } from 'lucide-react'
import { pocApi, countryGuidesApi, pocCountryTemplatesApi, getAuthHeaders } from '@/api/client'
import type { PocRecord } from './types'
import { deriveMatchLabel } from './data/discoveryChecklist'
import { COUNTRIES } from './data/countries'
import * as XLSX from 'xlsx'
import { VENDORS } from './data/vendorMetadata'
import { COUNTRY_ID_TYPES, UNIVERSAL_ID_TYPES } from '../MatchRateTool/data/idTypes'
import { exportSocureIntakePDF } from './utils/exportSocureIntake'
import { exportPocPDF } from './utils/exportPocPDF'
import { Info, Search, Loader2 } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { showToast } from '@/store/uiStore'
import { parseDelimitedTable, toCsv, checkExport } from './utils/csv'
import { CsvIntegrityWarning } from './components/CsvIntegrityWarning'
import { PocQualify } from './components/PocQualify'
import { PocOverview } from './components/PocOverview'

// ─── Debounced Input ────────────────────────────────────────────────────
export function DInput({ value, onSave, ...props }: { value: string; onSave: (v: string) => void } & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'>) {
  const [v, setV] = useState(value)
  useEffect(() => { setV(value) }, [value])
  return <input {...props} value={v} onChange={e => { setV(e.target.value); }} onBlur={() => { if (v !== value) onSave(v) }} />
}

export function DTextarea({ value, onSave, ...props }: { value: string; onSave: (v: string) => void } & Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange' | 'value'>) {
  const [v, setV] = useState(value)
  useEffect(() => { setV(value) }, [value])
  return <textarea {...props} value={v} onChange={e => { setV(e.target.value); }} onBlur={() => { if (v !== value) onSave(v) }} />
}

// ─── Multi Select ───────────────────────────────────────────────────────
export function MultiPick({ options, selected, onChange, placeholder }: { options: { id: string; label: string }[]; selected: string[]; onChange: (s: string[]) => void; placeholder: string }) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const filtered = q ? options.filter(o => o.label.toLowerCase().includes(q.toLowerCase())) : options
  return (
    <div className="relative">
      <div className="relative">
        <input value={q} onChange={e => { setQ(e.target.value); setOpen(true) }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 250)}
          placeholder={placeholder} className="w-full px-2 py-1.5 pl-7 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-xs text-[var(--text-primary)]" />
        <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
      </div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {selected.map(id => <span key={id} onClick={() => onChange(selected.filter(s => s !== id))} className="px-2 py-0.5 rounded text-[10px] font-semibold bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 cursor-pointer hover:bg-red-500/10 hover:text-red-400 transition-colors">{options.find(o => o.id === id)?.label || id} ×</span>)}
        </div>
      )}
      {open && filtered.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-xl max-h-48 overflow-y-auto">
          {filtered.slice(0, 40).map(o => (
            <div key={o.id} className={`px-3 py-1.5 hover:bg-[var(--bg-tertiary)] cursor-pointer flex items-center gap-2 text-xs ${selected.includes(o.id) ? 'text-cyan-400' : 'text-[var(--text-primary)]'}`}
              onMouseDown={() => onChange(selected.includes(o.id) ? selected.filter(s => s !== o.id) : [...selected, o.id])}>
              <span className={`w-3 h-3 rounded border text-[8px] flex items-center justify-center ${selected.includes(o.id) ? 'bg-cyan-500 border-cyan-500 text-white' : 'border-[var(--border-color)]'}`}>{selected.includes(o.id) ? '✓' : ''}</span>
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const STATUS_COLORS: Record<string, string> = {
  intake: 'bg-blue-500/20 text-blue-400', planning: 'bg-purple-500/20 text-purple-400',
  execution: 'bg-amber-500/20 text-amber-400', results: 'bg-cyan-500/20 text-cyan-400',
  completed: 'bg-green-500/20 text-green-400', cancelled: 'bg-red-500/20 text-red-400',
}

export const ic = 'w-full px-2 py-1.5 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-sm text-[var(--text-primary)]'
export const lc = 'text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide'

function DataSourcesView() {
  const [data, setData] = useState<any[]>([])
  const [recs, setRecs] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [vendorFilter, setVendorFilter] = useState('')
  const [expanded, setExpanded] = useState<number | null>(null)

  useEffect(() => {
    (async () => {
      try {
        const [srcRes, recRes] = await Promise.all([
          fetch('/api/coverage/sources', { headers: getAuthHeaders() }),
          fetch('/api/coverage/recommendations', { headers: getAuthHeaders() }),
        ])
        if (srcRes.ok) { const d = await srcRes.json(); setData(d?.data?.data || d?.data || []) }
        if (recRes.ok) { const d = await recRes.json(); setRecs(d?.data?.data || d?.data || []) }
      } catch {}
      setLoading(false)
    })()
  }, [])

  // Build a lookup: country -> recommended vendor(s)
  const recByCountry: Record<string, { vendor: string; price: string; status: string; notes: string }[]> = {}
  for (const r of recs) {
    const key = r.country || ''
    if (!recByCountry[key]) recByCountry[key] = []
    recByCountry[key].push({ vendor: r.recommended_vendor, price: r.sell_price, status: r.api_status, notes: r.notes })
  }

  const vendors = [...new Set(data.map(r => r.vendor).filter(Boolean))].sort()

  const filtered = data.filter(r => {
    if (r.purpose && r.purpose !== 'KYC') return false // Only show KYC sources, not national ID type rows
    if (search && !r.country?.toLowerCase().includes(search.toLowerCase()) && !r.country_code?.toLowerCase().includes(search.toLowerCase())) return false
    if (vendorFilter && r.vendor !== vendorFilter) return false
    return true
  })

  // Group by country
  const grouped: Record<string, any[]> = {}
  for (const r of filtered) {
    const key = r.country || 'Unknown'
    if (!grouped[key]) grouped[key] = []
    grouped[key].push(r)
  }

  const xmark = (v: number) => v ? '✓' : ''
  const xCls = (v: number) => v ? 'text-green-400 font-bold' : 'text-[var(--text-muted)] opacity-20'

  if (loading) return <div className="py-10 text-center"><Loader2 className="mx-auto animate-spin text-cyan-400" /></div>

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">KYC Data Sources</h2>
        <p className="text-xs text-[var(--text-muted)]">{filtered.length} sources across {Object.keys(grouped).length} countries · {vendors.length} vendors</p>
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search countries..." className="w-full pl-8 pr-3 py-1.5 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-sm text-[var(--text-primary)]" />
        </div>
        <select value={vendorFilter} onChange={e => setVendorFilter(e.target.value)} className="px-2 py-1.5 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-sm text-[var(--text-primary)]">
          <option value="">All vendors</option>
          {vendors.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>
      <div className="space-y-2" style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
        {Object.entries(grouped).map(([country, sources]) => {
          const countryRecs = recByCountry[country] || []
          const recVendors = countryRecs.map(r => r.vendor?.toLowerCase().trim())
          const topRec = countryRecs[0]

          return (
          <div key={country} className="rounded border border-[var(--border-color)] overflow-hidden">
            <div className="px-3 py-2 bg-[var(--bg-tertiary)] flex items-center justify-between cursor-pointer" onClick={() => setExpanded(expanded === sources[0]?.id ? null : sources[0]?.id)}>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-[var(--text-primary)]">{country}</span>
                <span className="text-[10px] text-[var(--text-muted)]">({sources[0]?.country_code})</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 font-semibold">{sources.length} source{sources.length !== 1 ? 's' : ''}</span>
                {topRec && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/10 text-green-400 border border-green-500/30 font-semibold">
                    ★ {topRec.vendor}{topRec.price && topRec.price !== 'TBC' ? ` @ ${topRec.price}` : ''}{topRec.status ? ` · ${topRec.status}` : ''}
                  </span>
                )}
              </div>
              <div className="flex gap-1 text-[10px]">
                {[...new Set(sources.map(s => s.vendor))].filter(Boolean).map(v => {
                  const isRec = recVendors.includes(v.toLowerCase().trim())
                  return (
                    <span key={v} className={`px-1.5 py-0.5 rounded ${isRec ? 'bg-green-500/10 text-green-400 border border-green-500/30 font-semibold' : 'bg-[var(--bg-secondary)] text-[var(--text-muted)]'}`}>{isRec && '★ '}{v}</span>
                  )
                })}
              </div>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[var(--bg-secondary)] border-t border-[var(--border-color)]">
                  <th className="text-left px-3 py-1 font-semibold text-[var(--text-muted)]">Vendor</th>
                  <th className="text-left px-2 py-1 font-semibold text-[var(--text-muted)]">Source</th>
                  <th className="text-center px-1 py-1 font-semibold text-[var(--text-muted)]" title="Government ID">Gov</th>
                  <th className="text-center px-1 py-1 font-semibold text-[var(--text-muted)]">Name</th>
                  <th className="text-center px-1 py-1 font-semibold text-[var(--text-muted)]">DOB</th>
                  <th className="text-center px-1 py-1 font-semibold text-[var(--text-muted)]">Addr</th>
                  <th className="text-center px-1 py-1 font-semibold text-[var(--text-muted)]">NatID</th>
                  <th className="text-center px-1 py-1 font-semibold text-[var(--text-muted)]">Phone</th>
                  <th className="text-right px-2 py-1 font-semibold text-[var(--text-muted)]">Cover</th>
                  <th className="text-left px-2 py-1 font-semibold text-[var(--text-muted)]">API</th>
                </tr>
              </thead>
              <tbody>
                {sources.map(s => {
                  const isRec = recVendors.includes((s.vendor || '').toLowerCase().trim())
                  return (
                  <tr key={s.id} className={`border-t border-[var(--border-color)] hover:bg-[var(--bg-secondary)] cursor-pointer ${isRec ? 'bg-green-500/5' : ''}`} onClick={() => setExpanded(expanded === s.id ? null : s.id)}>
                    <td className="px-3 py-1.5 font-medium text-[var(--text-primary)]">
                      {isRec && <span className="text-green-400 mr-1" title="Recommended vendor">★</span>}
                      {s.vendor}
                    </td>
                    <td className="px-2 py-1.5 text-[var(--text-secondary)]" title={s.description}>{s.data_source_header || s.description?.slice(0, 30)}</td>
                    <td className={`px-1 py-1.5 text-center ${xCls(s.government_id)}`}>{xmark(s.government_id) || '·'}</td>
                    <td className={`px-1 py-1.5 text-center ${xCls(s.has_first_name || s.has_last_name)}`}>{xmark(s.has_first_name || s.has_last_name) || '·'}</td>
                    <td className={`px-1 py-1.5 text-center ${xCls(s.has_dob)}`}>{xmark(s.has_dob) || '·'}</td>
                    <td className={`px-1 py-1.5 text-center ${xCls(s.has_address)}`}>{xmark(s.has_address) || '·'}</td>
                    <td className={`px-1 py-1.5 text-center ${xCls(s.has_national_id)}`}>{xmark(s.has_national_id) || '·'}</td>
                    <td className={`px-1 py-1.5 text-center ${xCls(s.has_phone)}`}>{xmark(s.has_phone) || '·'}</td>
                    <td className="px-2 py-1.5 text-right text-[var(--text-muted)]">{s.adult_coverage ? `${Math.round(s.adult_coverage * 100)}%` : '-'}</td>
                    <td className={`px-2 py-1.5 ${s.api_version === 'Both' || s.api_version === 'both' ? 'text-green-400' : s.api_version?.includes('Untested') ? 'text-amber-400' : 'text-[var(--text-secondary)]'}`}>{s.api_version || '-'}</td>
                  </tr>
                  )})}
              </tbody>
            </table>
            {/* Expanded detail for selected source */}
            {sources.some(s => s.id === expanded) && (() => {
              const s = sources.find(s => s.id === expanded)!
              return (
                <div className="px-3 py-2 bg-[var(--bg-primary)] border-t border-[var(--border-color)] space-y-1 text-xs">
                  {s.description && <div><span className="text-[var(--text-muted)]">Description:</span> <span className="text-[var(--text-secondary)]">{s.description}</span></div>}
                  {s.mandatory_pii && <div><span className="text-[var(--text-muted)]">Mandatory PII:</span> <span className="text-amber-400">{s.mandatory_pii}</span></div>}
                  {s.verifiable_elements && <div><span className="text-[var(--text-muted)]">Verifiable:</span> <span className="text-green-400">{s.verifiable_elements}</span></div>}
                  {s.optional_elements && <div><span className="text-[var(--text-muted)]">Optional:</span> <span className="text-[var(--text-secondary)]">{s.optional_elements}</span></div>}
                  {s.f1_deal_sheet && <div><span className="text-[var(--text-muted)]">Deal Sheet:</span> <span className="text-[var(--text-secondary)]">{s.f1_deal_sheet}</span></div>}
                  {s.national_id_name && <div><span className="text-[var(--text-muted)]">National ID:</span> <span className="text-cyan-400">{s.national_id_name}</span>{s.national_id_format ? ` (${s.national_id_format})` : ''}{s.national_id_example ? ` e.g. ${s.national_id_example}` : ''}</div>}
                  {s.consent_required ? <div className="text-red-400 font-medium">⚠ Consent required</div> : null}
                  {s.notes && <div><span className="text-[var(--text-muted)]">Notes:</span> <span className="text-[var(--text-secondary)]">{s.notes}</span></div>}
                </div>
              )
            })()}
          </div>
        )})}
      </div>
    </div>
  )
}

function CoverageView() {
  const [data, setData] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/coverage', { headers: getAuthHeaders() })
        if (res.ok) {
          const d = await res.json()
          setData(d?.data?.data || d?.data || [])
        }
      } catch {}
      setLoading(false)
    })()
  }, [])

  const filtered = search
    ? data.filter(r => r.country_name?.toLowerCase().includes(search.toLowerCase()) || r.country_code?.toLowerCase().includes(search.toLowerCase()) || r.region?.toLowerCase().includes(search.toLowerCase()))
    : data

  const flag = (v: number) => v ? '✓' : ''
  const flagCls = (v: number) => v ? 'text-green-400' : 'text-[var(--text-muted)] opacity-20'

  if (loading) return <div className="py-10 text-center"><Loader2 className="mx-auto animate-spin text-cyan-400" /></div>

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">Country Coverage</h2>
          <p className="text-xs text-[var(--text-muted)]">{data.length} countries · Source: FrankieOne internal spreadsheet</p>
        </div>
      </div>
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search countries..." className="w-full pl-8 pr-3 py-1.5 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-sm text-[var(--text-primary)]" />
      </div>
      <div className="rounded border border-[var(--border-color)] overflow-auto" style={{ maxHeight: 'calc(100vh - 300px)' }}>
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-[var(--bg-tertiary)] z-10">
            <tr>
              <th className="text-left px-3 py-2 font-semibold text-[var(--text-secondary)]">Country</th>
              <th className="text-left px-2 py-2 font-semibold text-[var(--text-secondary)]">Region</th>
              <th className="text-center px-2 py-2 font-semibold text-[var(--text-secondary)]" title="Biometrics / Document Verification">IDV</th>
              <th className="text-center px-2 py-2 font-semibold text-[var(--text-secondary)]" title="At least 1 KYC source">1+</th>
              <th className="text-center px-2 py-2 font-semibold text-[var(--text-secondary)]" title="At least 2 KYC sources">2+</th>
              <th className="text-center px-2 py-2 font-semibold text-[var(--text-secondary)]" title="Government ID match">Gov</th>
              <th className="text-center px-2 py-2 font-semibold text-[var(--text-secondary)]">KYB</th>
              <th className="text-center px-2 py-2 font-semibold text-[var(--text-secondary)]">AML</th>
              <th className="text-right px-2 py-2 font-semibold text-[var(--text-secondary)]">Sources</th>
              <th className="text-right px-2 py-2 font-semibold text-[var(--text-secondary)]">Coverage</th>
              <th className="text-right px-2 py-2 font-semibold text-[var(--text-secondary)]">Match %</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id} className="border-t border-[var(--border-color)] hover:bg-[var(--bg-secondary)]">
                <td className="px-3 py-1.5 font-medium text-[var(--text-primary)]">{r.country_name} <span className="text-[var(--text-muted)] font-normal">({r.country_code})</span></td>
                <td className="px-2 py-1.5 text-[var(--text-muted)]">{r.region || '-'}</td>
                <td className={`px-2 py-1.5 text-center font-bold ${flagCls(r.idv)}`}>{flag(r.idv) || '·'}</td>
                <td className={`px-2 py-1.5 text-center font-bold ${flagCls(r.kyc_one_plus)}`}>{flag(r.kyc_one_plus) || '·'}</td>
                <td className={`px-2 py-1.5 text-center font-bold ${flagCls(r.kyc_two_plus)}`}>{flag(r.kyc_two_plus) || '·'}</td>
                <td className={`px-2 py-1.5 text-center font-bold ${flagCls(r.kyc_gov_id)}`}>{flag(r.kyc_gov_id) || '·'}</td>
                <td className={`px-2 py-1.5 text-center font-bold ${flagCls(r.kyb_profile || r.kyb_registry_docs || r.kyb_ownership)}`}>{flag(r.kyb_profile || r.kyb_registry_docs || r.kyb_ownership) || '·'}</td>
                <td className={`px-2 py-1.5 text-center font-bold ${flagCls(r.aml)}`}>{flag(r.aml) || '·'}</td>
                <td className="px-2 py-1.5 text-right text-[var(--text-secondary)]">{r.data_source_count || '-'}</td>
                <td className="px-2 py-1.5 text-right text-[var(--text-secondary)]">{r.coverage_estimate ? `${r.coverage_estimate}%` : '-'}</td>
                <td className={`px-2 py-1.5 text-right font-medium ${r.avg_match_rate >= 0.8 ? 'text-green-400' : r.avg_match_rate >= 0.6 ? 'text-amber-400' : r.avg_match_rate ? 'text-red-400' : 'text-[var(--text-muted)]'}`}>
                  {r.avg_match_rate ? `${Math.round(r.avg_match_rate * 100)}%` : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── POC Detail View (full page) ─────────────────────────────────────────
const ANON_LABELS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')

function PocDetail({ pocId, onBack, anonymised, anonIndex }: { pocId: number; onBack: () => void; anonymised: boolean; anonIndex: number }) {
  const [, setSearchParams] = useSearchParams()
  const [poc, setPoc] = useState<PocRecord | null>(null)
  const [loading, setLoading] = useState(true)
  const [pocTab, setPocTab] = useState<'overview' | 'qualify' | 'vendors' | 'considerations' | 'checklist' | 'confirmation' | 'data' | 'results'>('overview')

  useEffect(() => {
    (async () => {
      try {
        const res = await pocApi.list() as any
        const items = res?.data?.data || res?.data || []
        const found = (items as PocRecord[]).find(p => p.id === pocId)
        if (found) setPoc(found)
      } catch (e) { console.error(e) }
      setLoading(false)
    })()
  }, [pocId])

  const save = (data: Partial<PocRecord>) => {
    if (!poc) return
    const updated = { ...poc, ...data } as PocRecord
    setPoc(updated)
    // Refresh URL slug if name changed
    if (data.company_name !== undefined || data.deal_name !== undefined) {
      const slug = (updated.company_name || updated.deal_name || '').split(/\s+/)[0].toLowerCase()
      setSearchParams(prev => { prev.set('poc', slug); return prev }, { replace: true })
    }
    pocApi.update(pocId, data as Record<string, unknown>).catch(console.error)
  }

  if (loading) return <div className="py-20 text-center"><Loader2 className="mx-auto animate-spin text-cyan-400" /></div>
  if (!poc) return <div className="py-20 text-center text-[var(--text-muted)]">POC not found</div>

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => { setSearchParams(prev => { prev.delete('poc'); return prev }, { replace: true }); onBack() }}
            className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <ChevronLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-2">
            <FlaskConical size={18} className="text-cyan-400" />
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">{anonymised ? `Company ${ANON_LABELS[anonIndex] || anonIndex + 1}` : (poc.company_name || poc.deal_name)}</h2>
            <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${STATUS_COLORS[poc.status] || ''}`}>{poc.status}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => exportPocPDF(poc)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)] border border-[var(--border-color)] hover:bg-[var(--bg-secondary)] transition-colors">
            <FileDown size={14} /> Export PDF
          </button>
          <button onClick={() => {
            const slug = (poc.company_name || poc.deal_name || '').split(/\s+/)[0].toLowerCase()
            const url = `${window.location.origin}${window.location.pathname}?poc=${slug}`
            navigator.clipboard.writeText(url).then(() => showToast('Link copied to clipboard', 'success'))
          }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)] border border-[var(--border-color)] hover:bg-[var(--bg-secondary)] transition-colors">
            <Link2 size={14} /> Copy Link
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[var(--border-color)]">
        {([
          { id: 'overview', label: 'Overview' },
          { id: 'qualify', label: 'Qualify' },
          { id: 'vendors', label: 'Vendor Coordination' },
          { id: 'considerations', label: 'Considerations' },
          { id: 'checklist', label: 'Checklist & Expectations' },
          { id: 'confirmation', label: 'Client Confirmation' },
          { id: 'data', label: 'Data Processing' },
          { id: 'results', label: 'Results' },
        ] as const).map(t => (
          <button key={t.id} onClick={() => setPocTab(t.id)}
            className={`px-3 py-1.5 text-xs font-medium transition-colors relative ${pocTab === t.id ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}>
            {t.label}
            {pocTab === t.id && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-500 rounded-full" />}
          </button>
        ))}
      </div>

      {pocTab === 'overview' && <PocOverview poc={poc} save={save} anonymised={anonymised} anonLabel={ANON_LABELS[anonIndex] || String(anonIndex + 1)} />}
      {pocTab === 'qualify' && <PocQualify poc={poc} save={save} />}
      {pocTab === 'vendors' && <VendorCoordination poc={poc} save={save} />}
      {pocTab === 'considerations' && <PocConsiderations poc={poc} save={save} />}
      {pocTab === 'checklist' && <PocChecklist poc={poc} save={save} />}
      {pocTab === 'confirmation' && <PocClientConfirmation poc={poc} save={save} />}
      {pocTab === 'data' && <DataProcessing poc={poc} save={save} />}
      {pocTab === 'results' && <PocResultsTab poc={poc} save={save} />}
    </div>
  )
}

export function PocTool() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [pocs, setPocs] = useState<PocRecord[]>([])
  const [selId, setSelId] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState<'poc' | 'coverage' | 'sources'>('poc')
  const [anonymised, setAnonymised] = useState(false)

  // Find a POC by slug (match company_name first word, then deal_name first word)
  const findBySlug = (list: PocRecord[], slug: string) => {
    const decoded = decodeURIComponent(slug).toLowerCase()
    return list.find(p => (p.company_name || '').split(/\s+/)[0].toLowerCase() === decoded)
      || list.find(p => (p.deal_name || '').split(/\s+/)[0].toLowerCase() === decoded)
  }

  const loadAll = async () => {
    setLoading(true)
    try {
      const res = await pocApi.list() as any
      const items = res?.data?.data || res?.data || []
      if (Array.isArray(items)) setPocs(items as PocRecord[])
    } catch (e) { console.error(e) }
    setLoading(false)
  }

  // Load on mount, restore selection from URL
  useEffect(() => {
    (async () => {
      await loadAll()
    })()
  }, [])

  // Check URL for deep link after pocs load
  useEffect(() => {
    if (pocs.length === 0) return
    const pocParam = searchParams.get('poc')
    if (pocParam && !selId) {
      const found = findBySlug(pocs, pocParam)
      if (found) setSelId(found.id)
    }
  }, [pocs, searchParams]) // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    const res = await pocApi.create({ deal_name: 'New POC' }) as any
    const created = res?.data?.data || res?.data
    if (created?.id) {
      const p = created as PocRecord
      setPocs(prev => [p, ...prev])
      setSelId(p.id)
      setSearchParams(prev => { prev.set('poc', 'new'); return prev }, { replace: true })
    }
  }

  const del = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation()
    await pocApi.remove(id).catch(() => {})
    setPocs(prev => prev.filter(p => p.id !== id))
    if (selId === id) setSelId(null)
  }

  if (loading) return <div className="py-20 text-center"><Loader2 className="mx-auto animate-spin text-cyan-400" /></div>

  // Detail view — full page
  if (selId) {
    const anonIdx = pocs.findIndex(p => p.id === selId)
    return <PocDetail pocId={selId} onBack={() => { setSelId(null); loadAll() }} anonymised={anonymised} anonIndex={anonIdx >= 0 ? anonIdx : 0} />
  }

  return (
    <div className="space-y-4" style={{ minHeight: 'calc(100vh - 200px)' }}>
      {/* Top tabs */}
      <div className="flex gap-1 border-b border-[var(--border-color)]">
        <button onClick={() => setView('poc')}
          className={`px-4 py-2 text-sm font-medium transition-colors relative ${view === 'poc' ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}>
          POC Requests
          {view === 'poc' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-500 rounded-full" />}
        </button>
        <button onClick={() => setView('coverage')}
          className={`px-4 py-2 text-sm font-medium transition-colors relative ${view === 'coverage' ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}>
          Country Coverage
          {view === 'coverage' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-500 rounded-full" />}
        </button>
        <button onClick={() => setView('sources')}
          className={`px-4 py-2 text-sm font-medium transition-colors relative ${view === 'sources' ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}>
          KYC Data Sources
          {view === 'sources' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-500 rounded-full" />}
        </button>
      </div>

      {view === 'coverage' && <CoverageView />}
      {view === 'sources' && <DataSourcesView />}

      {view === 'poc' && (
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-[var(--text-primary)]">POC Manager</h2>
              <p className="text-xs text-[var(--text-muted)] mt-0.5">Proof-of-concept tests — vendor selection, batch execution, and pass-rate cascade analysis</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setAnonymised(a => !a)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${anonymised ? 'bg-violet-500/15 text-violet-400 border-violet-500/30' : 'text-[var(--text-muted)] border-[var(--border-color)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]'}`}
                title={anonymised ? 'Show real names' : 'Hide client names'}>
                {anonymised ? <Eye size={14} /> : <EyeOff size={14} />}
                {anonymised ? 'Show names' : 'Anonymise'}
              </button>
              <button onClick={create} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/20 transition-colors">
                <Plus size={14} /> New POC
              </button>
            </div>
          </div>

          {/* POC list */}
          {pocs.length === 0 ? (
            <div className="py-16 text-center">
              <FlaskConical size={32} className="mx-auto mb-3 text-[var(--text-muted)] opacity-30" />
              <p className="text-sm text-[var(--text-muted)]">No POCs yet. Create one to get started.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {pocs.map((p, idx) => {
                const countries = (p.countries_in_scope || []).length
                const checks = (p.check_types || []).join(', ')
                const anonLabel = `Company ${ANON_LABELS[idx] || idx + 1}`
                return (
                  <div key={p.id} onClick={() => { setSelId(p.id); setSearchParams(prev => { prev.set('poc', (p.company_name || p.deal_name || '').split(/\s+/)[0].toLowerCase()); return prev }, { replace: true }) }}
                    className="group rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors cursor-pointer p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FlaskConical size={18} className="text-cyan-400 shrink-0" />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-[var(--text-primary)]">{anonymised ? anonLabel : p.deal_name}</span>
                            {!anonymised && p.company_name && <span className="text-xs text-[var(--text-muted)]">({p.company_name})</span>}
                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${STATUS_COLORS[p.status] || ''}`}>{p.status}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5 text-[10px] text-[var(--text-muted)]">
                            {checks && <span>{checks}</span>}
                            {countries > 0 && <span>{countries} {countries === 1 ? 'country' : 'countries'}</span>}
                            {p.record_count && <span>{p.record_count.toLocaleString()} records</span>}
                            {p.presales_owner && <span>{p.presales_owner}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={e => del(p.id, e)} className="opacity-0 group-hover:opacity-100 p-1.5 rounded text-[var(--text-muted)] hover:text-red-400 hover:bg-red-500/10 transition-all" title="Delete">
                          <Trash2 size={14} />
                        </button>
                        <ChevronRight size={16} className="text-[var(--text-muted)]" />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Intake Form (inline, no separate file) ─────────────────────────────
// ─── Data Processing Tab ─────────────────────────────────────────────────
const SOCURE_COLS = ['unique_id', 'firstname', 'surname', 'mobilenumber', 'email', 'physicaladdress', 'physicaladdress2', 'city', 'state', 'zip', 'country', 'ipaddress', 'submissiondate', 'dob', 'ssn', 'fraud_outcome']

const PII_TO_SOCURE: Record<string, string> = {
  'First Name': 'firstname', 'Last Name': 'surname', 'DOB': 'dob',
  'Address': 'physicaladdress', 'Street': 'physicaladdress', 'City': 'city',
  'State': 'state', 'Postal Code': 'zip', 'Country': 'country',
  'Phone': 'mobilenumber', 'Email': 'email', 'National ID': 'ssn',
  'Customer ID': 'unique_id',
}

// Direct column name → socure column mappings (case-insensitive)
const DIRECT_COL_TO_SOCURE: Record<string, string> = {
  'customer_id': 'unique_id', 'customeruserid': 'unique_id', 'id': 'unique_id', 'unique_id': 'unique_id', 'user_id': 'unique_id',
  'given_name': 'firstname', 'givenname': 'firstname', 'first_name': 'firstname', 'firstname': 'firstname', 'fname': 'firstname',
  'family_name': 'surname', 'familyname': 'surname', 'last_name': 'surname', 'lastname': 'surname', 'surname': 'surname', 'lname': 'surname',
  'mobile': 'mobilenumber', 'mobilenumber': 'mobilenumber', 'phone': 'mobilenumber', 'phone_number': 'mobilenumber', 'telephone': 'mobilenumber',
  'email': 'email', 'email_address': 'email', 'emailaddress': 'email',
  'street_address': 'physicaladdress', 'streetaddress': 'physicaladdress', 'address': 'physicaladdress', 'address_line_1': 'physicaladdress', 'address1': 'physicaladdress', 'physicaladdress': 'physicaladdress',
  'address_line_2': 'physicaladdress2', 'address2': 'physicaladdress2', 'physicaladdress2': 'physicaladdress2', 'apartment': 'physicaladdress2', 'unit': 'physicaladdress2',
  'city': 'city', 'town': 'city', 'locality': 'city', 'suburb': 'city',
  'state': 'state', 'province': 'state', 'region': 'state', 'subdivision': 'state',
  'zip': 'zip', 'zip_code': 'zip', 'zipcode': 'zip', 'postal_code': 'zip', 'postalcode': 'zip', 'postcode': 'zip',
  'country': 'country', 'country_code': 'country', 'countrycode': 'country', 'person_country': 'country',
  'ip': 'ipaddress', 'ip_address': 'ipaddress', 'ipaddress': 'ipaddress',
  'submission_date': 'submissiondate', 'submissiondate': 'submissiondate', 'order_date': 'submissiondate', 'created_at': 'submissiondate',
  'dob': 'dob', 'date_of_birth': 'dob', 'dateofbirth': 'dob', 'birth_date': 'dob', 'birthdate': 'dob',
  'ssn': 'ssn', 'national_id': 'ssn', 'nationalid': 'ssn', 'id_number': 'ssn', 'idnumber': 'ssn', 'social_security': 'ssn',
  'fraud_outcome': 'fraud_outcome', 'fraudoutcome': 'fraud_outcome', 'kyc_answer': 'fraud_outcome',
}

// GDC POC Template columns (from GDC POC Template IDV.xlsx)
const GDC_COLS = ['recordID', 'firstName', 'lastName', 'lastNameMaternal', 'dob', 'nationalID', 'nationalIDType', 'phoneNumber', 'email', 'houseNumber', 'houseNumberAddition', 'street', 'addressLine1', 'addressLine2', 'city', 'province', 'postalCode', 'countryCode']

const PII_TO_GDC: Record<string, string> = {
  'First Name': 'firstName', 'Last Name': 'lastName', 'DOB': 'dob',
  'Address': 'addressLine1', 'Street': 'street', 'City': 'city',
  'State': 'province', 'Postal Code': 'postalCode', 'Country': 'countryCode',
  'Phone': 'phoneNumber', 'Email': 'email', 'National ID': 'nationalID',
  'Customer ID': 'recordID',
}

const DIRECT_COL_TO_GDC: Record<string, string> = {
  'customer_id': 'recordID', 'customeruserid': 'recordID', 'id': 'recordID', 'unique_id': 'recordID', 'user_id': 'recordID', 'recordid': 'recordID',
  'given_name': 'firstName', 'givenname': 'firstName', 'first_name': 'firstName', 'firstname': 'firstName', 'fname': 'firstName',
  'family_name': 'lastName', 'familyname': 'lastName', 'last_name': 'lastName', 'lastname': 'lastName', 'surname': 'lastName', 'lname': 'lastName',
  'lastnamematernal': 'lastNameMaternal', 'maternal_name': 'lastNameMaternal',
  'dob': 'dob', 'date_of_birth': 'dob', 'dateofbirth': 'dob', 'birth_date': 'dob', 'birthdate': 'dob',
  'national_id': 'nationalID', 'nationalid': 'nationalID', 'ssn': 'nationalID', 'id_number': 'nationalID', 'idnumber': 'nationalID',
  'national_id_type': 'nationalIDType', 'nationalidtype': 'nationalIDType', 'id_type': 'nationalIDType',
  'phone': 'phoneNumber', 'phone_number': 'phoneNumber', 'mobile': 'phoneNumber', 'mobilenumber': 'phoneNumber', 'telephone': 'phoneNumber',
  'email': 'email', 'email_address': 'email', 'emailaddress': 'email',
  'house_number': 'houseNumber', 'housenumber': 'houseNumber', 'street_number': 'houseNumber', 'building_number': 'houseNumber',
  'house_number_addition': 'houseNumberAddition', 'housenumberaddition': 'houseNumberAddition', 'apartment': 'houseNumberAddition', 'unit': 'houseNumberAddition', 'suite': 'houseNumberAddition',
  'street': 'street', 'street_name': 'street', 'thoroughfare': 'street',
  'street_address': 'addressLine1', 'streetaddress': 'addressLine1', 'address': 'addressLine1', 'address_line_1': 'addressLine1', 'address1': 'addressLine1', 'addressline1': 'addressLine1',
  'address_line_2': 'addressLine2', 'address2': 'addressLine2', 'addressline2': 'addressLine2',
  'city': 'city', 'town': 'city', 'locality': 'city', 'suburb': 'city',
  'state': 'province', 'province': 'province', 'region': 'province',
  'zip': 'postalCode', 'zip_code': 'postalCode', 'zipcode': 'postalCode', 'postal_code': 'postalCode', 'postalcode': 'postalCode', 'postcode': 'postalCode',
  'country': 'countryCode', 'country_code': 'countryCode', 'countrycode': 'countryCode',
}

// Data Zoo POC Batch Template columns (from Data Zoo POC Batch Template.xlsx)
// addressElement1..5 are: street line 1, line 2/unit, city, state/province, postal code
const DATAZOO_COLS = ['DZ_ID', 'clientReference', 'firstName', 'middleName', 'lastName', 'addressElement1', 'addressElement2', 'addressElement3', 'addressElement4', 'addressElement5', 'countryCode', 'dateOfBirth', 'nationalIDNo', 'phoneNo', 'landlineNo', 'email', 'ip', 'consentObtained']

const PII_TO_DATAZOO: Record<string, string> = {
  'First Name': 'firstName', 'Middle Name': 'middleName', 'Last Name': 'lastName', 'DOB': 'dateOfBirth',
  'Address': 'addressElement1', 'Street': 'addressElement1', 'City': 'addressElement3',
  'State': 'addressElement4', 'Postal Code': 'addressElement5', 'Country': 'countryCode',
  'Phone': 'phoneNo', 'Email': 'email', 'National ID': 'nationalIDNo',
  'Customer ID': 'clientReference', 'IP': 'ip',
}

const DIRECT_COL_TO_DATAZOO: Record<string, string> = {
  'dz_id': 'DZ_ID', 'dzid': 'DZ_ID',
  'client_reference': 'clientReference', 'clientreference': 'clientReference', 'customer_id': 'clientReference', 'customeruserid': 'clientReference', 'id': 'clientReference', 'unique_id': 'clientReference', 'user_id': 'clientReference', 'recordid': 'clientReference', 'record_id': 'clientReference',
  'given_name': 'firstName', 'givenname': 'firstName', 'first_name': 'firstName', 'firstname': 'firstName', 'fname': 'firstName',
  'middle_name': 'middleName', 'middlename': 'middleName',
  'family_name': 'lastName', 'familyname': 'lastName', 'last_name': 'lastName', 'lastname': 'lastName', 'surname': 'lastName', 'lname': 'lastName',
  'street_address': 'addressElement1', 'streetaddress': 'addressElement1', 'address': 'addressElement1', 'address_line_1': 'addressElement1', 'address1': 'addressElement1', 'addressline1': 'addressElement1', 'street': 'addressElement1',
  'address_line_2': 'addressElement2', 'address2': 'addressElement2', 'addressline2': 'addressElement2', 'apartment': 'addressElement2', 'unit': 'addressElement2', 'suite': 'addressElement2',
  'city': 'addressElement3', 'town': 'addressElement3', 'locality': 'addressElement3', 'suburb': 'addressElement3',
  'state': 'addressElement4', 'province': 'addressElement4', 'region': 'addressElement4',
  'zip': 'addressElement5', 'zip_code': 'addressElement5', 'zipcode': 'addressElement5', 'postal_code': 'addressElement5', 'postalcode': 'addressElement5', 'postcode': 'addressElement5',
  'country': 'countryCode', 'country_code': 'countryCode', 'countrycode': 'countryCode',
  'dob': 'dateOfBirth', 'date_of_birth': 'dateOfBirth', 'dateofbirth': 'dateOfBirth', 'birth_date': 'dateOfBirth', 'birthdate': 'dateOfBirth',
  'national_id': 'nationalIDNo', 'nationalid': 'nationalIDNo', 'nationalidno': 'nationalIDNo', 'ssn': 'nationalIDNo', 'id_number': 'nationalIDNo', 'idnumber': 'nationalIDNo',
  'phone': 'phoneNo', 'phone_number': 'phoneNo', 'phoneno': 'phoneNo', 'mobile': 'phoneNo', 'mobilenumber': 'phoneNo', 'mobile_number': 'phoneNo',
  'landline': 'landlineNo', 'landlineno': 'landlineNo', 'landline_number': 'landlineNo', 'home_phone': 'landlineNo', 'telephone': 'landlineNo',
  'email': 'email', 'email_address': 'email', 'emailaddress': 'email',
}

// Prove POC Template columns (phone-centric identity verification, US & Brazil)
const PROVE_COLS = ['referenceId', 'firstName', 'lastName', 'phoneNumber', 'dob', 'address', 'city', 'state', 'postalCode', 'countryCode', 'email', 'nationalId']

const PII_TO_PROVE: Record<string, string> = {
  'First Name': 'firstName', 'Last Name': 'lastName', 'DOB': 'dob',
  'Address': 'address', 'City': 'city', 'State': 'state',
  'Postal Code': 'postalCode', 'Country': 'countryCode',
  'Phone': 'phoneNumber', 'Email': 'email', 'National ID': 'nationalId',
  'Customer ID': 'referenceId',
}

const DIRECT_COL_TO_PROVE: Record<string, string> = {
  'customer_id': 'referenceId', 'customeruserid': 'referenceId', 'id': 'referenceId', 'unique_id': 'referenceId', 'user_id': 'referenceId', 'recordid': 'referenceId', 'record_id': 'referenceId', 'reference_id': 'referenceId', 'referenceid': 'referenceId', 'client_reference': 'referenceId',
  'given_name': 'firstName', 'givenname': 'firstName', 'first_name': 'firstName', 'firstname': 'firstName', 'fname': 'firstName',
  'family_name': 'lastName', 'familyname': 'lastName', 'last_name': 'lastName', 'lastname': 'lastName', 'surname': 'lastName', 'lname': 'lastName',
  'phone': 'phoneNumber', 'phone_number': 'phoneNumber', 'phoneno': 'phoneNumber', 'mobile': 'phoneNumber', 'mobilenumber': 'phoneNumber', 'mobile_number': 'phoneNumber', 'telephone': 'phoneNumber',
  'dob': 'dob', 'date_of_birth': 'dob', 'dateofbirth': 'dob', 'birth_date': 'dob', 'birthdate': 'dob',
  'street_address': 'address', 'streetaddress': 'address', 'address': 'address', 'address_line_1': 'address', 'address1': 'address', 'addressline1': 'address',
  'city': 'city', 'town': 'city', 'locality': 'city', 'suburb': 'city',
  'state': 'state', 'province': 'state', 'region': 'state',
  'zip': 'postalCode', 'zip_code': 'postalCode', 'zipcode': 'postalCode', 'postal_code': 'postalCode', 'postalcode': 'postalCode', 'postcode': 'postalCode',
  'country': 'countryCode', 'country_code': 'countryCode', 'countrycode': 'countryCode',
  'email': 'email', 'email_address': 'email', 'emailaddress': 'email',
  'national_id': 'nationalId', 'nationalid': 'nationalId', 'ssn': 'nationalId', 'id_number': 'nationalId', 'idnumber': 'nationalId', 'social_security': 'nationalId',
  'ip': 'ip', 'ip_address': 'ip', 'ipaddress': 'ip',
  'consent': 'consentObtained', 'consent_obtained': 'consentObtained', 'consentobtained': 'consentObtained',
}

function transformDobDataZoo(val: string): string {
  if (!val) return ''
  // Data Zoo wants YYYY-MM-DD
  const m = val.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m) return val.slice(0, 10)
  const m2 = val.match(/^(\d{2})[\/\-](\d{2})[\/\-](\d{4})/)
  if (m2) return `${m2[3]}-${m2[2]}-${m2[1]}`
  const clean = val.replace(/[^0-9]/g, '')
  if (clean.length === 8) return `${clean.slice(0, 4)}-${clean.slice(4, 6)}-${clean.slice(6, 8)}`
  return val
}

function transformDobGDC(val: string): string {
  if (!val) return ''
  // GDC wants YYYY-MM-DD
  const m = val.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m) return val.slice(0, 10) // already correct
  const m2 = val.match(/^(\d{2})[\/\-](\d{2})[\/\-](\d{4})/)
  if (m2) return `${m2[3]}-${m2[2]}-${m2[1]}`
  const clean = val.replace(/[^0-9]/g, '')
  if (clean.length === 8) return `${clean.slice(0, 4)}-${clean.slice(4, 6)}-${clean.slice(6, 8)}`
  return val
}

function transformDob(val: string): string {
  if (!val) return ''
  const clean = val.replace(/[^0-9]/g, '')
  if (clean.length === 8) return clean
  const m = val.match(/(\d{4})-(\d{2})-(\d{2})/)
  if (m) return m[1] + m[2] + m[3]
  const m2 = val.match(/(\d{2})[\/\-](\d{2})[\/\-](\d{4})/)
  if (m2) return m2[3] + m2[2] + m2[1]
  return val
}

function DataProcessing({ poc, save }: { poc: PocRecord; save: (data: Partial<PocRecord>) => void }) {
  const disc = (poc.discovery_json as any) || {}
  const countries = poc.countries_in_scope || []
  const [dataProc, setDataProc] = useState<Record<string, any>>(() => (disc.data_processing || {}) as Record<string, any>)

  // Sync from parent when switching POCs
  useEffect(() => { setDataProc((poc.discovery_json as any)?.data_processing || {}) }, [poc.id])

  // Get selected vendors across all countries
  const allVendors = new Set<string>()
  for (const code of countries) {
    const cc = ((disc.country_config || {})[code]) || {}
    for (const v of (cc.vendors || []) as string[]) allVendors.add(v)
  }
  const hasSocure = [...allVendors].some(v => v.toLowerCase().includes('socure'))
  const hasGDC = [...allVendors].some(v => v.toLowerCase().includes('gdc'))
  const hasDataZoo = [...allVendors].some(v => {
    const s = v.toLowerCase()
    return s.includes('data zoo') || s.includes('datazoo')
  })
  const hasProve = [...allVendors].some(v => v.toLowerCase().includes('prove'))

  // Record count declared in DealHub for this country (Request Form → per-country
  // records), falling back to the POC-level count when only one country is in scope.
  const declaredFor = (code: string): number | null => {
    const cc = ((disc.country_config || {})[code]) || {}
    const perCountry = parseInt(cc.records)
    if (perCountry > 0) return perCountry
    if (countries.length === 1 && poc.record_count) return poc.record_count
    return null
  }

  const saveProc = (key: string, value: any) => {
    const updated = { ...dataProc, [key]: value }
    setDataProc(updated) // Immediate local update for preview
    save({ discovery_json: { ...disc, data_processing: updated } as any })
  }

  if (countries.length === 0) {
    return <div className="py-12 text-center text-[var(--text-muted)]"><p className="text-sm">Add countries on the Request Form tab first.</p></div>
  }

  // Helper: get shared raw data for a country (check shared key first, fall back to legacy socure key)
  const getRawData = (code: string): string => {
    return (dataProc[`country_${code}_raw`] || dataProc[`socure_${code}_raw`] || '') as string
  }

  const saveRawData = (code: string, value: string) => {
    // Save to shared key; also keep legacy socure key in sync for backward compat
    const updated = { ...dataProc, [`country_${code}_raw`]: value, [`socure_${code}_raw`]: value }
    // When clearing data, also remove column mappings for all vendors
    if (!value) {
      delete updated[`socure_${code}_mapping`]
      delete updated[`gdc_${code}_mapping`]
      delete updated[`datazoo_${code}_mapping`]
      delete updated[`prove_${code}_mapping`]
    }
    setDataProc(updated)
    save({ discovery_json: { ...disc, data_processing: updated } as any })
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">Data Processing</h2>
        <p className="text-xs text-[var(--text-muted)]">Upload customer data once per country, then map to each vendor's format</p>
      </div>

      {[...allVendors].length === 0 && (
        <Card className="p-8 text-center text-[var(--text-muted)]">
          <p className="text-sm">Select vendors on the Request Form tab first.</p>
        </Card>
      )}

      {/* Upload Data Per Country (shared across vendors) */}
      {[...allVendors].length > 0 && (
        <Card className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">Customer Data per Country</h3>
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/30">Upload once, map to all vendors</span>
          </div>
          {countries.map(code => {
            const countryName = COUNTRIES.find(c => c.code === code)?.name || code
            const rawData = getRawData(code)
            const hasData = rawData.trim().length > 0
            const rowCount = hasData ? parseDelimitedTable(rawData).rows.length : 0
            const declared = declaredFor(code)
            const countMismatch = hasData && declared != null && declared !== rowCount

            return (
              <div key={code} className="rounded border border-[var(--border-color)] overflow-hidden">
                <div className="px-3 py-2 bg-[var(--bg-tertiary)] flex items-center gap-2">
                  <span className="text-xs font-bold text-[var(--text-primary)]">{countryName}</span>
                  <span className="text-[10px] text-[var(--text-muted)]">({code})</span>
                  {hasData && (countMismatch
                    ? <span className="text-[10px] text-red-400 font-semibold" title={`DealHub declares ${declared} records for this country`}>
                        ⚠ {rowCount.toLocaleString()} rows — {declared?.toLocaleString()} declared
                      </span>
                    : <span className="text-[10px] text-green-400 font-semibold">✓ {rowCount.toLocaleString()} rows</span>
                  )}
                </div>
                <div className="p-3 space-y-2">
                  <div className="flex items-center gap-2 mb-1">
                    <label className={lc}>Customer data (CSV/TSV/Excel)</label>
                    <label className="text-[9px] px-1.5 py-0.5 rounded bg-[var(--bg-tertiary)] text-[var(--text-muted)] border border-[var(--border-color)] cursor-pointer hover:text-[var(--text-primary)]">
                      Upload File
                      <input type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" className="hidden" onChange={e => {
                        const file = e.target.files?.[0]; if (!file) return
                        if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
                          const reader = new FileReader()
                          reader.onload = (evt) => {
                            const data = new Uint8Array(evt.target?.result as ArrayBuffer)
                            const wb = XLSX.read(data, { type: 'array' })
                            if (wb.SheetNames.length > 1) {
                              showToast(
                                `"${file.name}" has ${wb.SheetNames.length} sheets. Only the first sheet ("${wb.SheetNames[0]}") will be used — other sheets are ignored.`,
                                'warning',
                                8000
                              )
                            }
                            const ws = wb.Sheets[wb.SheetNames[0]]
                            const csv = XLSX.utils.sheet_to_csv(ws)
                            saveRawData(code, csv)
                          }
                          reader.readAsArrayBuffer(file)
                        } else {
                          const reader = new FileReader()
                          reader.onload = () => saveRawData(code, reader.result as string)
                          reader.readAsText(file)
                        }
                        e.target.value = ''
                      }} />
                    </label>
                    {hasData && (() => {
                      const uidHeaders = parseDelimitedTable(rawData).headers
                      const hasUniqueId = uidHeaders.some(h => h.toLowerCase() === 'unique_id')
                      return (
                        <>
                          {!hasUniqueId && (
                            <button type="button" onClick={() => {
                              // Re-serialise as RFC 4180 CSV so quoted fields survive the rewrite
                              const { headers, rows } = parseDelimitedTable(rawData)
                              saveRawData(code, toCsv(
                                ['unique_id', ...headers],
                                rows.map((row, i) => [`${code}-${String(i + 1).padStart(4, '0')}`, ...row]),
                              ))
                            }}
                              className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/30 hover:bg-blue-500/20 transition-colors">
                              + Add Unique IDs
                            </button>
                          )}
                          {hasUniqueId && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-500/10 text-green-400 border border-green-500/30">
                              unique_id ✓
                            </span>
                          )}
                          <button type="button" onClick={() => saveRawData(code, '')}
                            className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 transition-colors">
                            Clear Data
                          </button>
                        </>
                      )
                    })()}
                  </div>
                  <textarea value={rawData} onChange={e => saveRawData(code, e.target.value)}
                    rows={hasData ? 2 : 3} className={`${ic} !text-[9px] font-mono`}
                    placeholder={`Paste CSV/TSV data with headers for ${countryName}, or upload a file...`} />
                </div>
              </div>
            )
          })}
        </Card>
      )}

      {/* Socure Data Formatter */}
      {hasSocure && (
        <Card className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-purple-400">Socure Data Formatter</h3>
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400 border border-purple-500/30">Pipe-delimited export</span>
          </div>
          <a href="http://riskos.socure.com/validate/login?token=3ec204b04a31111706a2448a465570152223d8a464e67d84c88dbb483687f3be" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[10px] text-purple-400 hover:text-purple-300 underline underline-offset-2">
            Upload to Socure RiskOS Portal ↗
          </a>
          <div className="flex items-start gap-2 p-2.5 rounded-md bg-amber-500/10 border border-amber-500/30">
            <Info size={14} className="text-amber-400 mt-0.5 shrink-0" />
            <p className="text-[10px] text-amber-300/90 leading-relaxed">
              Remember to also register this deal on the{' '}
              <a href="https://partners.socure.com/English/" target="_blank" rel="noopener noreferrer"
                className="text-amber-400 hover:text-amber-300 underline underline-offset-2 font-medium">
                Socure Partner Portal ↗
              </a>{' '}
              before proceeding with the POC.
            </p>
          </div>

          {countries.map(code => {
            const countryName = COUNTRIES.find(c => c.code === code)?.name || code
            const fmtKey = `socure_${code}`
            const rawData = getRawData(code)
            const colMapping = (dataProc[fmtKey + '_mapping'] || {}) as Record<string, string>

            const { headers: customerHeaders, rows: dataRows } = parseDelimitedTable(rawData)

            // Auto-suggest from direct name matching + AI field mappings
            const fm = ((disc.field_mappings || {})[code] || {}) as Record<string, string>
            const suggest = (sc: string): string => {
              if (colMapping[sc]) return colMapping[sc]
              // 1. Direct column name matching (covers STREET_ADDRESS → physicaladdress, TOWN → city, etc.)
              for (const h of customerHeaders) {
                const mapped = DIRECT_COL_TO_SOCURE[h.toLowerCase().replace(/\s+/g, '_')]
                if (mapped === sc) return h
              }
              // 2. AI field mapping results
              for (const [origCol, piiField] of Object.entries(fm)) {
                if (PII_TO_SOCURE[piiField] === sc) return origCol
              }
              // 3. Fuzzy name match
              return customerHeaders.find(h => h.toLowerCase().replace(/_/g, '') === sc.toLowerCase().replace(/_/g, '')) || ''
            }

            const buildRow = (row: string[]) => SOCURE_COLS.map(sc => {
              const m = colMapping[sc] || suggest(sc); if (!m) return ''
              const idx = customerHeaders.indexOf(m); if (idx < 0) return ''
              let v = row[idx] || ''; if (sc === 'dob') v = transformDob(v); return v
            })

            const outRows = dataRows.map(row => buildRow(row))
            const issues = checkExport({ sourceHeaders: customerHeaders, sourceRows: dataRows, outHeader: SOCURE_COLS, outRows, declared: declaredFor(code) })
            const blocking = issues.filter(i => i.level === 'error')

            const doDownload = () => {
              if (blocking.length > 0 && !window.confirm(
                `This export failed ${blocking.length} integrity check${blocking.length === 1 ? '' : 's'}:\n\n${blocking.map(i => `• ${i.message}`).join('\n')}\n\nDownload anyway?`
              )) return
              const content = toCsv(SOCURE_COLS, outRows)
              const blob = new Blob(['\ufeff' + content], { type: 'text/csv;charset=utf-8' })
              const a = document.createElement('a'); a.href = URL.createObjectURL(blob)
              a.download = `socure_${code.toLowerCase()}_poc.csv`
              a.click(); URL.revokeObjectURL(a.href)
            }

            const previewRows = outRows.slice(0, 5)

            if (!rawData.trim()) return null // Skip countries with no uploaded data

            return (
              <div key={code} className="rounded border border-purple-500/20 overflow-hidden">
                <div className="px-3 py-2 bg-purple-500/5 flex items-center gap-2">
                  <span className="text-xs font-bold text-[var(--text-primary)]">{countryName}</span>
                  <span className="text-[10px] text-[var(--text-muted)]">({code})</span>
                  {dataRows.length > 0 && <span className="text-[10px] text-purple-400 font-semibold">{dataRows.length} rows</span>}
                </div>
                <div className="p-3 space-y-2">

                  {customerHeaders.length > 0 && (
                    <div>
                      <label className={lc}>Column Mapping — Customer → Socure</label>
                      <div className="grid grid-cols-2 gap-1 mt-1">
                        {SOCURE_COLS.map(sc => (
                          <div key={sc} className="flex items-center gap-1">
                            <span className="text-[9px] font-mono text-purple-400 w-28 shrink-0 truncate" title={sc}>{sc}</span>
                            <span className="text-[9px] text-[var(--text-muted)]">←</span>
                            <select value={colMapping[sc] || suggest(sc)} onChange={e => saveProc(fmtKey + '_mapping', { ...colMapping, [sc]: e.target.value })}
                              className="flex-1 px-1 py-0.5 rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-[9px] text-[var(--text-primary)]">
                              <option value="">— unmapped —</option>
                              {customerHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                            </select>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <CsvIntegrityWarning issues={issues} />

                  {previewRows.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between">
                        <label className={lc}>Preview</label>
                        <button type="button" onClick={doDownload}
                          className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium border cursor-pointer ${blocking.length > 0
                            ? 'bg-red-500/10 text-red-400 border-red-500/30 hover:bg-red-500/20'
                            : 'bg-purple-500/10 text-purple-400 border-purple-500/30 hover:bg-purple-500/20'}`}>
                          ⬇ Download CSV ({outRows.length} rows)
                        </button>
                      </div>
                      <div className="mt-1 overflow-x-auto rounded border border-[var(--border-color)]">
                        <table className="text-[8px] font-mono">
                          <thead><tr className="bg-purple-500/5">
                            {SOCURE_COLS.map(c => <th key={c} className="px-1.5 py-1 text-left text-purple-400 font-semibold whitespace-nowrap">{c}</th>)}
                          </tr></thead>
                          <tbody>
                            {previewRows.map((row, i) => (
                              <tr key={i} className="border-t border-[var(--border-color)]">
                                {row.map((cell, j) => <td key={j} className="px-1.5 py-0.5 text-[var(--text-secondary)] whitespace-nowrap max-w-[120px] truncate" title={cell}>{cell || <span className="text-[var(--text-muted)] opacity-30">—</span>}</td>)}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </Card>
      )}

      {/* GDC Data Formatter */}
      {hasGDC && (
        <Card className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-cyan-400">GDC Data Formatter</h3>
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/30">CSV export</span>
          </div>
          <p className="text-[10px] text-[var(--text-muted)]">Maps uploaded customer data to GDC's POC template. DOB auto-converts to YYYY-MM-DD.</p>
          <a href="https://eshare.lseg.com/collaborations/QqXBbmfjqrgR/share_access/75KNwKQbgbNx/" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[10px] text-cyan-400 hover:text-cyan-300 underline underline-offset-2">
            Upload to GDC Portal ↗
          </a>

          {countries.map(code => {
            const countryName = COUNTRIES.find(c => c.code === code)?.name || code
            const fmtKey = `gdc_${code}`
            const rawData = getRawData(code)
            const colMapping = (dataProc[fmtKey + '_mapping'] || {}) as Record<string, string>

            const { headers: customerHeaders, rows: dataRows } = parseDelimitedTable(rawData)

            const fm = ((disc.field_mappings || {})[code] || {}) as Record<string, string>
            const suggestGDC = (gc: string): string => {
              if (colMapping[gc]) return colMapping[gc]
              for (const h of customerHeaders) {
                const mapped = DIRECT_COL_TO_GDC[h.toLowerCase().replace(/\s+/g, '_')]
                if (mapped === gc) return h
              }
              for (const [origCol, piiField] of Object.entries(fm)) {
                if (PII_TO_GDC[piiField] === gc) return origCol
              }
              return customerHeaders.find(h => h.toLowerCase().replace(/_/g, '') === gc.toLowerCase().replace(/_/g, '')) || ''
            }

            const buildGDCRow = (row: string[]) => GDC_COLS.map(gc => {
              const m = colMapping[gc] || suggestGDC(gc); if (!m) return ''
              const idx = customerHeaders.indexOf(m); if (idx < 0) return ''
              let v = row[idx] || ''
              if (gc === 'dob') v = transformDobGDC(v)
              if (gc === 'countryCode' && !v) v = code // default to POC country
              return v
            })

            const outRows = dataRows.map(row => buildGDCRow(row))
            const issues = checkExport({ sourceHeaders: customerHeaders, sourceRows: dataRows, outHeader: GDC_COLS, outRows, declared: declaredFor(code) })
            const blocking = issues.filter(i => i.level === 'error')

            const doDownloadGDC = () => {
              if (blocking.length > 0 && !window.confirm(
                `This export failed ${blocking.length} integrity check${blocking.length === 1 ? '' : 's'}:\n\n${blocking.map(i => `• ${i.message}`).join('\n')}\n\nDownload anyway?`
              )) return
              const content = toCsv(GDC_COLS, outRows)
              const blob = new Blob(['\ufeff' + content], { type: 'text/csv;charset=utf-8' })
              const a = document.createElement('a'); a.href = URL.createObjectURL(blob)
              a.download = `gdc_${code.toLowerCase()}_poc.csv`
              a.click(); URL.revokeObjectURL(a.href)
            }

            const previewRowsGDC = outRows.slice(0, 5)

            if (!rawData.trim()) return null // Skip countries with no uploaded data

            return (
              <div key={code} className="rounded border border-cyan-500/20 overflow-hidden">
                <div className="px-3 py-2 bg-cyan-500/5 flex items-center gap-2">
                  <span className="text-xs font-bold text-[var(--text-primary)]">{countryName}</span>
                  <span className="text-[10px] text-[var(--text-muted)]">({code})</span>
                  {dataRows.length > 0 && <span className="text-[10px] text-cyan-400 font-semibold">{dataRows.length} rows</span>}
                </div>
                <div className="p-3 space-y-2">
                  {customerHeaders.length > 0 && (
                    <div>
                      <label className={lc}>Column Mapping — Customer → GDC</label>
                      <div className="grid grid-cols-2 gap-1 mt-1">
                        {GDC_COLS.map(gc => (
                          <div key={gc} className="flex items-center gap-1">
                            <span className="text-[9px] font-mono text-cyan-400 w-32 shrink-0 truncate" title={gc}>{gc}</span>
                            <span className="text-[9px] text-[var(--text-muted)]">←</span>
                            <select value={colMapping[gc] || suggestGDC(gc)} onChange={e => saveProc(fmtKey + '_mapping', { ...colMapping, [gc]: e.target.value })}
                              className="flex-1 px-1 py-0.5 rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-[9px] text-[var(--text-primary)]">
                              <option value="">— unmapped —</option>
                              {customerHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                            </select>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <CsvIntegrityWarning issues={issues} />

                  {previewRowsGDC.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between">
                        <label className={lc}>Preview</label>
                        <button type="button" onClick={doDownloadGDC}
                          className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium border cursor-pointer ${blocking.length > 0
                            ? 'bg-red-500/10 text-red-400 border-red-500/30 hover:bg-red-500/20'
                            : 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30 hover:bg-cyan-500/20'}`}>
                          ⬇ Download CSV ({outRows.length} rows)
                        </button>
                      </div>
                      <div className="mt-1 overflow-x-auto rounded border border-[var(--border-color)]">
                        <table className="text-[8px] font-mono">
                          <thead><tr className="bg-cyan-500/5">
                            {GDC_COLS.map(c => <th key={c} className="px-1.5 py-1 text-left text-cyan-400 font-semibold whitespace-nowrap">{c}</th>)}
                          </tr></thead>
                          <tbody>
                            {previewRowsGDC.map((row, i) => (
                              <tr key={i} className="border-t border-[var(--border-color)]">
                                {row.map((cell, j) => <td key={j} className="px-1.5 py-0.5 text-[var(--text-secondary)] whitespace-nowrap max-w-[120px] truncate" title={cell}>{cell || <span className="text-[var(--text-muted)] opacity-30">—</span>}</td>)}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </Card>
      )}

      {/* Data Zoo Data Formatter */}
      {hasDataZoo && (
        <Card className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-emerald-400">Data Zoo Data Formatter</h3>
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">CSV export</span>
          </div>
          <p className="text-[10px] text-[var(--text-muted)]">Maps uploaded customer data to Data Zoo's POC Batch Template. DOB auto-converts to YYYY-MM-DD. Address splits across addressElement1–5 (line1, line2, city, state, postal).</p>
          <a href="https://securedata.datazoo.com/#/" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[10px] text-emerald-400 hover:text-emerald-300 underline underline-offset-2">
            Upload to DataZoo Secure Portal ↗
          </a>

          {countries.map(code => {
            const countryName = COUNTRIES.find(c => c.code === code)?.name || code
            const fmtKey = `datazoo_${code}`
            const rawData = getRawData(code)
            const colMapping = (dataProc[fmtKey + '_mapping'] || {}) as Record<string, string>

            const { headers: customerHeaders, rows: dataRows } = parseDelimitedTable(rawData)

            const fm = ((disc.field_mappings || {})[code] || {}) as Record<string, string>
            const suggestDataZoo = (dzc: string): string => {
              if (colMapping[dzc]) return colMapping[dzc]
              for (const h of customerHeaders) {
                const mapped = DIRECT_COL_TO_DATAZOO[h.toLowerCase().replace(/\s+/g, '_')]
                if (mapped === dzc) return h
              }
              for (const [origCol, piiField] of Object.entries(fm)) {
                if (PII_TO_DATAZOO[piiField] === dzc) return origCol
              }
              return customerHeaders.find(h => h.toLowerCase().replace(/_/g, '') === dzc.toLowerCase().replace(/_/g, '')) || ''
            }

            const buildDataZooRow = (row: string[]) => DATAZOO_COLS.map(dzc => {
              const m = colMapping[dzc] || suggestDataZoo(dzc); if (!m) return ''
              const idx = customerHeaders.indexOf(m); if (idx < 0) return ''
              let v = row[idx] || ''
              if (dzc === 'dateOfBirth') v = transformDobDataZoo(v)
              if (dzc === 'countryCode' && !v) v = code // default to POC country
              return v
            })

            const outRows = dataRows.map(row => buildDataZooRow(row))
            const issues = checkExport({ sourceHeaders: customerHeaders, sourceRows: dataRows, outHeader: DATAZOO_COLS, outRows, declared: declaredFor(code) })
            const blocking = issues.filter(i => i.level === 'error')

            const doDownloadDataZoo = () => {
              if (blocking.length > 0 && !window.confirm(
                `This export failed ${blocking.length} integrity check${blocking.length === 1 ? '' : 's'}:\n\n${blocking.map(i => `• ${i.message}`).join('\n')}\n\nDownload anyway?`
              )) return
              const content = toCsv(DATAZOO_COLS, outRows)
              const blob = new Blob(['\ufeff' + content], { type: 'text/csv;charset=utf-8' })
              const a = document.createElement('a'); a.href = URL.createObjectURL(blob)
              a.download = `datazoo_${code.toLowerCase()}_poc.csv`
              a.click(); URL.revokeObjectURL(a.href)
            }

            const previewRowsDataZoo = outRows.slice(0, 5)

            if (!rawData.trim()) return null

            return (
              <div key={code} className="rounded border border-emerald-500/20 overflow-hidden">
                <div className="px-3 py-2 bg-emerald-500/5 flex items-center gap-2">
                  <span className="text-xs font-bold text-[var(--text-primary)]">{countryName}</span>
                  <span className="text-[10px] text-[var(--text-muted)]">({code})</span>
                  {dataRows.length > 0 && <span className="text-[10px] text-emerald-400 font-semibold">{dataRows.length} rows</span>}
                </div>
                <div className="p-3 space-y-2">
                  {customerHeaders.length > 0 && (
                    <div>
                      <label className={lc}>Column Mapping — Customer → Data Zoo</label>
                      <div className="grid grid-cols-2 gap-1 mt-1">
                        {DATAZOO_COLS.map(dzc => (
                          <div key={dzc} className="flex items-center gap-1">
                            <span className="text-[9px] font-mono text-emerald-400 w-32 shrink-0 truncate" title={dzc}>{dzc}</span>
                            <span className="text-[9px] text-[var(--text-muted)]">←</span>
                            <select value={colMapping[dzc] || suggestDataZoo(dzc)} onChange={e => saveProc(fmtKey + '_mapping', { ...colMapping, [dzc]: e.target.value })}
                              className="flex-1 px-1 py-0.5 rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-[9px] text-[var(--text-primary)]">
                              <option value="">— unmapped —</option>
                              {customerHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                            </select>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <CsvIntegrityWarning issues={issues} />

                  {previewRowsDataZoo.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between">
                        <label className={lc}>Preview</label>
                        <button type="button" onClick={doDownloadDataZoo}
                          className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium border cursor-pointer ${blocking.length > 0
                            ? 'bg-red-500/10 text-red-400 border-red-500/30 hover:bg-red-500/20'
                            : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20'}`}>
                          ⬇ Download CSV ({outRows.length} rows)
                        </button>
                      </div>
                      <div className="mt-1 overflow-x-auto rounded border border-[var(--border-color)]">
                        <table className="text-[8px] font-mono">
                          <thead><tr className="bg-emerald-500/5">
                            {DATAZOO_COLS.map(c => <th key={c} className="px-1.5 py-1 text-left text-emerald-400 font-semibold whitespace-nowrap">{c}</th>)}
                          </tr></thead>
                          <tbody>
                            {previewRowsDataZoo.map((row, i) => (
                              <tr key={i} className="border-t border-[var(--border-color)]">
                                {row.map((cell, j) => <td key={j} className="px-1.5 py-0.5 text-[var(--text-secondary)] whitespace-nowrap max-w-[120px] truncate" title={cell}>{cell || <span className="text-[var(--text-muted)] opacity-30">—</span>}</td>)}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </Card>
      )}

      {/* Prove Data Formatter */}
      {hasProve && (
        <Card className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-orange-400">Prove Data Formatter</h3>
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-400 border border-orange-500/30">CSV export</span>
          </div>
          <p className="text-[10px] text-[var(--text-muted)]">Maps uploaded customer data to Prove's POC format. Phone-centric verification — phone number is the most critical field. DOB auto-converts to YYYY-MM-DD.</p>
          <a href="https://sftp-eu.proveapis.com/WebInterface/login.html" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[10px] text-orange-400 hover:text-orange-300 underline underline-offset-2">
            Upload to Prove SFTP Portal ↗
          </a>

          {countries.map(code => {
            const countryName = COUNTRIES.find(c => c.code === code)?.name || code
            const fmtKey = `prove_${code}`
            const rawData = getRawData(code)
            const colMapping = (dataProc[fmtKey + '_mapping'] || {}) as Record<string, string>

            const { headers: customerHeaders, rows: dataRows } = parseDelimitedTable(rawData)

            const fm = ((disc.field_mappings || {})[code] || {}) as Record<string, string>
            const suggestProve = (pc: string): string => {
              if (colMapping[pc]) return colMapping[pc]
              for (const h of customerHeaders) {
                const mapped = DIRECT_COL_TO_PROVE[h.toLowerCase().replace(/\s+/g, '_')]
                if (mapped === pc) return h
              }
              for (const [origCol, piiField] of Object.entries(fm)) {
                if (PII_TO_PROVE[piiField] === pc) return origCol
              }
              return customerHeaders.find(h => h.toLowerCase().replace(/_/g, '') === pc.toLowerCase().replace(/_/g, '')) || ''
            }

            const buildProveRow = (row: string[]) => PROVE_COLS.map(pc => {
              const m = colMapping[pc] || suggestProve(pc); if (!m) return ''
              const idx = customerHeaders.indexOf(m); if (idx < 0) return ''
              let v = row[idx] || ''
              if (pc === 'dob') v = transformDobGDC(v) // YYYY-MM-DD format
              if (pc === 'countryCode' && !v) v = code
              return v
            })

            const outRows = dataRows.map(row => buildProveRow(row))
            const issues = checkExport({ sourceHeaders: customerHeaders, sourceRows: dataRows, outHeader: PROVE_COLS, outRows, declared: declaredFor(code) })
            const blocking = issues.filter(i => i.level === 'error')

            const doDownloadProve = () => {
              if (blocking.length > 0 && !window.confirm(
                `This export failed ${blocking.length} integrity check${blocking.length === 1 ? '' : 's'}:\n\n${blocking.map(i => `• ${i.message}`).join('\n')}\n\nDownload anyway?`
              )) return
              const content = toCsv(PROVE_COLS, outRows)
              const blob = new Blob(['\ufeff' + content], { type: 'text/csv;charset=utf-8' })
              const a = document.createElement('a'); a.href = URL.createObjectURL(blob)
              a.download = `prove_${code.toLowerCase()}_poc.csv`
              a.click(); URL.revokeObjectURL(a.href)
            }

            const previewRowsProve = outRows.slice(0, 5)

            if (!rawData.trim()) return null

            return (
              <div key={code} className="rounded border border-orange-500/20 overflow-hidden">
                <div className="px-3 py-2 bg-orange-500/5 flex items-center gap-2">
                  <span className="text-xs font-bold text-[var(--text-primary)]">{countryName}</span>
                  <span className="text-[10px] text-[var(--text-muted)]">({code})</span>
                  {dataRows.length > 0 && <span className="text-[10px] text-orange-400 font-semibold">{dataRows.length} rows</span>}
                </div>
                <div className="p-3 space-y-2">
                  {customerHeaders.length > 0 && (
                    <div>
                      <label className={lc}>Column Mapping — Customer → Prove</label>
                      <div className="grid grid-cols-2 gap-1 mt-1">
                        {PROVE_COLS.map(pc => (
                          <div key={pc} className="flex items-center gap-1">
                            <span className="text-[9px] font-mono text-orange-400 w-32 shrink-0 truncate" title={pc}>{pc}</span>
                            <span className="text-[9px] text-[var(--text-muted)]">←</span>
                            <select value={colMapping[pc] || suggestProve(pc)} onChange={e => saveProc(fmtKey + '_mapping', { ...colMapping, [pc]: e.target.value })}
                              className="flex-1 px-1 py-0.5 rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-[9px] text-[var(--text-primary)]">
                              <option value="">— unmapped —</option>
                              {customerHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                            </select>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <CsvIntegrityWarning issues={issues} />

                  {previewRowsProve.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between">
                        <label className={lc}>Preview</label>
                        <button type="button" onClick={doDownloadProve}
                          className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium border cursor-pointer ${blocking.length > 0
                            ? 'bg-red-500/10 text-red-400 border-red-500/30 hover:bg-red-500/20'
                            : 'bg-orange-500/10 text-orange-400 border-orange-500/30 hover:bg-orange-500/20'}`}>
                          ⬇ Download CSV ({outRows.length} rows)
                        </button>
                      </div>
                      <div className="mt-1 overflow-x-auto rounded border border-[var(--border-color)]">
                        <table className="text-[8px] font-mono">
                          <thead><tr className="bg-orange-500/5">
                            {PROVE_COLS.map(c => <th key={c} className="px-1.5 py-1 text-left text-orange-400 font-semibold whitespace-nowrap">{c}</th>)}
                          </tr></thead>
                          <tbody>
                            {previewRowsProve.map((row, i) => (
                              <tr key={i} className="border-t border-[var(--border-color)]">
                                {row.map((cell, j) => <td key={j} className="px-1.5 py-0.5 text-[var(--text-secondary)] whitespace-nowrap max-w-[120px] truncate" title={cell}>{cell || <span className="text-[var(--text-muted)] opacity-30">—</span>}</td>)}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </Card>
      )}

      {/* No vendor formatters needed */}
      {!hasSocure && !hasGDC && !hasDataZoo && !hasProve && (
        <Card className="p-8 text-center text-[var(--text-muted)]">
          <p className="text-sm">No vendor-specific data formatting required for the selected vendors.</p>
          <p className="text-xs mt-1">Vendor-specific formatters appear here when Socure, GDC, Data Zoo, or Prove are added as vendors.</p>
        </Card>
      )}
    </div>
  )
}

// ─── POC Results Tab ─────────────────────────────────────────────────────
function PocResultsTab({ poc, save }: { poc: PocRecord; save: (data: Partial<PocRecord>) => void }) {
  const disc = (poc.discovery_json as any) || {}
  const [uploading, setUploading] = useState(false)
  const [calculating, setCalculating] = useState(false)

  // Results are stored per-vendor as uploaded CSV data
  const vendorResults = (disc.vendor_results || {}) as Record<string, { country: string; customer_id: string; matched: boolean }[]>
  const countries = poc.countries_in_scope || []

  // Get all vendors that have results
  const resultVendors = Object.keys(vendorResults).filter(v => vendorResults[v]?.length > 0)

  // Get selected vendors from country config
  const allSelectedVendors = new Set<string>()
  for (const code of countries) {
    const cc = ((disc.country_config || {})[code]) || {}
    for (const v of (cc.vendors || []) as string[]) allSelectedVendors.add(v)
  }

  const handleUploadVendorResults = (vendorId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    const reader = new FileReader()
    reader.onload = () => {
      const text = reader.result as string
      const table = parseDelimitedTable(text)
      if (table.rows.length < 1) { setUploading(false); return }
      const headers = table.headers.map(h => h.toLowerCase())

      const idCol = headers.findIndex(h => h.includes('customer_id') || h.includes('customeruserid') || h === 'id')
      const countryCol = headers.findIndex(h => h.includes('country') || h.includes('kyc_country'))
      const matchCol = headers.findIndex(h => h.includes('verified') || h.includes('match') || h.includes('status') || h.includes('result'))

      if (idCol < 0) { setUploading(false); return }

      const rows: { customer_id: string; country: string; matched: boolean }[] = []
      for (const cols of table.rows) {
        if (!cols[idCol]) continue
        const matchVal = matchCol >= 0 ? cols[matchCol].toLowerCase() : ''
        rows.push({
          customer_id: cols[idCol],
          country: countryCol >= 0 ? cols[countryCol] : '',
          matched: matchVal === 'true' || matchVal === 'pass' || matchVal === '1' || matchVal === 'yes',
        })
      }

      const updated = { ...vendorResults, [vendorId]: rows }
      save({ discovery_json: { ...disc, vendor_results: updated } as any })
      setUploading(false)
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  // Calculate results
  const calculate = () => {
    setCalculating(true)

    // Build per-customer-id combined view
    const allCustomerIds = new Set<string>()
    for (const rows of Object.values(vendorResults)) {
      for (const r of rows) allCustomerIds.add(r.customer_id)
    }

    // Map customer_id -> country (from any vendor result that has it)
    const idToCountry: Record<string, string> = {}
    for (const rows of Object.values(vendorResults)) {
      for (const r of rows) { if (r.country && r.customer_id) idToCountry[r.customer_id] = r.country }
    }

    // Build combined data: per customer_id, per vendor -> matched
    const combined: Record<string, Record<string, boolean>> = {}
    for (const cid of allCustomerIds) {
      combined[cid] = {}
      for (const [vendor, rows] of Object.entries(vendorResults)) {
        const row = rows.find(r => r.customer_id === cid)
        if (row) combined[cid][vendor] = row.matched
      }
    }

    // Per-country per-vendor stats
    const countryStats: Record<string, { total: number; vendors: Record<string, { pass: number; fail: number; rate: number }>; combined: { pass: number; rate: number }; bestVendor: string; bestRate: number; cascades: Record<string, { pass: number; rate: number; gain: number }> }> = {}

    const byCountry: Record<string, string[]> = {}
    for (const [cid, country] of Object.entries(idToCountry)) {
      if (!byCountry[country]) byCountry[country] = []
      byCountry[country].push(cid)
    }

    for (const [country, cids] of Object.entries(byCountry)) {
      const total = cids.length
      const vendors: Record<string, { pass: number; fail: number; rate: number }> = {}
      let bestVendor = ''; let bestRate = 0

      for (const vendor of resultVendors) {
        let pass = 0
        for (const cid of cids) { if (combined[cid]?.[vendor]) pass++ }
        const rate = total > 0 ? Math.round((pass / total) * 1000) / 10 : 0
        vendors[vendor] = { pass, fail: total - pass, rate }
        if (rate > bestRate) { bestRate = rate; bestVendor = vendor }
      }

      // Combined (any vendor passes)
      let combinedPass = 0
      for (const cid of cids) {
        if (resultVendors.some(v => combined[cid]?.[v])) combinedPass++
      }
      const combinedRate = total > 0 ? Math.round((combinedPass / total) * 1000) / 10 : 0

      // Cascades (2-vendor combos)
      const cascades: Record<string, { pass: number; rate: number; gain: number }> = {}
      for (let i = 0; i < resultVendors.length; i++) {
        for (let j = i + 1; j < resultVendors.length; j++) {
          const v1 = resultVendors[i]; const v2 = resultVendors[j]
          let pass = 0
          for (const cid of cids) { if (combined[cid]?.[v1] || combined[cid]?.[v2]) pass++ }
          const rate = total > 0 ? Math.round((pass / total) * 1000) / 10 : 0
          cascades[`${v1} + ${v2}`] = { pass, rate, gain: Math.round((rate - bestRate) * 10) / 10 }
        }
      }

      countryStats[country] = { total, vendors, combined: { pass: combinedPass, rate: combinedRate }, bestVendor, bestRate, cascades }
    }

    save({ discovery_json: { ...disc, calculated_results: countryStats } as any })
    setCalculating(false)
  }

  const results = (disc.calculated_results || {}) as Record<string, any>
  const hasResults = Object.keys(results).length > 0
  const pct = (v: number) => `${v}%`
  const rateCls = (v: number) => v >= 80 ? 'text-green-400' : v >= 60 ? 'text-amber-400' : 'text-red-400'

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">POC Results</h2>
        <p className="text-xs text-[var(--text-muted)]">Upload vendor result files, calculate pass rates, and compare cascade options</p>
      </div>

      {/* Upload per vendor */}
      <Card className="p-4 space-y-3">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Upload Vendor Results</h3>
        <p className="text-[10px] text-[var(--text-muted)]">Upload CSV per vendor with columns: CUSTOMER_ID, Country/KYC_Country, Verified/Match Status (True/False)</p>
        <div className="space-y-2">
          {[...allSelectedVendors].map(vendor => (
            <div key={vendor} className="flex items-center gap-3 p-2 rounded bg-[var(--bg-secondary)] border border-[var(--border-color)]">
              <span className="text-xs font-semibold text-[var(--text-primary)] w-24">{vendor}</span>
              <label className="flex items-center gap-1 px-2 py-1 rounded text-[10px] bg-[var(--bg-tertiary)] text-[var(--text-secondary)] border border-[var(--border-color)] hover:text-[var(--text-primary)] cursor-pointer">
                {uploading ? 'Uploading...' : vendorResults[vendor]?.length ? `✓ ${vendorResults[vendor].length} records` : 'Upload CSV'}
                <input type="file" accept=".csv,.tsv,.txt" onChange={e => handleUploadVendorResults(vendor, e)} className="hidden" />
              </label>
              {vendorResults[vendor]?.length > 0 && (
                <button type="button" onClick={() => save({ discovery_json: { ...disc, vendor_results: { ...vendorResults, [vendor]: [] } } as any })}
                  className="text-[10px] text-[var(--text-muted)] hover:text-red-400 cursor-pointer">Clear</button>
              )}
            </div>
          ))}
        </div>
        {resultVendors.length >= 1 && (
          <button type="button" onClick={calculate} disabled={calculating}
            className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/20 cursor-pointer disabled:opacity-50">
            {calculating ? <><Loader2 size={12} className="animate-spin" /> Calculating...</> : '🧮 Calculate Results'}
          </button>
        )}
      </Card>

      {hasResults && (
        <>
          {/* Per-vendor per-country table */}
          <Card className="overflow-hidden">
            <div className="px-4 py-3 border-b border-[var(--border-color)]">
              <h3 className="text-sm font-semibold text-[var(--text-primary)]">Pass Rates by Country & Vendor</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[var(--bg-tertiary)] border-b border-[var(--border-color)]">
                    <th className="text-left px-3 py-2 font-semibold text-[var(--text-secondary)]">Country</th>
                    <th className="text-right px-3 py-2 font-semibold text-[var(--text-secondary)]">Records</th>
                    {resultVendors.map(v => <th key={v} className="text-right px-3 py-2 font-semibold text-[var(--text-secondary)]">{v}</th>)}
                    <th className="text-right px-3 py-2 font-semibold text-[var(--text-secondary)]">Combined</th>
                    <th className="text-center px-3 py-2 font-semibold text-[var(--text-secondary)]">Best Single</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(results).sort(([a], [b]) => a.localeCompare(b)).map(([country, data]) => {
                    const d = data as any
                    const target = ((disc.country_config || {})[country] || {}).target_rate
                    return (
                      <tr key={country} className="border-b border-[var(--border-color)] hover:bg-[var(--bg-secondary)]">
                        <td className="px-3 py-2 font-semibold text-[var(--text-primary)]">
                          {country}
                          {target && <span className="ml-1 text-[9px] text-[var(--text-muted)]">target: {target}%</span>}
                        </td>
                        <td className="px-3 py-2 text-right text-[var(--text-muted)]">{d.total}</td>
                        {resultVendors.map(v => {
                          const vs = d.vendors?.[v]
                          const isBest = v === d.bestVendor
                          return (
                            <td key={v} className={`px-3 py-2 text-right font-bold ${vs ? rateCls(vs.rate) : 'text-[var(--text-muted)]'}`}>
                              {isBest && '★ '}{vs ? pct(vs.rate) : '-'}
                              {vs && <div className="text-[9px] font-normal text-[var(--text-muted)]">{vs.pass}/{d.total}</div>}
                            </td>
                          )
                        })}
                        <td className={`px-3 py-2 text-right font-bold ${rateCls(d.combined?.rate || 0)}`}>
                          {d.combined ? pct(d.combined.rate) : '-'}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/10 text-green-400 font-semibold">{d.bestVendor}</span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Cascade analysis */}
          {Object.values(results).some((d: any) => Object.keys(d.cascades || {}).length > 0) && (
            <Card className="overflow-hidden">
              <div className="px-4 py-3 border-b border-[var(--border-color)]">
                <h3 className="text-sm font-semibold text-[var(--text-primary)]">Cascade Analysis</h3>
                <p className="text-[10px] text-[var(--text-muted)]">Pass rate when combining vendors — a record passes if ANY vendor in the cascade matches</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[var(--bg-tertiary)] border-b border-[var(--border-color)]">
                      <th className="text-left px-3 py-2 font-semibold text-[var(--text-secondary)]">Country</th>
                      <th className="text-left px-3 py-2 font-semibold text-[var(--text-secondary)]">Cascade</th>
                      <th className="text-right px-3 py-2 font-semibold text-[var(--text-secondary)]">Pass Rate</th>
                      <th className="text-right px-3 py-2 font-semibold text-[var(--text-secondary)]">Gain vs Best Single</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(results).sort(([a], [b]) => a.localeCompare(b)).map(([country, data]) => {
                      const d = data as any
                      return Object.entries(d.cascades || {}).sort(([, a]: any, [, b]: any) => b.rate - a.rate).map(([cascade, stats]: any) => (
                        <tr key={`${country}-${cascade}`} className="border-b border-[var(--border-color)] hover:bg-[var(--bg-secondary)]">
                          <td className="px-3 py-2 font-semibold text-[var(--text-primary)]">{country}</td>
                          <td className="px-3 py-2 text-[var(--text-secondary)]">{cascade}</td>
                          <td className={`px-3 py-2 text-right font-bold ${rateCls(stats.rate)}`}>{pct(stats.rate)}</td>
                          <td className={`px-3 py-2 text-right ${stats.gain > 0 ? 'text-green-400 font-medium' : 'text-[var(--text-muted)]'}`}>
                            {stats.gain > 0 ? `+${stats.gain}%` : '-'}
                          </td>
                        </tr>
                      ))
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Recommended cascade per country */}
          <Card className="p-4 space-y-3">
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">Recommendation Summary</h3>
            <div className="space-y-2">
              {Object.entries(results).sort(([a], [b]) => a.localeCompare(b)).map(([country, data]) => {
                const d = data as any
                const bestCascade = Object.entries(d.cascades || {}).sort(([, a]: any, [, b]: any) => b.rate - a.rate)[0]
                const target = ((disc.country_config || {})[country] || {}).target_rate
                const meetsTarget = target && d.bestRate >= parseFloat(target)
                const cascadeMeetsTarget = target && bestCascade && (bestCascade[1] as any).rate >= parseFloat(target)

                return (
                  <div key={country} className="flex items-center gap-3 p-2 rounded bg-[var(--bg-secondary)] border border-[var(--border-color)]">
                    <span className="text-xs font-bold text-[var(--text-primary)] w-24">{country}</span>
                    <div className="flex-1 flex items-center gap-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${meetsTarget ? 'bg-green-500/10 text-green-400' : 'bg-amber-500/10 text-amber-400'}`}>
                        Best: {d.bestVendor} @ {pct(d.bestRate)}
                      </span>
                      {bestCascade && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${cascadeMeetsTarget ? 'bg-green-500/10 text-green-400' : 'bg-cyan-500/10 text-cyan-400'}`}>
                          Cascade: {bestCascade[0]} @ {pct((bestCascade[1] as any).rate)} (+{(bestCascade[1] as any).gain}%)
                        </span>
                      )}
                      {target && (
                        <span className={`text-[10px] ${meetsTarget || cascadeMeetsTarget ? 'text-green-400' : 'text-red-400'}`}>
                          Target: {target}% {meetsTarget ? '✓ Met (single)' : cascadeMeetsTarget ? '✓ Met (cascade)' : '✗ Not met'}
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </Card>
        </>
      )}
    </div>
  )
}

// ─── POC Checklist & Expectations ────────────────────────────────────────
const CHECKLIST_SECTIONS = [
  {
    id: 'customer_expectations',
    title: 'Customer Expectations',
    description: 'Confirm these with the customer before proceeding',
    items: [
      { id: 'success_criteria_defined', label: 'Success criteria defined and agreed', hint: 'Target pass rates per country, matching criteria, what constitutes a successful POC' },
      { id: 'matching_criteria_agreed', label: 'Matching criteria confirmed', hint: 'e.g. Name + Address (partial), Name + DOB, 1+1, 2+2. Must be signed off by customer AND Vanessa' },
      { id: 'data_format_explained', label: 'Data format requirements explained', hint: 'Customer must use Frankie Batch Template. Incorrect data leads to bad results' },
      { id: 'timeline_agreed', label: 'Timeline agreed', hint: 'Live connector setup ~10 business days. Vendor turnaround varies. Set realistic expectations' },
      { id: 'data_source_explained', label: 'Customer understands where data comes from', hint: 'Top of funnel / random sample (preferred), curated sample, or end-of-funnel fails only (expect low rates)' },
      { id: 'demographics_discussed', label: 'Demographics discussed', hint: 'Age distribution within each country can impact pass rates' },
      { id: 'consent_confirmed', label: 'Consent status confirmed', hint: 'Has customer collected consent from entities? Required for consent-based sources (Equifax, Experian, DVS)' },
      { id: 'gov_id_availability', label: 'Government ID data availability confirmed', hint: 'Without Gov ID data, 34+ sources across 7+ countries excluded' },
      { id: 'pricing_expectations', label: 'Pricing expectations set', hint: 'Do not commit pricing. POCs are non-standard. Can it be accomplished via standard agreements?' },
      { id: 'results_format_agreed', label: 'Results delivery format agreed', hint: 'JSON, Portal, Presentation/pitch deck — set expectation for what they will receive' },
      { id: 'poc_vs_mock', label: 'POC type clarified (POC vs Mock)', hint: 'Is this a real data POC or a mock/demo with test data to prove scenarios? Complex rules may need mock' },
    ],
  },
  {
    id: 'customer_data',
    title: 'Customer Data Assessment',
    description: 'Understand the data quality and coverage',
    items: [
      { id: 'data_collection_method', label: 'How was data collected?', hint: 'Website via IDV/Biometrics? Customer-entered data? Impacts data quality' },
      { id: 'data_volume_confirmed', label: 'Volume confirmed per country', hint: 'Min 100, ideal 500, max 1000 per country' },
      { id: 'data_fields_mapped', label: 'Data fields mapped and confirmed', hint: 'Name, Address, DOB, Phone, National ID — specify detailed fields (street, postcode, state etc.)' },
      { id: 'data_sample_reviewed', label: 'Sample data reviewed', hint: 'Run 50-100 records first to validate configuration before full batch' },
      { id: 'data_restrictions', label: 'Data source restrictions discussed', hint: 'Any restrictions on sources? Government only? No commercial? What is acceptable?' },
      { id: 'data_transfer_method', label: 'Secure data transfer method agreed', hint: 'SFTP or Keybase. Never share PII in unsecured formats' },
    ],
  },
  {
    id: 'vendor_assessment',
    title: 'Vendor & Data Source Assessment',
    description: 'Confirm vendor readiness and data alignment',
    items: [
      { id: 'vendors_selected', label: 'Vendors selected per country', hint: 'Based on coverage, pricing, API status. Check for Socure+GDC overlap for 2+ checks' },
      { id: 'vendor_data_alignment', label: 'Customer data aligns with vendor requirements', hint: 'Mandatory PII fields per vendor match what customer provides. Assess early in process' },
      { id: 'vendor_matching_criteria', label: 'Vendor matching criteria confirmed', hint: 'Name + Address are generally primary. ID usage differs by provider and country' },
      { id: 'vendor_data_assessed', label: 'Vendors have assessed the data', hint: 'Partners may omit records (not fail) for other jurisdictions or malformed data. Match rates based on checks run, not data provided' },
      { id: 'vendor_pricing_confirmed', label: 'Vendor pricing / no-charge confirmed', hint: 'Confirm each vendor will not charge, or what the arrangement is' },
      { id: 'vendor_turnaround', label: 'Vendor turnaround confirmed', hint: 'GDC ~10 days. Socure unreliable for batch. Allow time for credential setup too' },
      { id: 'cascade_or_point', label: 'Cascade vs point offering clarified', hint: 'Are we testing individual vendors or emulating a cascade? Multiple partners or via FrankieOne?' },
    ],
  },
  {
    id: 'internal_setup',
    title: 'Internal Setup',
    description: 'FrankieOne internal readiness',
    items: [
      { id: 'hubspot_ticket', label: 'HubSpot ticket created', hint: 'Technical Section > Implementation Support Required > POC / Complex Demo' },
      { id: 'slack_channel', label: 'Slack channel created with CS, Tech Ops, Solution Support', hint: 'All relevant people in one channel' },
      { id: 'tech_walkthrough', label: 'Technical walkthrough with TechOps, CS, Implementation', hint: 'Discuss plan in detail including code review of scripts' },
      { id: 'account_setup', label: 'POC account/environment set up', hint: 'Child account under relevant parent. Use live connectors. Clean up after POC' },
      { id: 'vendor_credentials', label: 'Vendor credentials obtained', hint: 'Customer-specific creds for consent-based sources. ~10 business days for new onboarding forms' },
      { id: 'connector_config', label: 'Connectors configured with correct match criteria', hint: 'Fuzziness settings in Tech Ops ticket. Forgetting this = meaningless results' },
      { id: 'workflow_recipe', label: 'Workflow/recipe configured', hint: 'check_type set (e.g. one_plus_address). Country-specific address rules in root config' },
      { id: 'batch_file_prepared', label: 'Batch file prepared', hint: 'user_id mapped to customer_reference. dump_raw_response: true. Country field included' },
      { id: 'poc_owner_assigned', label: 'POC owner assigned', hint: 'Who is leading? Who from Partner / Implementation / Product?' },
    ],
  },
  {
    id: 'technical_readiness',
    title: 'Technical Readiness',
    description: 'Technical capabilities and limitations',
    items: [
      { id: 'tech_capability', label: 'We have the technology to run all required checks', hint: 'Can we do what the customer needs? Or does this need a new connector/feature?' },
      { id: 'easy_vs_hard', label: 'POC complexity assessed (easy vs hard path)', hint: 'Easy: existing connector, known process. Hard: new connector, cascade, custom API build' },
      { id: 'custom_scripts', label: 'Custom scripts assessed', hint: 'Most POCs need custom scripts increasing timelines. Factor this in' },
      { id: 'scaled_remediation', label: 'Remediation capability noted', hint: 'FrankieOne does NOT have scaled remediation capability. POCs are within that scope' },
      { id: 'compliance_validity', label: 'Compliance validity confirmed', hint: 'Would the results be compliant? Do we have consent passed to data sources?' },
      { id: 'output_standardised', label: 'Output format standardised', hint: 'Include all raw results — name matches, DOB matches, address matches. Overlay 1+/2+ rulesets' },
    ],
  },
  {
    id: 'results_delivery',
    title: 'Results & Delivery',
    description: 'How results are calculated and presented',
    items: [
      { id: 'pass_rate_methodology', label: 'Pass rate methodology agreed', hint: 'Total data provided vs checks performed? e.g. 1000 records, 900 checked, 810 passed = 90% or 81%? Either OK if explained' },
      { id: 'first_pass_methodology', label: 'First-pass pass rate approach explained', hint: 'We run all vendors simultaneously, not cascade. Cascade projections calculated from results' },
      { id: 'results_meeting', label: 'Results meeting scheduled', hint: 'Portal demo, presentation with results, walk customer through findings' },
    ],
  },
]

function PocChecklist({ poc, save }: { poc: PocRecord; save: (data: Partial<PocRecord>) => void }) {
  const disc = (poc.discovery_json as any) || {}
  const checks = (disc.checklist || {}) as Record<string, boolean>
  const notes = (disc.checklist_notes || {}) as Record<string, string>
  const [expandedNotes, setExpandedNotes] = useState<string | null>(null)

  const toggle = (id: string) => {
    const updated = { ...checks, [id]: !checks[id] }
    save({ discovery_json: { ...disc, checklist: updated } as any })
  }

  const saveNote = (id: string, value: string) => {
    const updated = { ...notes, [id]: value }
    save({ discovery_json: { ...disc, checklist_notes: updated } as any })
  }

  const totalItems = CHECKLIST_SECTIONS.reduce((sum, s) => sum + s.items.length, 0)
  const checkedItems = Object.values(checks).filter(Boolean).length

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">Checklist & Expectations</h2>
          <p className="text-xs text-[var(--text-muted)]">{checkedItems}/{totalItems} items confirmed</p>
        </div>
        <div className="w-32 h-1.5 bg-[var(--bg-tertiary)] rounded-full overflow-hidden">
          <div className="h-full bg-green-500 transition-all" style={{ width: `${totalItems > 0 ? (checkedItems / totalItems) * 100 : 0}%` }} />
        </div>
      </div>

      {CHECKLIST_SECTIONS.map(section => {
        const sectionChecked = section.items.filter(i => checks[i.id]).length
        return (
          <Card key={section.id} className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-[var(--text-primary)]">{section.title}</h3>
                <p className="text-[10px] text-[var(--text-muted)]">{section.description}</p>
              </div>
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${sectionChecked === section.items.length ? 'bg-green-500/10 text-green-400' : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)]'}`}>
                {sectionChecked}/{section.items.length}
              </span>
            </div>
            <div className="space-y-1">
              {section.items.map(item => (
                <div key={item.id}>
                  <div className="flex items-start gap-2 py-1 group">
                    <button type="button" onClick={() => toggle(item.id)}
                      className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center text-[9px] shrink-0 cursor-pointer transition-colors ${
                        checks[item.id] ? 'bg-green-500 border-green-500 text-white' : 'border-[var(--border-color)] hover:border-[var(--text-muted)]'
                      }`}>
                      {checks[item.id] && '✓'}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs ${checks[item.id] ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}>{item.label}</div>
                      <div className="text-[10px] text-[var(--text-muted)]">{item.hint}</div>
                    </div>
                    <button type="button" onClick={() => setExpandedNotes(expandedNotes === item.id ? null : item.id)}
                      className={`text-[9px] px-1.5 py-0.5 rounded cursor-pointer transition-colors ${
                        notes[item.id] ? 'bg-cyan-500/10 text-cyan-400' : 'opacity-0 group-hover:opacity-100 text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                      }`}>
                      {notes[item.id] ? '📝' : '+ note'}
                    </button>
                  </div>
                  {expandedNotes === item.id && (
                    <div className="ml-6 mb-1">
                      <textarea value={notes[item.id] || ''} onChange={e => saveNote(item.id, e.target.value)} onBlur={() => { if (!notes[item.id]) setExpandedNotes(null) }}
                        rows={2} placeholder="Add a note..." className={`${ic} !text-[10px]`} autoFocus />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )
      })}
    </div>
  )
}

// ─── Client Confirmation ─────────────────────────────────────────────────
// ─── Data Template Export ─────────────────────────────────────────────────
// Generates country-specific PII field definitions for data template sheets.
// Base fields (name, DOB, address, ID, phone) are always included.
// Country overrides customise labels, formats, and examples per locale.

type FieldDef = { field: string; format: string; example: string; required: boolean }

// Phone dial codes by country
const DIAL_CODES: Record<string, string> = {
  US: '+1', CA: '+1', GB: '+44', AU: '+61', NZ: '+64', IN: '+91', CN: '+86',
  CO: '+57', BR: '+55', MX: '+52', AR: '+54', CL: '+56', PE: '+51',
  DE: '+49', FR: '+33', IT: '+39', ES: '+34', NL: '+31', SE: '+46',
  JP: '+81', KR: '+82', SG: '+65', MY: '+60', TH: '+66', PH: '+63',
  ID: '+62', VN: '+84', ZA: '+27', NG: '+234', KE: '+254', EG: '+20',
  AE: '+971', SA: '+966', IL: '+972', TR: '+90', PK: '+92', BD: '+880',
}

// Country-specific overrides for name and address fields
// ID fields are derived dynamically from COUNTRY_ID_TYPES in idTypes.ts
interface CountryOverride {
  nameFields: FieldDef[]
  addressFields: FieldDef[]
  dobNote?: string
}

const COUNTRY_OVERRIDES: Record<string, CountryOverride> = {
  CN: {
    nameFields: [
      { field: 'Full Name (Chinese)', format: 'Chinese characters (Hanzi) — romanised/Latin names will fail matching', example: '张伟', required: true },
      { field: 'Full Name (Pinyin)', format: 'Pinyin romanisation — used as fallback only', example: 'Zhang Wei', required: false },
    ],
    addressFields: [
      { field: 'Street Address', format: 'Street name and number in Hanzi', example: '建国路88号', required: false },
      { field: 'District', format: 'District/area in Hanzi', example: '朝阳区', required: false },
      { field: 'City', format: 'City name in Hanzi', example: '北京市', required: false },
      { field: 'Province', format: 'Province name in Hanzi', example: '北京', required: false },
      { field: 'Postal Code', format: '6-digit postal code', example: '100020', required: false },
    ],
  },
  CO: {
    nameFields: [
      { field: 'First Name (Primer Nombre)', format: 'First given name', example: 'Juan', required: true },
      { field: 'Second Name (Segundo Nombre)', format: 'Second given name — common in Colombia, do not omit', example: 'Carlos', required: false },
      { field: 'First Surname (Primer Apellido)', format: 'Paternal surname — Colombia uses TWO surnames, both matter for matching', example: 'Rodriguez', required: true },
      { field: 'Second Surname (Segundo Apellido)', format: 'Maternal surname — dropping this will cause mismatches', example: 'Garcia', required: true },
    ],
    addressFields: [
      { field: 'Street Address', format: 'Street type (Calle/Carrera/etc.) and number', example: 'Calle 100 #15-20', required: false },
      { field: 'Apartment/Unit', format: 'Apartment, office, or floor if applicable', example: 'Apto 301', required: false },
      { field: 'City', format: 'City name', example: 'Bogota', required: false },
      { field: 'Department', format: 'Colombian department (state equivalent)', example: 'Cundinamarca', required: false },
      { field: 'Postal Code', format: 'Postal code if available', example: '110111', required: false },
    ],
  },
  US: {
    nameFields: [
      { field: 'First Name', format: 'Legal first name', example: 'John', required: true },
      { field: 'Middle Name', format: 'Middle name or initial if available', example: 'Michael', required: false },
      { field: 'Last Name', format: 'Legal last name / surname', example: 'Smith', required: true },
    ],
    addressFields: [
      { field: 'Street Address', format: 'Street address line 1', example: '123 Main Street', required: true },
      { field: 'Apartment/Unit', format: 'Address line 2 if applicable', example: 'Apt 4B', required: false },
      { field: 'City', format: 'City name', example: 'New York', required: true },
      { field: 'State', format: '2-letter state code', example: 'NY', required: true },
      { field: 'ZIP Code', format: '5-digit or ZIP+4', example: '10001', required: true },
    ],
    dobNote: 'YYYY-MM-DD (not MM/DD/YYYY)',
  },
  BR: {
    nameFields: [
      { field: 'First Name', format: 'Legal first name', example: 'Maria', required: true },
      { field: 'Last Name', format: 'Full surname(s) — include all parts', example: 'da Silva Santos', required: true },
    ],
    addressFields: [
      { field: 'Street Address', format: 'Logradouro — street type and name', example: 'Rua Augusta, 1500', required: false },
      { field: 'Complement', format: 'Apartment, suite, block', example: 'Apto 42', required: false },
      { field: 'Neighbourhood', format: 'Bairro', example: 'Consolação', required: false },
      { field: 'City', format: 'City name', example: 'São Paulo', required: false },
      { field: 'State', format: '2-letter state code (UF)', example: 'SP', required: false },
      { field: 'CEP', format: '8-digit postal code', example: '01305-100', required: false },
    ],
  },
  MX: {
    nameFields: [
      { field: 'First Name', format: 'Nombre(s)', example: 'Carlos', required: true },
      { field: 'Paternal Surname (Apellido Paterno)', format: 'Father\'s surname — Mexico uses two surnames', example: 'Lopez', required: true },
      { field: 'Maternal Surname (Apellido Materno)', format: 'Mother\'s surname', example: 'Hernandez', required: true },
    ],
    addressFields: [
      { field: 'Street Address', format: 'Calle and number', example: 'Av. Reforma 222', required: false },
      { field: 'Apartment/Unit', format: 'Interior number if applicable', example: 'Int. 5', required: false },
      { field: 'Colonia', format: 'Neighbourhood/colonia', example: 'Juárez', required: false },
      { field: 'City', format: 'City / Delegación', example: 'Ciudad de México', required: false },
      { field: 'State', format: 'State name', example: 'CDMX', required: false },
      { field: 'Postal Code', format: '5-digit postal code', example: '06600', required: false },
    ],
  },
  IN: {
    nameFields: [
      { field: 'First Name', format: 'Legal first name', example: 'Priya', required: true },
      { field: 'Last Name', format: 'Surname / family name', example: 'Sharma', required: true },
    ],
    addressFields: [
      { field: 'Street Address', format: 'House/flat number and street', example: '42, MG Road', required: false },
      { field: 'Locality', format: 'Area / locality name', example: 'Indiranagar', required: false },
      { field: 'City', format: 'City name', example: 'Bangalore', required: false },
      { field: 'State', format: 'State name', example: 'Karnataka', required: false },
      { field: 'PIN Code', format: '6-digit PIN code', example: '560038', required: false },
    ],
  },
  GB: {
    nameFields: [
      { field: 'First Name', format: 'Legal first name', example: 'James', required: true },
      { field: 'Last Name', format: 'Surname', example: 'Wilson', required: true },
    ],
    addressFields: [
      { field: 'Street Address', format: 'House number and street', example: '10 Downing Street', required: true },
      { field: 'Flat/Unit', format: 'Flat, apartment, or unit number', example: 'Flat 2', required: false },
      { field: 'City', format: 'City / town', example: 'London', required: true },
      { field: 'County', format: 'County if applicable', example: 'Greater London', required: false },
      { field: 'Postcode', format: 'UK postcode', example: 'SW1A 2AA', required: true },
    ],
  },
  AU: {
    nameFields: [
      { field: 'First Name', format: 'Legal first name', example: 'Sarah', required: true },
      { field: 'Middle Name', format: 'Middle name if applicable', example: 'Jane', required: false },
      { field: 'Last Name', format: 'Surname / family name', example: 'Thompson', required: true },
    ],
    addressFields: [
      { field: 'Street Address', format: 'Unit/street number and street name', example: '42 Collins Street', required: true },
      { field: 'Unit/Flat', format: 'Unit or apartment number', example: 'Unit 5', required: false },
      { field: 'Suburb', format: 'Suburb name', example: 'Melbourne', required: true },
      { field: 'State', format: '2-3 letter state code', example: 'VIC', required: true },
      { field: 'Postcode', format: '4-digit postcode', example: '3000', required: true },
    ],
  },
}

/** Build field definitions for any country. Uses country-specific overrides where available, otherwise falls back to generic fields. */
function getFieldDefsForCountry(code: string): FieldDef[] {
  const override = COUNTRY_OVERRIDES[code]
  const dialCode = DIAL_CODES[code] || ''
  const phoneExample = dialCode ? `${dialCode}412345678` : '+XX412345678'

  const fields: FieldDef[] = []

  // Record ID — always first
  fields.push({ field: 'Record ID', format: 'Unique identifier per record', example: `${code}-001`, required: true })

  // Name fields
  if (override?.nameFields) {
    fields.push(...override.nameFields)
  } else {
    fields.push(
      { field: 'First Name', format: 'Legal first name as per official ID', example: 'Maria', required: true },
      { field: 'Last Name', format: 'Legal last name / surname as per official ID', example: 'Garcia', required: true },
    )
  }

  // Date of birth
  const dobNote = override?.dobNote || 'YYYY-MM-DD'
  fields.push({ field: 'Date of Birth', format: dobNote, example: '1990-03-15', required: true })

  // ID documents — derived from COUNTRY_ID_TYPES + universal types
  const countryIds = COUNTRY_ID_TYPES[code] || []
  const allIds = [...UNIVERSAL_ID_TYPES, ...countryIds]
  for (const idType of allIds) {
    const note = 'note' in idType ? (idType as { note?: string }).note : undefined
    fields.push({
      field: idType.label,
      format: `Store as TEXT — preserve leading zeros${note ? ` (${note})` : ''}`,
      example: '',
      required: false,
    })
  }

  // Phone
  const phoneHint = dialCode ? `International format with ${dialCode} prefix` : 'International format with country code prefix'
  fields.push({ field: 'Phone Number', format: `${phoneHint} — can significantly improve pass rates`, example: phoneExample, required: false })

  // Email
  fields.push({ field: 'Email', format: 'Email address', example: 'name@example.com', required: false })

  // Address fields
  if (override?.addressFields) {
    fields.push(...override.addressFields)
  } else {
    fields.push(
      { field: 'Street Address', format: 'Street number and name', example: '123 Main Street', required: false },
      { field: 'Apartment/Unit', format: 'Unit, apartment, or suite if applicable', example: 'Apt 4B', required: false },
      { field: 'City', format: 'City / town name', example: 'Capital City', required: false },
      { field: 'State / Province', format: 'State, province, or region', example: 'Province', required: false },
      { field: 'Postal Code', format: 'Postal / ZIP code', example: '10001', required: false },
    )
  }

  return fields
}

function DataTemplateExport({ poc, save }: { poc: PocRecord; save: (data: Partial<PocRecord>) => void }) {
  const disc = (poc.discovery_json as any) || {}
  const countries = poc.countries_in_scope || []
  const countryConfig = (disc.country_config || {}) as Record<string, any>
  const companyName = poc.company_name || poc.deal_name || 'Client'
  const contactName = disc.contact_name || ''

  // Saved country templates
  const [savedTemplates, setSavedTemplates] = useState<Record<string, { fields: FieldDef[]; updated_at: string }>>({})
  const [savingTemplate, setSavingTemplate] = useState<string | null>(null)

  useEffect(() => {
    // Load saved templates for all countries in scope
    countries.forEach(async (code) => {
      try {
        const res = await pocCountryTemplatesApi.get(code)
        if (res?.data) {
          const t = res.data as any
          const parsed = typeof t.fields_json === 'string' ? JSON.parse(t.fields_json) : t.fields_json
          setSavedTemplates(prev => ({ ...prev, [code]: { fields: parsed, updated_at: t.updated_at } }))
        }
      } catch { /* no saved template */ }
    })
  }, [countries.join(',')])

  /** Get field defs for a country — uses saved template if available, otherwise generates defaults */
  const getFields = (code: string): FieldDef[] => {
    return savedTemplates[code]?.fields || getFieldDefsForCountry(code)
  }

  const saveTemplate = async (code: string) => {
    setSavingTemplate(code)
    const fields = getFields(code)
    const name = COUNTRIES.find(c => c.code === code)?.name || code
    try {
      await pocCountryTemplatesApi.save(code, { country_name: name, fields_json: fields })
      setSavedTemplates(prev => ({ ...prev, [code]: { fields, updated_at: new Date().toISOString() } }))
      showToast(`${name} template saved`, 'success')
    } catch {
      showToast('Failed to save template', 'error')
    }
    setSavingTemplate(null)
  }

  const deleteTemplate = async (code: string) => {
    const name = COUNTRIES.find(c => c.code === code)?.name || code
    try {
      await pocCountryTemplatesApi.remove(code)
      setSavedTemplates(prev => { const n = { ...prev }; delete n[code]; return n })
      showToast(`${name} template removed — will use defaults`, 'success')
    } catch {
      showToast('Failed to remove template', 'error')
    }
  }

  // Email state
  const dataRequest = (disc.data_request_email || {}) as {
    body?: string
    sent?: boolean
    sent_at?: string
    sent_by?: string
  }
  const [emailBody, setEmailBody] = useState(dataRequest.body || '')
  const [emailCopied, setEmailCopied] = useState(false)

  const saveDataRequest = (updates: Record<string, unknown>) => {
    const updated = { ...dataRequest, ...updates }
    save({ discovery_json: { ...disc, data_request_email: updated } as any })
  }

  const generateTemplate = async () => {
    const ExcelJS = await import('exceljs')
    const wb = new ExcelJS.Workbook()
    wb.creator = 'FrankieOne'
    wb.created = new Date()

    // Brand colours
    const TEAL = '0D9488'       // primary teal
    const TEAL_LIGHT = 'CCFBF1' // light teal bg
    const DARK = '1E293B'       // slate-800 text
    const GRAY_BG = 'F1F5F9'    // slate-100
    const WHITE = 'FFFFFF'
    const AMBER_LIGHT = 'FEF3C7'

    const sectionFont = { bold: true, color: { argb: DARK }, size: 11 }
    const bodyFont = { color: { argb: DARK }, size: 10 }
    const borderStyle = { style: 'thin' as const, color: { argb: 'CBD5E1' } }
    const thinBorder = { top: borderStyle, bottom: borderStyle, left: borderStyle, right: borderStyle }

    // ── Sheet 1: Overview ──────────────────────────────────────────────
    const ws = wb.addWorksheet('Overview')
    ws.columns = [
      { width: 45 }, { width: 25 }, { width: 20 }, { width: 20 },
    ]

    // Title row
    ws.mergeCells('A1:D1')
    const titleCell = ws.getCell('A1')
    titleCell.value = `FrankieOne Proof of Concept — Data Template`
    titleCell.font = { bold: true, color: { argb: WHITE }, size: 16 }
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: TEAL } }
    titleCell.alignment = { vertical: 'middle', horizontal: 'left' }
    ws.getRow(1).height = 36

    // Sub-header
    ws.mergeCells('A2:D2')
    const subCell = ws.getCell('A2')
    subCell.value = `Prepared for: ${companyName}  |  ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`
    subCell.font = { color: { argb: TEAL }, size: 11 }
    subCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: TEAL_LIGHT } }
    subCell.alignment = { vertical: 'middle' }
    ws.getRow(2).height = 24

    // Blank row
    let row = 4

    // ── What is this POC? ──
    const addSection = (title: string, lines: string[]) => {
      ws.mergeCells(`A${row}:D${row}`)
      const c = ws.getCell(`A${row}`)
      c.value = title
      c.font = { ...sectionFont, size: 12 }
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GRAY_BG } }
      ws.getRow(row).height = 22
      row++
      for (const line of lines) {
        ws.mergeCells(`A${row}:D${row}`)
        const lc = ws.getCell(`A${row}`)
        lc.value = line
        lc.font = bodyFont
        lc.alignment = { wrapText: true }
        row++
      }
      row++ // blank spacer
    }

    addSection('WHAT IS THIS POC?', [
      'FrankieOne will run your customer records through our identity verification network to demonstrate that we can match or beat your current pass rates.',
      'If FrankieOne achieves higher pass rates across the countries in scope, this forms the basis for migrating your verification to our platform.',
    ])

    addSection('HOW IT WORKS', [
      '1. You provide us with a sample of your customer records (this spreadsheet)',
      '2. We run them through our verification network (multiple data sources per country)',
      '3. We compare our pass rates against your current provider',
      '4. We present results showing pass/fail per record, per vendor, and per country',
    ])

    // ── Countries in scope table ──
    ws.mergeCells(`A${row}:D${row}`)
    const ctHeader = ws.getCell(`A${row}`)
    ctHeader.value = 'COUNTRIES IN SCOPE'
    ctHeader.font = { ...sectionFont, size: 12 }
    ctHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GRAY_BG } }
    ws.getRow(row).height = 22
    row++

    // Table header
    const tableHeaders = ['Country', 'Your Current Pass Rate', 'Target Rate', 'Records Needed']
    const thRow = ws.getRow(row)
    tableHeaders.forEach((h, i) => {
      const cell = thRow.getCell(i + 1)
      cell.value = h
      cell.font = { bold: true, color: { argb: WHITE }, size: 10 }
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: TEAL } }
      cell.border = thinBorder
      cell.alignment = { vertical: 'middle' }
    })
    thRow.height = 22
    row++

    // Table data rows
    for (const code of countries) {
      const cc = countryConfig[code] || {}
      const name = COUNTRIES.find(c => c.code === code)?.name || code
      const currentRate = cc.current_rate ? `${cc.current_rate}%` : 'REQUIRED — please provide'
      const targetRate = cc.target_rate ? `${cc.target_rate}%` : 'Beat current rate'
      const records = cc.records || 'Min 500 recommended'
      const dataRow = ws.getRow(row)
      const vals = [name, currentRate, targetRate, String(records)]
      vals.forEach((v, i) => {
        const cell = dataRow.getCell(i + 1)
        cell.value = v
        cell.font = bodyFont
        cell.border = thinBorder
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: row % 2 === 0 ? TEAL_LIGHT : WHITE } }
      })
      row++
    }

    // Target rate note
    ws.mergeCells(`A${row}:D${row}`)
    const noteCell = ws.getCell(`A${row}`)
    noteCell.value = 'Note: The target rate indicates the pass rate at which you would be prepared to move your verification to FrankieOne.'
    noteCell.font = { italic: true, color: { argb: '64748B' }, size: 9 }
    row += 2

    // ── Important callout ──
    ws.mergeCells(`A${row}:D${row}`)
    const impCell = ws.getCell(`A${row}`)
    impCell.value = '⚠  IMPORTANT: YOUR CURRENT PASS RATES'
    impCell.font = { bold: true, color: { argb: DARK }, size: 11 }
    impCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: AMBER_LIGHT } }
    ws.getRow(row).height = 22
    row++
    for (const line of [
      'We MUST know your current pass rates for each country before the POC begins.',
      'This is how we measure success — FrankieOne needs to beat your current rates.',
      'Please provide your current pass rates per country in the table above so we can set meaningful targets.',
    ]) {
      ws.mergeCells(`A${row}:D${row}`)
      const c = ws.getCell(`A${row}`)
      c.value = line
      c.font = bodyFont
      row++
    }
    row++

    addSection('DATA REQUIREMENTS', [
      '• Each country has its own sheet in this workbook with the exact fields we need',
      '• Required fields are marked — missing required fields will result in automatic failures',
      '• Dates must be in YYYY-MM-DD format (e.g., 1990-03-15)',
      '• Phone numbers: include wherever possible with country code (e.g., +1 for US, +86 for China) — can significantly improve pass rates',
      '• National IDs / SSNs must be stored as TEXT, not numbers (leading zeros matter)',
      '• Addresses should be broken into separate fields (street, city, state/province, postal code) as shown in each tab',
      '• Ensure your current pass rates are provided in the Countries in Scope table above',
    ])

    addSection('DATA SECURITY', [
      '• All data will be transferred securely via encrypted channel',
      '• Data is processed in FrankieOne\'s secure environment and deleted after POC completion',
      '• We recommend a minimum of 500 records per country for statistically meaningful results',
    ])

    // ── Country Sheets ─────────────────────────────────────────────────
    for (const code of countries) {
      const name = COUNTRIES.find(c => c.code === code)?.name || code
      const fieldDefs = getFields(code)
      const cc = countryConfig[code] || {}

      const sheetName = name.length > 31 ? name.slice(0, 31) : name
      const cs = wb.addWorksheet(sheetName)

      // Set column widths — ensure at least 4 columns for the ruleset section
      const minCols = Math.max(fieldDefs.length, 4)
      cs.columns = Array.from({ length: minCols }, (_, i) => {
        const f = fieldDefs[i]
        return { width: f ? Math.max(f.field.length + 2, f.format.length * 0.7, 18) : 18 }
      })

      // Row 1: Guide note (merged across all columns)
      cs.mergeCells(1, 1, 1, fieldDefs.length)
      const guideCell = cs.getCell('A1')
      guideCell.value = `${name} — Please verify the ruleset below, then fill in your customer records. Required fields are highlighted.`
      guideCell.font = { bold: true, color: { argb: TEAL }, size: 10 }
      guideCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: TEAL_LIGHT } }
      guideCell.alignment = { wrapText: true, vertical: 'middle' }
      cs.getRow(1).height = 28

      // Rows 3-4: Compact verification ruleset
      const s1 = (cc.match_source_1 || []) as string[]
      const s2 = (cc.match_source_2 || []) as string[]
      const derived = deriveMatchLabel(s1, s2)
      const matchType = derived.type || cc.match_type || '2+2'
      const addressReq = cc.address_mode === 'none' ? 'No'
        : cc.address_mode === 'partial' ? 'Partial' : 'Yes'
      // Countries where gov ID is essential for reliable matching
      const GOV_ID_REQUIRED_COUNTRIES = new Set(['CO', 'BR', 'MX', 'IN', 'CN', 'ID', 'VN', 'TH', 'PH', 'NG', 'KE', 'ZA', 'PK'])
      const govId = s1.includes('id') || s2.includes('id') || GOV_ID_REQUIRED_COUNTRIES.has(code) ? 'Yes' : 'No'
      const nameFuzz = cc.fuzziness_name || 'Nicknames & minor variations accepted'
      const addrFuzz = cc.fuzziness_address || 'Standard abbreviations accepted (St/Street, Rd/Road)'

      // Row 3: Ruleset header labels
      const ruleLabels = ['Ruleset', 'Address Required', 'Gov ID Required', 'Name Fuzziness', 'Address Fuzziness']
      const ruleValues = [matchType, addressReq, govId, nameFuzz, addrFuzz]

      const ruleRow3 = cs.getRow(3)
      ruleLabels.forEach((label, i) => {
        const cell = ruleRow3.getCell(i + 1)
        cell.value = label
        cell.font = { bold: true, color: { argb: DARK }, size: 9 }
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GRAY_BG } }
        cell.border = thinBorder
      })
      ruleRow3.height = 18

      // Row 4: Ruleset values (editable — yellow background)
      const ruleRow4 = cs.getRow(4)
      ruleValues.forEach((val, i) => {
        const cell = ruleRow4.getCell(i + 1)
        cell.value = val
        cell.font = { color: { argb: TEAL }, size: 9 }
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFDE8' } }
        cell.border = thinBorder
        cell.alignment = { wrapText: true }
      })
      ruleRow4.height = 20

      // Row 5: Note
      cs.mergeCells(5, 1, 5, ruleLabels.length)
      const noteCell = cs.getCell('A5')
      noteCell.value = 'If any of the above defaults are incorrect, please update them or let us know.'
      noteCell.font = { italic: true, color: { argb: '94A3B8' }, size: 8 }

      // Data section starts at row 7 (after blank row 6)
      const dataStartRow = 7

      // Format hints row
      const hintRow = cs.getRow(dataStartRow)
      fieldDefs.forEach((f, i) => {
        const cell = hintRow.getCell(i + 1)
        cell.value = f.required ? `${f.format}  (REQUIRED)` : f.format
        cell.font = { italic: true, color: { argb: '94A3B8' }, size: 9 }
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GRAY_BG } }
        cell.alignment = { wrapText: true, vertical: 'top' }
        cell.border = thinBorder
      })
      hintRow.height = 30

      // Column headers
      const headRow = cs.getRow(dataStartRow + 1)
      fieldDefs.forEach((f, i) => {
        const cell = headRow.getCell(i + 1)
        cell.value = f.field
        cell.font = { bold: true, color: { argb: WHITE }, size: 10 }
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: f.required ? TEAL : '64748B' } }
        cell.border = thinBorder
        cell.alignment = { vertical: 'middle' }
      })
      headRow.height = 24

      // Example rows (5 pre-filled with Record ID)
      for (let i = 1; i <= 5; i++) {
        const dr = cs.getRow(dataStartRow + 1 + i)
        fieldDefs.forEach((_f, idx) => {
          const cell = dr.getCell(idx + 1)
          if (idx === 0) {
            cell.value = `${code}-${String(i).padStart(3, '0')}`
            cell.font = { color: { argb: '94A3B8' }, size: 10 }
          }
          cell.border = thinBorder
        })
      }

      // Freeze panes: lock everything above data rows
      cs.views = [{ state: 'frozen', ySplit: dataStartRow + 1, xSplit: 0 }]
    }

    // ── Download ──
    const buffer = await wb.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    const fileName = `${companyName.replace(/[^a-zA-Z0-9]/g, '_')}_POC_Data_Template.xlsx`
    a.href = url
    a.download = fileName
    a.click()
    URL.revokeObjectURL(url)
    showToast(`Downloaded ${fileName}`, 'success')
  }

  const generateEmail = () => {
    const countryNames = countries.map(c => COUNTRIES.find(x => x.code === c)?.name || c)
    const countryListStr = countryNames.length <= 2
      ? countryNames.join(' and ')
      : countryNames.slice(0, -1).join(', ') + ', and ' + countryNames[countryNames.length - 1]

    const greeting = contactName ? `Hi ${contactName},` : 'Hi,'
    const body = `${greeting}

We're excited to show you the pass rates FrankieOne can deliver across ${countryListStr}. We're confident the results will speak for themselves.

To get started, please find attached a data template spreadsheet. It's straightforward — the workbook has:

  - An Overview tab with a quick summary of the process
  - One tab per country (${countryListStr}) with the exact fields we need, formatting guidance, and examples pre-filled

All you need to do is populate each country tab with a sample of your customer records (ideally 500+ per country). Each tab shows exactly what format we need — just follow the examples.

One thing that makes a big difference — phone numbers. Include these wherever you can, they significantly boost match rates in many countries.

Once we have the file back, we'll run it through our network and share a full comparison. The whole process is quick and painless.

Let me know if you have any questions — happy to jump on a call to walk through the template.

Best regards`

    setEmailBody(body)
    saveDataRequest({ body })
    showToast('Email template generated', 'success')
  }

  const copyEmail = () => {
    navigator.clipboard.writeText(emailBody)
    setEmailCopied(true)
    showToast('Email copied to clipboard', 'success')
    setTimeout(() => setEmailCopied(false), 2000)
  }

  const markAsSent = () => {
    saveDataRequest({
      sent: true,
      sent_at: new Date().toISOString(),
    })
    showToast('Marked as sent', 'success')
  }

  const markAsUnsent = () => {
    saveDataRequest({ sent: false, sent_at: null })
  }

  if (countries.length === 0) return null

  return (
    <div className="space-y-4">
      {/* Template download */}
      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
              <Table size={14} className="text-cyan-400" />
              Data Template
            </h3>
            <p className="text-xs text-[var(--text-muted)] mt-0.5">
              Excel workbook with country-specific data templates and formatting requirements
            </p>
          </div>
          <button type="button" onClick={generateTemplate}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/20 cursor-pointer transition-colors">
            <FileDown size={13} />
            Download Template
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <span className="text-[10px] px-2 py-0.5 rounded bg-[var(--bg-tertiary)] text-[var(--text-muted)] border border-[var(--border-color)]">
            Sheet 1: Overview
          </span>
          {countries.map(code => {
            const name = COUNTRIES.find(c => c.code === code)?.name || code
            const hasSaved = !!savedTemplates[code]
            return (
              <span key={code} className={`text-[10px] px-2 py-0.5 rounded border flex items-center gap-1 ${
                hasSaved
                  ? 'bg-green-500/10 text-green-400 border-green-500/30'
                  : 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30'
              }`}>
                {name}
                {hasSaved && <span title="Using saved template">✓</span>}
              </span>
            )
          })}
        </div>
        {/* Save / reset templates per country */}
        <div className="flex flex-wrap gap-1.5">
          {countries.map(code => {
            const name = COUNTRIES.find(c => c.code === code)?.name || code
            const hasSaved = !!savedTemplates[code]
            return (
              <div key={code} className="flex items-center gap-1">
                <button type="button" onClick={() => saveTemplate(code)} disabled={savingTemplate === code}
                  className="flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-medium bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] border border-[var(--border-color)] cursor-pointer disabled:opacity-50 transition-colors"
                  title={hasSaved ? `Update saved ${name} template` : `Save ${name} fields as reusable template`}>
                  <Save size={10} />
                  {hasSaved ? `Update ${code}` : `Save ${code}`}
                </button>
                {hasSaved && (
                  <button type="button" onClick={() => deleteTemplate(code)}
                    className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] text-red-400 hover:bg-red-500/10 cursor-pointer transition-colors"
                    title={`Remove saved template — revert to defaults for ${name}`}>
                    <X size={10} />
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </Card>

      {/* Data request email */}
      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
              <Mail size={14} className="text-purple-400" />
              Data Request Email
            </h3>
            <p className="text-xs text-[var(--text-muted)] mt-0.5">
              Template email to send with the data template attached
            </p>
          </div>
          <div className="flex items-center gap-2">
            {dataRequest.sent && (
              <span className="flex items-center gap-1 text-[10px] text-green-400 font-medium">
                <CheckCheck size={12} />
                Sent {dataRequest.sent_at ? new Date(dataRequest.sent_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : ''}
              </span>
            )}
            <button type="button" onClick={generateEmail}
              className="flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-medium bg-purple-500/10 text-purple-400 border border-purple-500/30 hover:bg-purple-500/20 cursor-pointer transition-colors">
              {emailBody ? '↻ Regenerate' : '✨ Generate Email'}
            </button>
          </div>
        </div>

        {!emailBody && (
          <div className="py-6 text-center text-[var(--text-muted)]">
            <Mail size={20} className="mx-auto mb-2 opacity-30" />
            <p className="text-xs">Generate an email template to send alongside the data template</p>
            <p className="text-[10px] mt-0.5">Includes country-specific instructions and formatting requirements</p>
          </div>
        )}

        {emailBody && (
          <div className="space-y-2">
            <textarea
              value={emailBody}
              onChange={e => {
                setEmailBody(e.target.value)
                saveDataRequest({ body: e.target.value })
              }}
              rows={16}
              className={`${ic} !text-xs font-mono`}
            />
            <div className="flex items-center gap-2">
              <button type="button" onClick={copyEmail}
                className="flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-medium bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] cursor-pointer transition-colors">
                {emailCopied ? <><CheckCheck size={11} className="text-green-400" /> Copied!</> : <><Copy size={11} /> Copy to Clipboard</>}
              </button>
              {!dataRequest.sent ? (
                <button type="button" onClick={markAsSent}
                  className="flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-medium bg-green-500/10 text-green-400 border border-green-500/30 hover:bg-green-500/20 cursor-pointer transition-colors">
                  <Send size={11} />
                  Mark as Sent
                </button>
              ) : (
                <button type="button" onClick={markAsUnsent}
                  className="flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-medium bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-secondary)] cursor-pointer transition-colors">
                  Undo sent
                </button>
              )}
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}

/** Extract bullet points from the considerations markdown for a specific country */
function PocClientConfirmation({ poc, save }: { poc: PocRecord; save: (data: Partial<PocRecord>) => void }) {
  const disc = (poc.discovery_json as any) || {}
  const confirmation = (disc.client_confirmation || {}) as {
    confirmed?: boolean
    confirmed_at?: string
    confirmed_by?: string
    notes?: string
  }

  const saveConfirmation = (updates: Record<string, unknown>) => {
    const updated = { ...confirmation, ...updates }
    save({ discovery_json: { ...disc, client_confirmation: updated } as any })
  }

  const toggleConfirmed = () => {
    const nowConfirmed = !confirmation.confirmed
    saveConfirmation({
      confirmed: nowConfirmed,
      confirmed_at: nowConfirmed ? new Date().toISOString() : null,
    })
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">Client Confirmation</h2>
        <p className="text-xs text-[var(--text-muted)]">Generate a data template and email to send to the client for their POC data</p>
      </div>

      {/* Data Template Export */}
      <DataTemplateExport poc={poc} save={save} />

      {/* Confirmation tracking */}
      <Card className="p-4 space-y-3">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Client Response</h3>
        <div className="flex items-center gap-3">
          <button type="button" onClick={toggleConfirmed}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border cursor-pointer transition-colors ${
              confirmation.confirmed
                ? 'bg-green-500/10 text-green-400 border-green-500/30'
                : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] border-[var(--border-color)] hover:text-[var(--text-primary)]'
            }`}>
            <span className={`w-4 h-4 rounded border flex items-center justify-center text-[9px] ${
              confirmation.confirmed ? 'bg-green-500 border-green-500 text-white' : 'border-[var(--border-color)]'
            }`}>
              {confirmation.confirmed && '✓'}
            </span>
            {confirmation.confirmed ? 'Client has confirmed' : 'Awaiting client confirmation'}
          </button>
          {confirmation.confirmed && confirmation.confirmed_at && (
            <span className="text-[10px] text-[var(--text-muted)]">
              Confirmed {new Date(confirmation.confirmed_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
        <div>
          <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide">Confirmation Notes</label>
          <DTextarea
            value={confirmation.notes || ''}
            onSave={v => saveConfirmation({ notes: v })}
            rows={3}
            placeholder="Any notes from the client's response, changes requested, etc."
            className={`${ic} !text-xs mt-1`}
          />
        </div>
      </Card>
    </div>
  )
}

// ─── Considerations ──────────────────────────────────────────────────────
const DEFAULT_CONSIDERATIONS_PROMPT = `You are a pre-sales solutions consultant at FrankieOne (identity verification platform). You are reviewing the countries and vendors selected for a Proof of Concept (POC) and producing a GOTCHA-FOCUSED checklist — things that will cause match failures, block the POC, or surprise the team if they don't know them upfront.

Your inputs per country are (1) the country verification guide and (2) each selected vendor's source data (required fields, consent flag, notes).

## Your job: surface the dangerous stuff

The team already has the field lists and source details in the Data Sources tool. Do NOT repeat them. Your job is to extract the **non-obvious, high-impact gotchas** that will trip up the POC if missed. Think:
- Formatting traps: "names MUST be in Chinese characters (Hanzi) — Latin/romanised names will fail matching"
- Unusual requirements: "Passport Verification requires BOTH passport number AND national ID — passport alone won't work"
- Name handling risks: "two surnames are standard — dropping the second will cause mismatches"
- ID gotchas: "ID length varies — don't hard-code a digit count", "store as text not numeric"
- Consent flags: note where consent is required (just the flag, don't elaborate)
- Document quirks: "date of issue is mapped to DOB — DOB is not actually verified by this source"

If something is obvious or already visible in the field lists (e.g. "you need a name and DOB"), skip it. Only surface things that would cause a **"we didn't know that"** moment.

## Hard rule: no guessing, and skip any country with no guide

Use ONLY the provided country guide and vendor source data. Do **not** fill gaps with your own general knowledge. If a fact isn't in the provided data, don't state it.

If a country has **no verification guide**, skip it with a one-line reason:

> ## US
> No country guide provided — skipping. *(Vendor source data was provided for GDC and DataZoo; add a US guide to enable considerations.)*

## Output structure per country

## {COUNTRY}

**⚠ Watch out:**
- Lead with the biggest gotcha in bold — the single thing most likely to cause a failed POC if missed. Make it impossible to skim past.
- Then list any other non-obvious gotchas, one bullet each. Keep each to one line.

**Consent:** One line — which sources require consent (yes/no per source). If all are the same, say it once.

**Questions for the client (3–5 max):**
- Only questions where the answer changes the setup. No obvious ones like "will you collect a name?"

## Rules

- Do NOT list required fields per source — that data is already in the Data Sources tool.
- Do NOT restate the country guide as prose — extract only the gotchas.
- Say each thing once. No repetition across sections.
- Keep it short. A country with one gotcha gets two lines, not a page.
- No country guide → skip with a one-line reason.`

function PocConsiderations({ poc, save }: { poc: PocRecord; save: (data: Partial<PocRecord>) => void }) {
  const disc = (poc.discovery_json as any) || {}
  const [considerations, setConsiderations] = useState<string>(disc.considerations_text || '')
  const [generating, setGenerating] = useState<boolean>(disc.considerations_status === 'generating')
  const [copied, setCopied] = useState(false)
  const [showPrompt, setShowPrompt] = useState(false)
  const [prompt, setPrompt] = useState<string>(disc.considerations_prompt || DEFAULT_CONSIDERATIONS_PROMPT)

  const countries = poc.countries_in_scope || []
  const countryConfig = (disc.country_config || {}) as Record<string, { vendors?: string[] }>
  const activeVendors = new Set<string>()
  for (const code of countries) {
    for (const v of (countryConfig[code]?.vendors || [])) activeVendors.add(v)
  }

  const countryName = (code: string) => COUNTRIES.find(c => c.code === code)?.name || code

  const [error, setError] = useState<string | null>(null)

  // Country guide status
  const [guideStatus, setGuideStatus] = useState<Record<string, 'loading' | 'exists' | 'missing' | 'generating' | 'error'>>({})
  const [guidesChecked, setGuidesChecked] = useState(false)

  // Check which country guides exist
  useEffect(() => {
    if (!countries.length || !activeVendors.size) return
    let mounted = true
    const initial: Record<string, 'loading'> = {}
    for (const code of countries) initial[code] = 'loading'
    setGuideStatus(initial)
    ;(async () => {
      const status: Record<string, 'exists' | 'missing'> = {}
      for (const code of countries) {
        try {
          const res = await countryGuidesApi.get(code)
          const d = (res?.data as any)?.data ?? res?.data
          status[code] = d?.status === 'generated' && d?.generated_content ? 'exists' : 'missing'
        } catch {
          status[code] = 'missing'
        }
      }
      if (mounted) { setGuideStatus(status); setGuidesChecked(true) }
    })()
    return () => { mounted = false }
  }, [countries.join(','), activeVendors.size]) // eslint-disable-line react-hooks/exhaustive-deps

  const missingGuides = countries.filter(c => guideStatus[c] === 'missing')

  const generateGuide = async (code: string) => {
    const name = countryName(code)
    setGuideStatus(prev => ({ ...prev, [code]: 'generating' }))
    try {
      // Ensure the profile exists first (create if missing)
      try { await countryGuidesApi.create(code, name) } catch { /* already exists */ }
      const res = await countryGuidesApi.generate(code)
      const d = (res?.data as any)?.data ?? res?.data
      if (d?.status === 'generated' || d?.content) {
        setGuideStatus(prev => ({ ...prev, [code]: 'exists' }))
        showToast(`${name} guide generated`, 'success')
      } else {
        throw new Error('No content returned')
      }
    } catch (err: any) {
      setGuideStatus(prev => ({ ...prev, [code]: 'error' }))
      showToast(`Failed to generate ${name} guide: ${err?.message || 'Unknown error'}`, 'error')
    }
  }

  const generateAllMissing = async () => {
    for (const code of missingGuides) {
      await generateGuide(code)
    }
  }

  // Poll for results when generating
  useEffect(() => {
    if (!generating) return
    const interval = setInterval(async () => {
      try {
        const res = await pocApi.getConsiderationsStatus(poc.id)
        const d = (res?.data as any)?.data ?? res?.data
        if (d?.status === 'done' && d.considerations) {
          setConsiderations(d.considerations)
          setGenerating(false)
          setError(null)
        } else if (d?.status === 'error') {
          setError(d.error || 'Generation failed. Please try again.')
          setGenerating(false)
        }
        // 'generating' → keep polling
      } catch { /* ignore polling errors */ }
    }, 3000)
    return () => clearInterval(interval)
  }, [generating, poc.id])

  const handleGenerate = async () => {
    setGenerating(true)
    setError(null)
    // Save the current prompt to discovery_json
    const customPrompt = prompt !== DEFAULT_CONSIDERATIONS_PROMPT ? prompt : undefined
    if (customPrompt) {
      save({ discovery_json: { ...disc, considerations_prompt: prompt } as any })
    }
    try {
      await pocApi.generateConsiderations(poc.id, customPrompt)
      // Backend returns immediately — polling useEffect handles the rest
    } catch (err: any) {
      setError(err?.message || 'Request failed. Try again.')
      setGenerating(false)
    }
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(considerations)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Render inline markdown: **bold**, *italic*, `code`
  const renderInline = (text: string): React.ReactNode[] => {
    const parts: React.ReactNode[] = []
    let remaining = text
    let key = 0
    while (remaining) {
      // Match **bold**, *italic*, or `code`
      const m = remaining.match(/(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/)
      if (!m || m.index === undefined) {
        parts.push(remaining)
        break
      }
      if (m.index > 0) parts.push(remaining.slice(0, m.index))
      if (m[2]) parts.push(<strong key={key++} className="font-semibold text-[var(--text-primary)]">{m[2]}</strong>)
      else if (m[3]) parts.push(<em key={key++}>{m[3]}</em>)
      else if (m[4]) parts.push(<code key={key++} className="px-1 py-0.5 rounded bg-[var(--bg-tertiary)] text-[var(--text-secondary)] text-[10px]">{m[4]}</code>)
      remaining = remaining.slice((m.index || 0) + m[0].length)
    }
    return parts
  }

  // Markdown renderer for considerations
  const renderMarkdown = (md: string) => {
    const lines = md.split('\n')
    const elements: React.ReactNode[] = []
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]
      if (line.startsWith('## ')) {
        elements.push(<h3 key={i} className="text-sm font-semibold text-[var(--text-primary)] mt-5 mb-2 first:mt-0">{line.slice(3)}</h3>)
      } else if (line.startsWith('### ')) {
        elements.push(<h4 key={i} className="text-xs font-semibold text-cyan-400 mt-3 mb-1">{line.slice(4)}</h4>)
      } else if (line.match(/^\*\*(.+?)\*\*/)) {
        // Bold section header line like **Posture:** or **Client data to confirm:**
        elements.push(<p key={i} className="text-xs text-[var(--text-secondary)] mt-3 mb-1">{renderInline(line)}</p>)
      } else if (line.startsWith('- ')) {
        elements.push(<div key={i} className="text-xs text-[var(--text-muted)] ml-3 mb-1 leading-relaxed">• {renderInline(line.slice(2))}</div>)
      } else if (line.startsWith('> ')) {
        elements.push(<blockquote key={i} className="border-l-2 border-cyan-500/30 pl-3 py-0.5 text-xs text-[var(--text-secondary)] italic ml-3 mb-1">{renderInline(line.slice(2))}</blockquote>)
      } else if (line.trim()) {
        elements.push(<p key={i} className="text-xs text-[var(--text-secondary)] mb-1">{renderInline(line)}</p>)
      }
    }
    return elements
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-primary)] flex items-center gap-2">
            <Lightbulb size={18} />
            Considerations
          </h2>
          <p className="text-xs text-[var(--text-muted)]">
            AI-generated considerations based on country verification guides and vendor source data for {countries.length} countr{countries.length !== 1 ? 'ies' : 'y'} and {activeVendors.size} vendor{activeVendors.size !== 1 ? 's' : ''}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {considerations && (
            <button onClick={handleCopy}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-[var(--border-color)] hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] transition-colors">
              {copied ? <CheckCheck size={13} className="text-green-500" /> : <Copy size={13} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          )}
          <button onClick={handleGenerate} disabled={generating || !countries.length || !activeVendors.size}
            className="flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg bg-cyan-600 text-white hover:bg-cyan-500 disabled:opacity-50 transition-colors"
            title={!activeVendors.size ? 'Select vendors per country on the Overview tab first' : ''}>
            {generating ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            {generating ? 'Generating…' : considerations ? 'Regenerate' : 'Generate Considerations'}
          </button>
        </div>
      </div>

      {/* Country list */}
      {countries.length > 0 && (
        <Card className="p-4">
          <p className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-2">Countries in scope</p>
          <div className="flex flex-wrap gap-2">
            {countries.map(code => {
              const vendors = countryConfig[code]?.vendors || []
              const gs = guideStatus[code]
              return (
                <div key={code} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border ${gs === 'missing' ? 'bg-amber-500/5 border-amber-500/30' : 'bg-[var(--bg-tertiary)] border-[var(--border-color)]'}`}>
                  <span className="text-xs font-medium text-[var(--text-primary)]">{countryName(code)}</span>
                  <span className="text-[10px] text-[var(--text-muted)]">({code})</span>
                  {vendors.length > 0 && (
                    <span className="text-[10px] text-cyan-400 ml-1">{vendors.length} vendor{vendors.length !== 1 ? 's' : ''}</span>
                  )}
                  {gs === 'exists' && <span className="text-[10px] text-green-400" title="Country guide available">✓ guide</span>}
                  {gs === 'missing' && <span className="text-[10px] text-amber-400" title="No country guide — considerations will be skipped for this country">✗ no guide</span>}
                  {gs === 'generating' && <Loader2 size={10} className="animate-spin text-cyan-400" />}
                  {gs === 'loading' && <Loader2 size={10} className="animate-spin text-[var(--text-muted)]" />}
                  {gs === 'error' && <span className="text-[10px] text-red-400">✗ error</span>}
                </div>
              )
            })}
          </div>
        </Card>
      )}

      {/* Missing country guides warning */}
      {guidesChecked && missingGuides.length > 0 && activeVendors.size > 0 && (
        <Card className="p-4 border-amber-500/30 space-y-3">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-400">
                {missingGuides.length === countries.length ? 'No country guides found' : `${missingGuides.length} of ${countries.length} country guide${missingGuides.length !== 1 ? 's' : ''} missing`}
              </p>
              <p className="text-xs text-[var(--text-muted)] mt-0.5">
                Considerations require a country verification guide for each country. Countries without a guide will be skipped.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {missingGuides.map(code => {
              const gs = guideStatus[code]
              return (
                <button
                  key={code}
                  onClick={() => generateGuide(code)}
                  disabled={gs === 'generating'}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/30 hover:bg-amber-500/20 disabled:opacity-50 cursor-pointer transition-colors"
                >
                  {gs === 'generating' ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                  Generate {countryName(code)} guide
                </button>
              )
            })}
          </div>
          {missingGuides.length > 1 && (
            <button
              onClick={generateAllMissing}
              disabled={missingGuides.some(c => guideStatus[c] === 'generating')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-cyan-600 text-white hover:bg-cyan-500 disabled:opacity-50 cursor-pointer transition-colors"
            >
              {missingGuides.some(c => guideStatus[c] === 'generating') ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Generate all {missingGuides.length} missing guides
            </button>
          )}
        </Card>
      )}

      {/* Collapsible prompt editor */}
      <div>
        <button onClick={() => setShowPrompt(!showPrompt)}
          className="flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors">
          <ChevronDown size={14} className={`transition-transform ${showPrompt ? 'rotate-180' : ''}`} />
          {showPrompt ? 'Hide' : 'Show'} AI Prompt
          {prompt !== DEFAULT_CONSIDERATIONS_PROMPT && <span className="text-[10px] text-amber-500 ml-1">(customised)</span>}
        </button>
        {showPrompt && (
          <div className="mt-2 space-y-2">
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              className="w-full h-64 p-3 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] font-mono resize-y focus:outline-none focus:ring-1 focus:ring-cyan-500/50"
              placeholder="Enter custom prompt..."
            />
            <div className="flex items-center gap-2">
              {prompt !== DEFAULT_CONSIDERATIONS_PROMPT && (
                <button onClick={() => setPrompt(DEFAULT_CONSIDERATIONS_PROMPT)}
                  className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] underline">
                  Reset to default
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {!countries.length && (
        <Card className="p-6 text-center">
          <p className="text-sm text-[var(--text-muted)]">Add countries in the Request Form tab first.</p>
        </Card>
      )}

      {!activeVendors.size && countries.length > 0 && (
        <Card className="p-6 text-center space-y-1">
          <p className="text-sm text-[var(--text-muted)]">Vendors must be selected before considerations can be generated.</p>
          <p className="text-xs text-[var(--text-muted)]">Go to the Overview tab and select vendors for each country under "Success Criteria & Match Configuration".</p>
        </Card>
      )}

      {error && (
        <Card className="p-4 border-red-500/30">
          <p className="text-sm text-red-500">{error}</p>
        </Card>
      )}

      {generating && !considerations && (
        <Card className="p-8 text-center">
          <Loader2 size={24} className="animate-spin mx-auto mb-3 text-cyan-400" />
          <p className="text-sm text-[var(--text-muted)]">Analysing country guides and vendor data…</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">This can take up to 2 minutes.</p>
        </Card>
      )}

      {considerations && (
        <Card className="p-5">
          <div className="max-h-[700px] overflow-y-auto pr-2">
            {renderMarkdown(considerations)}
          </div>
        </Card>
      )}
    </div>
  )
}

// ─── Vendor Coordination ─────────────────────────────────────────────────
function VendorCoordination({ poc, save }: { poc: PocRecord; save: (data: Partial<PocRecord>) => void }) {
  const [countryData, setCountryData] = useState<Record<string, any>>({})
  const [loading, setLoading] = useState(true)
  const [sampleFields, setSampleFields] = useState<Record<string, string[]>>(() => ((poc.discovery_json as any)?.sample_fields || {}) as Record<string, string[]>)
  const [fieldMappings, setFieldMappings] = useState<Record<string, Record<string, string>>>(() => ((poc.discovery_json as any)?.field_mappings || {}) as Record<string, Record<string, string>>)
  const [mappingCountry, setMappingCountry] = useState<string | null>(null)
  const [_samplePreview, setSamplePreview] = useState<string | null>(null)
  const [drafting, setDrafting] = useState<string | null>(null) // vendor being drafted
  const [drafts, setDrafts] = useState<Record<string, string>>(() => {
    // Load persisted drafts from vendor coordination data
    const vc = ((poc.discovery_json as any)?.vendor_coordination || {}) as Record<string, any>
    const loaded: Record<string, string> = {}
    for (const [vid, coord] of Object.entries(vc)) {
      if (coord?.draft_message) loaded[vid] = coord.draft_message
    }
    return loaded
  })
  const [copied, setCopied] = useState<string | null>(null)
  const [sampleCollapsed, setSampleCollapsed] = useState(Object.keys(sampleFields).length > 0) // auto-collapse if data exists
  const [vendorCollapsed, setVendorCollapsed] = useState<Record<string, boolean>>({})
  const disc = (poc.discovery_json as any) || {}
  const vendorCoord = (disc.vendor_coordination || {}) as Record<string, any>

  const countries = poc.countries_in_scope || []

  // Load all country data on mount
  useEffect(() => {
    let mounted = true
    ;(async () => {
      for (const code of countries) {
        try {
          const res = await fetch(`/api/coverage/country/${code}`, { headers: getAuthHeaders() })
          if (res.ok && mounted) {
            const d = await res.json()
            const inner = d?.data?.data || d?.data || {}
            setCountryData(prev => ({ ...prev, [code]: inner }))
          }
        } catch {}
      }
      if (mounted) setLoading(false)
    })()
    return () => { mounted = false }
  }, [countries.join(',')])

  // Collect all selected vendors across countries from the intake form
  const allSelectedByCountry: Record<string, string[]> = {}
  for (const code of countries) {
    const countryConf = ((disc.country_config || {})[code]) || {}
    allSelectedByCountry[code] = (countryConf.vendors || []) as string[]
  }

  // Group by vendor — which countries each vendor covers
  const vendorCountries: Record<string, string[]> = {}
  for (const [code, vendors] of Object.entries(allSelectedByCountry)) {
    for (const v of vendors) {
      if (!vendorCountries[v]) vendorCountries[v] = []
      vendorCountries[v].push(code)
    }
  }
  const activeVendors = Object.keys(vendorCountries).sort()

  const saveVendorCoord = (vendorId: string, field: string, value: any) => {
    const updated = { ...vendorCoord, [vendorId]: { ...(vendorCoord[vendorId] || {}), [field]: value } }
    save({ discovery_json: { ...disc, vendor_coordination: updated } as any })
  }

  // PII field name mapping — common CSV column names to standard PII
  const PII_MAP: Record<string, string> = {
    'first_name': 'First Name', 'firstname': 'First Name', 'given_name': 'First Name', 'givenname': 'First Name',
    'last_name': 'Last Name', 'lastname': 'Last Name', 'family_name': 'Last Name', 'familyname': 'Last Name', 'surname': 'Last Name',
    'middle_name': 'Middle Name', 'middlename': 'Middle Name',
    'dob': 'DOB', 'date_of_birth': 'DOB', 'dateofbirth': 'DOB', 'birth_date': 'DOB', 'birthdate': 'DOB',
    'gender': 'Gender', 'sex': 'Gender',
    'address': 'Address', 'street': 'Address', 'street_address': 'Address', 'address_line_1': 'Address', 'address1': 'Address',
    'city': 'City', 'town': 'City', 'locality': 'City',
    'state': 'State', 'province': 'State', 'region': 'State', 'subdivision': 'State',
    'postcode': 'Postal Code', 'postal_code': 'Postal Code', 'zip': 'Postal Code', 'zip_code': 'Postal Code', 'postalcode': 'Postal Code',
    'country': 'Country', 'country_code': 'Country',
    'national_id': 'National ID', 'nationalid': 'National ID', 'id_number': 'National ID', 'ssn': 'National ID', 'dni': 'National ID', 'cpf': 'National ID',
    'phone': 'Phone', 'phone_number': 'Phone', 'mobile': 'Phone', 'telephone': 'Phone',
    'email': 'Email', 'email_address': 'Email',
    'customer_id': 'Customer ID', 'id': 'Customer ID', 'user_id': 'Customer ID',
  }

  const handleSampleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      const text = reader.result as string
      setSamplePreview(text.split('\n').slice(0, 4).join('\n'))
      const sampleTable = parseDelimitedTable(text)
      if (sampleTable.rows.length < 1) return
      const headers = sampleTable.headers.map(h => h.toLowerCase())

      // Map headers to PII fields
      const mappedFields = headers.map(h => PII_MAP[h] || h).filter(f => f && f !== 'Customer ID')

      // Try to detect country column and group
      const countryColIdx = headers.findIndex(h => h === 'country' || h === 'country_code')
      const fieldsByCountry: Record<string, string[]> = {}

      if (countryColIdx >= 0) {
        const seen = new Set<string>()
        for (const cols of sampleTable.rows) {
          const cc = (cols[countryColIdx] || '').toUpperCase().trim()
          if (cc && !seen.has(cc)) {
            // Detect which fields have data for this country
            const fieldsWithData = headers.map((h, idx) => ({ field: PII_MAP[h] || h, hasData: (cols[idx] || '').trim() !== '' }))
              .filter(f => f.hasData && f.field !== 'Country' && f.field !== 'Customer ID')
              .map(f => f.field)
            fieldsByCountry[cc] = fieldsWithData
            seen.add(cc)
          }
        }
      } else {
        // No country column — apply same fields to all POC countries
        for (const code of countries) {
          fieldsByCountry[code] = mappedFields
        }
      }

      setSampleFields(fieldsByCountry)
      save({ discovery_json: { ...disc, sample_fields: fieldsByCountry } as any })
    }
    reader.readAsText(file)
    e.target.value = '' // reset file input
  }

  const draftMessage = async (vendorId: string) => {
    setDrafting(vendorId)
    const vendorCtries = vendorCountries[vendorId] || []
    const countriesPayload = vendorCtries.map(code => {
      const name = COUNTRIES.find(c => c.code === code)?.name || code
      const allFields = sampleFields[code] || []
      const excluded = ((disc.excluded_fields || {}) as Record<string, string[]>)[code] || []
      const fields = allFields.filter(f => !excluded.includes(f))
      const countryConf = ((disc.country_config || {})[code]) || {}
      const s1c = (countryConf.match_source_1 || []) as string[]
      const s2c = (countryConf.match_source_2 || []) as string[]
      const derivedC = deriveMatchLabel(s1c, s2c)
      return {
        code, name, fields,
        records: parseInt(countryConf.records) || undefined,
        current_rate: countryConf.current_rate || undefined,
        target_rate: countryConf.target_rate || undefined,
        match_type: derivedC.type || countryConf.match_type || undefined,
        match_label: derivedC.label || undefined,
        address_mode: countryConf.address_mode || undefined,
        notes: countryConf.notes || undefined,
      }
    })

    // Collect vendor data source info
    const vendorSrcInfo = vendorCtries.map(code => {
      const cd = countryData[code] || {}
      const sources = ((cd.sources || []) as any[]).filter((s: any) => s.vendor === vendorId)
      return sources.map((s: any) => `${code}: ${s.data_source_header || ''} — mandatory: ${s.mandatory_pii || 'N/A'}`).join('\n')
    }).filter(Boolean).join('\n')

    // Build vendor-specific form data for the AI context
    const coord = vendorCoord[vendorId] || {}
    let vendorFormContext = coord.vendor_notes || ''

    // Socure: include their POC intake form responses
    if (vendorId.toLowerCase().includes('socure')) {
      const parts: string[] = []
      if (coord.socure_business_objective) parts.push(`Business objective: ${coord.socure_business_objective}`)
      if (coord.socure_use_case) parts.push(`Use case: ${coord.socure_use_case}`)
      if (coord.socure_product_workflow || coord.socure_products) parts.push(`Product/workflow: ${coord.socure_product_workflow || coord.socure_products}`)
      if (coord.socure_key_decision) parts.push(`Key decision: ${coord.socure_key_decision}`)
      if (coord.socure_volume) parts.push(`Total applicants: ${coord.socure_volume}`)
      if (coord.socure_target_pass_rate) parts.push(`Target pass rate: ${coord.socure_target_pass_rate}`)
      if (coord.socure_success_outcome || coord.socure_success_criteria) parts.push(`Success criteria: ${coord.socure_success_outcome || coord.socure_success_criteria}`)
      if (coord.socure_funnel_top_of_funnel) parts.push(`Top of funnel: ${coord.socure_funnel_top_of_funnel}`)
      if (coord.socure_funnel_applicant_type) parts.push(`Applicant type: ${coord.socure_funnel_applicant_type}`)
      if (coord.socure_scorecard_type) parts.push(`Scorecard type: ${coord.socure_scorecard_type}`)
      if (parts.length > 0) vendorFormContext += '\n\nSocure POC Intake Form responses:\n' + parts.join('\n')
    }

    // Datazoo: include their POC form responses
    if (vendorId.toLowerCase().includes('datazoo') || vendorId.toLowerCase() === 'dz') {
      const parts: string[] = []
      // Customer info
      parts.push(`End customer: ${poc.company_name || poc.deal_name}`)
      parts.push(`Use case: ${(coord.dz_use_case || []).length > 0 ? (coord.dz_use_case as string[]).join(', ') : 'KYC verification'}`)
      parts.push(`Objective: ${coord.dz_objective || 'Pre-go-live performance assessment'}`)
      if (coord.dz_due_date) parts.push(`Due date: ${coord.dz_due_date}`)
      // Volumes
      const totalRecs = vendorCtries.reduce((s, c) => s + (parseInt(((disc.country_config || {})[c] || {}).records) || 0), 0)
      parts.push(`Total records: ${totalRecs || 'TBD'}`)
      parts.push(`Countries: ${vendorCtries.map(c => COUNTRIES.find(x => x.code === c)?.name || c).join(', ')}`)
      const volumeBreakdown = vendorCtries.map(c => {
        const cc = ((disc.country_config || {})[c]) || {}
        return `${COUNTRIES.find(x => x.code === c)?.name || c}: ${cc.records || 'TBD'} records`
      }).join(', ')
      parts.push(`Volume breakdown: ${volumeBreakdown}`)
      // Data history & matching
      parts.push(`Data history: ${coord.dz_data_history || 'Not specified'}`)
      parts.push(`Match elements: ${coord.dz_match_elements || 'Name + Address / Name + DOB'}`)
      parts.push(`Match approach: ${coord.dz_match_approach || 'Not specified'}`)
      // Success criteria
      const targets = vendorCtries.map(code => {
        const cc = ((disc.country_config || {})[code]) || {}
        const name = COUNTRIES.find(x => x.code === code)?.name || code
        return cc.target_rate ? `${name}: target ≥ ${cc.target_rate}%` : null
      }).filter(Boolean)
      parts.push(`Success criteria: ${coord.dz_success_criteria || targets.join(', ') || 'Not specified'}`)
      parts.push(`Next steps: ${coord.dz_next_steps || 'Production implementation'}`)
      // Data quality
      if ((coord.dz_data_quality || []).length > 0) parts.push(`Known data quality issues: ${(coord.dz_data_quality as string[]).join(', ')}`)
      if (coord.dz_data_quality_notes) parts.push(`Data quality notes: ${coord.dz_data_quality_notes}`)

      vendorFormContext += '\n\nDatazoo POC Information Requirements (structure the message to address these points):\n1. End Customer, Use Case & POC Objective\n2. Volumes & Geographic Scope\n3. Data History & Match Requirements\n4. Success Criteria & Expected Outcomes\n5. Known Data Quality Characteristics\n\nOur answers:\n' + parts.join('\n')
    }

    try {
      const res = await pocApi.generateVendorMessage({
        vendor: vendorId,
        company: poc.company_name || poc.deal_name,
        countries: countriesPayload,
        vendor_sources: vendorSrcInfo || undefined,
        additional_context: vendorFormContext || undefined,
      })
      // apiRequest wraps response -> res.data = { success, data: { success, data: { message } } }
      const d = res?.data as any
      const msg = d?.data?.data?.message || d?.data?.message || d?.message || (typeof d === 'string' ? d : '')
      if (msg) {
        setDrafts(prev => ({ ...prev, [vendorId]: msg }))
        saveVendorCoord(vendorId, 'draft_message', msg)
      } else {
        const errMsg = res?.error || d?.error || 'Failed to generate message'
        setDrafts(prev => ({ ...prev, [vendorId]: `Error: ${errMsg}` }))
      }
    } catch (e) {
      setDrafts(prev => ({ ...prev, [vendorId]: `Error: ${e}` }))
    }
    setDrafting(null)
  }

  const [idTypes, setIdTypes] = useState<Record<string, { id_types: any[]; id_type_column: string | null; id_type_values: Record<string, string> }>>(() => ((poc.discovery_json as any)?.id_types_info || {}) as any)

  const mapFieldsWithAI = async (code: string, rawPaste: string) => {
    setMappingCountry(code)
    const { headers, rows: allRows } = parseDelimitedTable(rawPaste)
    if (headers.length < 1) { setMappingCountry(null); return }
    const sampleRows = allRows.slice(0, 5)
    const countryName = COUNTRIES.find(c => c.code === code)?.name || code

    try {
      const res = await fetch('/api/poc/map-fields', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ headers, sample_rows: sampleRows, country: code, country_name: countryName }),
      })
      if (res.ok) {
        const d = await res.json()
        const inner = d?.data?.data || d?.data || {}
        const mapping = inner.mapping || {}
        const idTypesData = { id_types: inner.id_types || [], id_type_column: inner.id_type_column || null, id_type_values: inner.id_type_values || {} }
        // Save mapping
        const newMappings = { ...fieldMappings, [code]: mapping }
        setFieldMappings(newMappings)
        const newIdTypes = { ...idTypes, [code]: idTypesData }
        setIdTypes(newIdTypes)
        // Extract mapped PII fields (skip 'skip' values)
        const piiFields = [...new Set(Object.values(mapping).filter((v: unknown) => v && (v as string) !== 'skip'))] as string[]
        setSampleFields(prev => ({ ...prev, [code]: piiFields }))
        save({ discovery_json: { ...disc, sample_fields: { ...sampleFields, [code]: piiFields }, field_mappings: newMappings, id_types_info: newIdTypes } as any })
      }
    } catch (e) {
      console.error('AI field mapping failed:', e)
    }
    setMappingCountry(null)
  }

  const copyToClipboard = (vendorId: string) => {
    navigator.clipboard.writeText(drafts[vendorId] || '')
    setCopied(vendorId)
    setTimeout(() => setCopied(null), 2000)
  }

  if (countries.length === 0) {
    return <div className="py-12 text-center text-[var(--text-muted)]"><p className="text-sm">Add countries on the Request Form tab first.</p></div>
  }

  if (activeVendors.length === 0) {
    return (
      <div className="py-12 text-center text-[var(--text-muted)]">
        <p className="text-sm">No vendors selected yet.</p>
        <p className="text-xs mt-1">Select vendors per country on the Request Form tab under "Success Criteria & Match Configuration".</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">Vendor Coordination</h2>
        <p className="text-xs text-[var(--text-muted)]">{activeVendors.length} vendor{activeVendors.length !== 1 ? 's' : ''} across {countries.length} countries</p>
      </div>

      {/* Sample Data — Per Country */}
      <Card className="p-4 space-y-3">
        <button type="button" onClick={() => setSampleCollapsed(!sampleCollapsed)} className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">Sample Data per Country</h3>
            {Object.keys(sampleFields).length > 0 && (
              <span className="text-[10px] text-green-400 font-semibold">{Object.keys(sampleFields).length} countr{Object.keys(sampleFields).length !== 1 ? 'ies' : 'y'} mapped</span>
            )}
          </div>
          <ChevronDown className={`w-4 h-4 text-[var(--text-muted)] transition-transform ${sampleCollapsed ? '' : 'rotate-180'}`} />
        </button>

        {!sampleCollapsed && <>
        <p className="text-[10px] text-[var(--text-muted)]">Paste a few rows of customer data (with headers) for each country to auto-detect available fields. Accepts CSV, TSV, or tab-separated data copied from a spreadsheet.</p>

        {countries.map(code => {
          const name = COUNTRIES.find(c => c.code === code)?.name || code
          const fields = sampleFields[code] || []
          const pasteKey = `paste_${code}`
          const rawPaste = (disc[pasteKey] as string) || ''

          return (
            <div key={code} className="rounded border border-[var(--border-color)] overflow-hidden">
              <div className="px-3 py-2 bg-[var(--bg-tertiary)] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-[var(--text-primary)]">{name}</span>
                  <span className="text-[10px] text-[var(--text-muted)]">({code})</span>
                  {fields.length > 0 && <span className="text-[10px] text-green-400 font-semibold">✓ {fields.length} fields detected</span>}
                </div>
              </div>
              <div className="p-3 space-y-2">
                <textarea
                  value={rawPaste}
                  onChange={e => {
                    const val = e.target.value
                    // Save raw paste
                    save({ discovery_json: { ...disc, [pasteKey]: val } as any })
                    // Parse fields
                    if (!val.trim()) { setSampleFields(prev => { const n = { ...prev }; delete n[code]; return n }); return }
                    const pasted = parseDelimitedTable(val)
                    if (pasted.headers.length < 1) return
                    const headers = pasted.headers.map(h => h.toLowerCase())
                    const mapped = headers.map(h => PII_MAP[h] || h).filter(f => f && f !== 'Country' && f !== 'Customer ID')
                    // Check which fields have actual data (not just headers)
                    if (pasted.rows.length > 0) {
                      const dataRow = pasted.rows[0]
                      const fieldsWithData = headers.map((h, idx) => ({ field: PII_MAP[h] || h, hasData: (dataRow[idx] || '').trim() !== '' }))
                        .filter(f => f.hasData && f.field !== 'Country' && f.field !== 'Customer ID')
                        .map(f => f.field)
                      setSampleFields(prev => ({ ...prev, [code]: fieldsWithData }))
                      save({ discovery_json: { ...disc, [pasteKey]: val, sample_fields: { ...sampleFields, [code]: fieldsWithData } } as any })
                    } else {
                      setSampleFields(prev => ({ ...prev, [code]: mapped }))
                      save({ discovery_json: { ...disc, [pasteKey]: val, sample_fields: { ...sampleFields, [code]: mapped } } as any })
                    }
                  }}
                  rows={3}
                  className={`${ic} !text-[10px] font-mono`}
                  placeholder={`Paste headers + a few rows for ${name}...\ne.g.\nCUSTOMER_ID\tGIVEN_NAME\tFAMILY_NAME\tDATE_OF_BIRTH\tSTREET_ADDRESS\nABC123\tJohn\tSmith\t1990-01-01\t123 Main St`}
                />
                <div className="flex items-center gap-2">
                  {rawPaste.trim() && (
                    <button type="button" disabled={mappingCountry === code} onClick={() => mapFieldsWithAI(code, rawPaste)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-medium bg-purple-500/10 text-purple-400 border border-purple-500/30 hover:bg-purple-500/20 cursor-pointer disabled:opacity-50 transition-colors">
                      {mappingCountry === code ? <><Loader2 size={10} className="animate-spin" /> Mapping...</> : '✨ AI Map Fields'}
                    </button>
                  )}
                  {fields.length > 0 && <span className="text-[10px] text-green-400 font-semibold">✓ {fields.length} PII fields</span>}
                </div>
                {/* Show mapping details */}
                {fieldMappings[code] && Object.keys(fieldMappings[code]).length > 0 && (
                  <div className="rounded bg-[var(--bg-primary)] border border-[var(--border-color)] p-2 space-y-0.5">
                    <div className="text-[9px] font-semibold text-[var(--text-muted)] uppercase mb-1">Field Mapping</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                      {Object.entries(fieldMappings[code]).map(([orig, mapped]) => (
                        <div key={orig} className="flex items-center gap-1 text-[10px]">
                          <span className="text-[var(--text-muted)] truncate">{orig}</span>
                          <span className="text-[var(--text-muted)]">→</span>
                          <span className={mapped === 'skip' ? 'text-[var(--text-muted)] opacity-40 line-through' : 'text-cyan-400 font-medium'}>{mapped}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* ID Type Detection */}
                {idTypes[code] && (idTypes[code].id_types?.length > 0 || Object.keys(idTypes[code].id_type_values || {}).length > 0) && (
                  <div className="rounded bg-purple-500/5 border border-purple-500/20 p-2 space-y-1">
                    <div className="text-[9px] font-semibold text-purple-400 uppercase">ID Types Detected</div>
                    {Object.keys(idTypes[code].id_type_values || {}).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(idTypes[code].id_type_values).map(([raw, label]) => (
                          <span key={raw} className="px-1.5 py-0.5 rounded text-[10px] bg-purple-500/10 text-purple-400 border border-purple-500/20 font-medium">
                            <span className="opacity-60">{raw}</span> → {label as string}
                          </span>
                        ))}
                      </div>
                    )}
                    {(idTypes[code].id_types || []).length > 0 && (
                      <div className="space-y-0.5">
                        {idTypes[code].id_types.map((idt: any, i: number) => (
                          <div key={i} className="flex items-center gap-2 text-[10px]">
                            <span className="text-purple-400 font-medium">{idt.type}</span>
                            {idt.format && <span className="text-[var(--text-muted)]">({idt.format})</span>}
                            {idt.confidence && <span className={`px-1 py-0.5 rounded text-[8px] ${idt.confidence === 'high' ? 'bg-green-500/10 text-green-400' : idt.confidence === 'medium' ? 'bg-amber-500/10 text-amber-400' : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)]'}`}>{idt.confidence}</span>}
                            {idt.examples?.length > 0 && <span className="text-[var(--text-muted)] font-mono text-[9px]">e.g. {idt.examples[0]}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {idTypes[code].id_type_column && <div className="text-[9px] text-[var(--text-muted)]">Source column: <span className="font-mono">{idTypes[code].id_type_column}</span></div>}
                  </div>
                )}

                {fields.length > 0 && (
                  <div>
                    <div className="text-[9px] font-semibold text-[var(--text-muted)] uppercase mb-1">Fields to include in vendor messages <span className="font-normal">(click to toggle)</span></div>
                    <div className="flex flex-wrap gap-1">
                    {fields.map(f => {
                      const excluded = ((disc.excluded_fields || {}) as Record<string, string[]>)[code] || []
                      const isExcluded = excluded.includes(f)
                      return <button key={f} type="button" onClick={() => {
                        const allExcluded = (disc.excluded_fields || {}) as Record<string, string[]>
                        const countryExcluded = allExcluded[code] || []
                        const next = isExcluded ? countryExcluded.filter((x: string) => x !== f) : [...countryExcluded, f]
                        save({ discovery_json: { ...disc, excluded_fields: { ...allExcluded, [code]: next } } as any })
                      }} className={`px-1.5 py-0.5 rounded text-[10px] border cursor-pointer transition-colors ${isExcluded ? 'bg-transparent text-[var(--text-muted)] border-[var(--border-color)] line-through opacity-40' : 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20 font-medium'}`}>{f}</button>
                    })}
                  </div>
                  </div>
                )}
              </div>
            </div>
          )
        })}

        {/* Also keep CSV upload as an option */}
        <div className="flex items-center gap-2 pt-2 border-t border-[var(--border-color)]">
          <label className="flex items-center gap-1.5 px-3 py-1.5 rounded text-[10px] font-medium bg-[var(--bg-tertiary)] text-[var(--text-muted)] border border-[var(--border-color)] hover:text-[var(--text-primary)] cursor-pointer transition-colors">
            Or upload a single CSV with all countries
            <input type="file" accept=".csv,.tsv,.txt" onChange={handleSampleUpload} className="hidden" />
          </label>
        </div>
        </>}
      </Card>

      {activeVendors.map(vendorId => {
        const vendorCtries = vendorCountries[vendorId]
        const coord = vendorCoord[vendorId] || {}
        const vendorMeta = VENDORS.find(v => v.id === vendorId.toLowerCase())

        // Collect all sources for this vendor across countries
        const allVendorSources: { code: string; sources: any[]; rec: any }[] = []
        for (const code of vendorCtries) {
          const cd = countryData[code] || {}
          const sources = ((cd.sources || []) as any[]).filter(s => s.vendor === vendorId)
          allVendorSources.push({ code, sources, rec: cd.recommendation || {} })
        }

        // Collect unique mandatory PII across all countries for this vendor
        const allPii = new Set<string>()
        for (const { sources } of allVendorSources) {
          for (const s of sources) { if (s.mandatory_pii) allPii.add(s.mandatory_pii) }
        }

        const totalRecords = vendorCtries.reduce((sum, code) => {
          const cc = ((disc.country_config || {})[code]) || {}
          return sum + (parseInt(cc.records) || 0)
        }, 0)

        return (
          <Card key={vendorId} className="p-4 space-y-3">
            {/* Vendor header — clickable to collapse */}
            <button type="button" onClick={() => setVendorCollapsed(prev => ({ ...prev, [vendorId]: !prev[vendorId] }))} className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-[var(--text-primary)]">{vendorId}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 font-semibold">
                  {vendorCtries.length} countr{vendorCtries.length !== 1 ? 'ies' : 'y'}
                </span>
                {totalRecords > 0 && <span className="text-[10px] text-[var(--text-muted)]">~{totalRecords} records</span>}
                {vendorMeta && !vendorMeta.batch_reliable && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30">Unreliable for batch</span>
                )}
                {vendorMeta?.consent_required && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/30">Consent required</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {loading && <Loader2 size={14} className="animate-spin text-[var(--text-muted)]" />}
                <ChevronDown className={`w-4 h-4 text-[var(--text-muted)] transition-transform ${vendorCollapsed[vendorId] ? '' : 'rotate-180'}`} />
              </div>
            </button>

            {!vendorCollapsed[vendorId] && <>
            {/* Countries this vendor covers */}
            <div>
              <label className={lc}>Countries</label>
              <div className="flex flex-wrap gap-1 mt-1">
                {vendorCtries.map(code => {
                  const { rec } = allVendorSources.find(a => a.code === code) || { rec: {} }
                  const isRec = rec?.recommended_vendor?.toLowerCase() === vendorId.toLowerCase()
                  const name = COUNTRIES.find(c => c.code === code)?.name || code
                  return (
                    <span key={code} className={`px-2 py-0.5 rounded text-[10px] font-medium ${isRec ? 'bg-green-500/10 text-green-400 border border-green-500/30' : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)]'}`}>
                      {isRec && '★ '}{name} ({code})
                    </span>
                  )
                })}
              </div>
            </div>

            {/* Required PII summary */}
            {allPii.size > 0 && (
              <div>
                <label className={lc}>Required PII (across all countries)</label>
                <div className="mt-1 text-[10px] text-amber-400 bg-amber-500/5 rounded p-2 border border-amber-500/20">
                  {[...allPii].join(' | ')}
                </div>
              </div>
            )}

            {/* Coordination fields */}
            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-[var(--border-color)]">
              <div>
                <label className={lc}>Vendor status</label>
                <select value={coord.data_status || 'not_engaged'} onChange={e => saveVendorCoord(vendorId, 'data_status', e.target.value)} className={`mt-0.5 ${ic} !text-xs`}>
                  <option value="not_engaged">Not yet engaged</option>
                  <option value="notified">Vendor notified</option>
                  <option value="briefed">Vendor briefed on requirements</option>
                  <option value="data_sent">Data sent to vendor</option>
                  <option value="vendor_reviewing">Vendor reviewing data</option>
                  <option value="vendor_ready">Vendor ready to process</option>
                  <option value="needs_more_data">Vendor needs more data</option>
                  <option value="processing">Vendor processing</option>
                  <option value="complete">Complete</option>
                </select>
                {coord.data_status === 'data_sent' && (
                  <div className="mt-1">
                    <label className="text-[9px] text-[var(--text-muted)]">Date sent</label>
                    <input type="date" value={coord.data_sent_date || ''} onChange={e => saveVendorCoord(vendorId, 'data_sent_date', e.target.value)}
                      className={`mt-0.5 ${ic} !text-xs`} />
                  </div>
                )}
              </div>
              <div>
                <label className={lc}>Processing method</label>
                <select value={coord.processing || ''} onChange={e => saveVendorCoord(vendorId, 'processing', e.target.value)} className={`mt-0.5 ${ic} !text-xs`}>
                  <option value="">Select...</option>
                  <option value="vendor_processes">Vendor processes on their side</option>
                  <option value="f1_internal">We process internally</option>
                  <option value="f1_special_account">We run under special F1 account</option>
                  <option value="customer_api">Customer runs via API</option>
                  <option value="techops_batch">Tech Ops batch</option>
                </select>
              </div>
              <div>
                <label className={lc}>Pricing arrangement</label>
                <select value={coord.pricing_status || 'not_confirmed'} onChange={e => saveVendorCoord(vendorId, 'pricing_status', e.target.value)} className={`mt-0.5 ${ic} !text-xs`}>
                  <option value="not_confirmed">Not confirmed</option>
                  <option value="no_charge">No charge confirmed</option>
                  <option value="jackie_signed_off">Jackie signed off</option>
                  <option value="customer_to_pay">Customer to pay</option>
                  <option value="quoted">Quoted — awaiting customer approval</option>
                  <option value="customer_approved">Customer approved pricing</option>
                </select>
              </div>
              <div>
                <label className={lc}>Expected turnaround</label>
                <input value={coord.turnaround || ''} onChange={e => saveVendorCoord(vendorId, 'turnaround', e.target.value)} className={`mt-0.5 ${ic} !text-xs`} placeholder="e.g. 10 business days" />
              </div>
              <div>
                <label className={lc}>Vendor contact</label>
                <input value={coord.contact || ''} onChange={e => saveVendorCoord(vendorId, 'contact', e.target.value)} className={`mt-0.5 ${ic} !text-xs`} placeholder="e.g. Ahan (primary)" />
              </div>
              <div>
                <label className={lc}>Data transfer method</label>
                <select value={coord.transfer_method || ''} onChange={e => saveVendorCoord(vendorId, 'transfer_method', e.target.value)} className={`mt-0.5 ${ic} !text-xs`}>
                  <option value="">Select...</option>
                  <option value="sftp">SFTP</option>
                  <option value="keybase">Keybase</option>
                  <option value="email">Email (encrypted)</option>
                  <option value="api">Direct API</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            {/* Vendor feedback */}
            <div>
              <label className={lc}>Vendor notes / feedback</label>
              <textarea value={coord.vendor_notes || ''} onChange={e => saveVendorCoord(vendorId, 'vendor_notes', e.target.value)}
                rows={2} className={`mt-0.5 ${ic} !text-xs`} placeholder="e.g. GDC confirmed data is sufficient for optimal pass rate. Will process within 10 business days. No charge for this POC." />
            </div>

            {/* Internal processing form — shown when we process internally */}
            {coord.processing === 'f1_internal' && (
              <div className="pt-2 border-t border-[var(--border-color)] space-y-2">
                <h4 className="text-xs font-bold text-cyan-400">Internal Processing</h4>
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2">
                    <label className={lc}>Jira ticket</label>
                    <input value={coord.jira_url || ''} onChange={e => saveVendorCoord(vendorId, 'jira_url', e.target.value)}
                      className={`mt-0.5 ${ic} !text-xs`} placeholder="https://frankieone.atlassian.net/browse/..." />
                  </div>
                  <div className="col-span-2">
                    <label className={lc}>Notes</label>
                    <textarea value={coord.internal_notes || ''} onChange={e => saveVendorCoord(vendorId, 'internal_notes', e.target.value)}
                      rows={3} className={`mt-0.5 ${ic} !text-xs`} placeholder="Processing details, progress notes..." />
                  </div>
                </div>
              </div>
            )}

            {/* Vendor-specific intake forms — only needed when vendor processes the data */}
            {coord.processing && coord.processing !== 'vendor_processes' ? (
              <div className="pt-2 border-t border-[var(--border-color)]">
                <div className="flex items-center gap-2 px-3 py-2 rounded bg-[var(--bg-tertiary)] border border-[var(--border-color)]">
                  <Info size={14} className="text-cyan-400 shrink-0" />
                  <p className="text-[10px] text-[var(--text-secondary)]">
                    Processing method is set to <strong>{coord.processing === 'f1_internal' ? 'Internal processing' : coord.processing === 'f1_special_account' ? 'F1 special account' : coord.processing === 'customer_api' ? 'Customer API' : 'Tech Ops batch'}</strong> — vendor intake form not required.
                  </p>
                </div>
              </div>
            ) : (
            <>
            {/* Socure POC Intake Form */}
            {vendorId.toLowerCase().includes('socure') && (
              <div className="pt-2 border-t border-[var(--border-color)] space-y-2">
                <div className="flex items-center gap-2">
                  <h4 className="text-xs font-bold text-purple-400">FrankieOne POC Intake Form</h4>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400 border border-purple-500/30">Required by Socure</span>
                </div>

                {/* 1. POC Overview */}
                <div className="space-y-1.5">
                  <label className={lc}>1. POC Overview</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[9px] text-[var(--text-muted)]">Customer / Prospect</label>
                      <input value={coord.socure_customer || poc.company_name || ''} onChange={e => saveVendorCoord(vendorId, 'socure_customer', e.target.value)}
                        className={`mt-0.5 ${ic} !text-xs`} placeholder="e.g. OKX (Europe)" />
                    </div>
                    <div>
                      <label className="text-[9px] text-[var(--text-muted)]">Primary contact</label>
                      <input value={coord.socure_primary_contact || (disc.contact_name as string) || ''} onChange={e => saveVendorCoord(vendorId, 'socure_primary_contact', e.target.value)}
                        className={`mt-0.5 ${ic} !text-xs`} placeholder="e.g. John Smith, Head of Compliance" />
                    </div>
                    <div>
                      <label className="text-[9px] text-[var(--text-muted)]">Requested start date</label>
                      <input type="date" value={coord.socure_start_date || ''} onChange={e => saveVendorCoord(vendorId, 'socure_start_date', e.target.value)}
                        className={`mt-0.5 ${ic} !text-xs`} />
                    </div>
                    <div>
                      <label className="text-[9px] text-[var(--text-muted)]">Requested go-live date</label>
                      <input type="date" value={coord.socure_golive_date || ''} onChange={e => saveVendorCoord(vendorId, 'socure_golive_date', e.target.value)}
                        className={`mt-0.5 ${ic} !text-xs`} />
                    </div>
                    <div>
                      <label className="text-[9px] text-[var(--text-muted)]">Internal owner</label>
                      <input value={coord.socure_internal_owner || poc.presales_owner || ''} onChange={e => saveVendorCoord(vendorId, 'socure_internal_owner', e.target.value)}
                        className={`mt-0.5 ${ic} !text-xs`} placeholder="e.g. Lindsay Hatton" />
                    </div>
                    <div>
                      <label className="text-[9px] text-[var(--text-muted)]">Other stakeholders</label>
                      <input value={coord.socure_other_stakeholders || ''} onChange={e => saveVendorCoord(vendorId, 'socure_other_stakeholders', e.target.value)}
                        className={`mt-0.5 ${ic} !text-xs`} placeholder="e.g. Sales, Product, Legal..." />
                    </div>
                  </div>
                </div>

                {/* 2. POC Objective */}
                <div className="space-y-1.5">
                  <label className={lc}>2. POC Objective</label>
                  <p className="text-[9px] text-[var(--text-muted)]">Describe the purpose of the POC and what FrankieOne is being evaluated for.</p>
                  <div>
                    <label className="text-[9px] text-[var(--text-muted)]">Business objective</label>
                    <textarea value={coord.socure_business_objective || ''} onChange={e => saveVendorCoord(vendorId, 'socure_business_objective', e.target.value)}
                      rows={2} className={`mt-0.5 ${ic} !text-xs`} placeholder="e.g. Evaluate whether FrankieOne can improve pass rates vs current vendor..." />
                  </div>
                  <div>
                    <label className="text-[9px] text-[var(--text-muted)]">Use case</label>
                    <input value={coord.socure_use_case || ''} onChange={e => saveVendorCoord(vendorId, 'socure_use_case', e.target.value)}
                      className={`mt-0.5 ${ic} !text-xs`} placeholder="e.g. Customer onboarding KYC, Ongoing monitoring..." />
                  </div>
                  <div>
                    <label className="text-[9px] text-[var(--text-muted)]">Product / workflow in scope</label>
                    <input value={coord.socure_product_workflow || coord.socure_products || ''} onChange={e => saveVendorCoord(vendorId, 'socure_product_workflow', e.target.value)}
                      className={`mt-0.5 ${ic} !text-xs`} placeholder="e.g. KYC, AML Screening, ID+..." />
                  </div>
                  <div>
                    <label className="text-[9px] text-[var(--text-muted)]">Key decision this POC is intended to support</label>
                    <input value={coord.socure_key_decision || ''} onChange={e => saveVendorCoord(vendorId, 'socure_key_decision', e.target.value)}
                      className={`mt-0.5 ${ic} !text-xs`} placeholder="e.g. Vendor selection for EU markets, replace Sumsub..." />
                  </div>
                </div>

                {/* 3. Projected Volumes */}
                <div className="space-y-1.5">
                  <label className={lc}>3. Projected Volumes</label>
                  <p className="text-[9px] text-[var(--text-muted)]">Expected volumes for the POC and expected production volumes if successful.</p>
                  <div className="rounded border border-[var(--border-color)] overflow-hidden">
                    <table className="w-full text-[10px]">
                      <thead><tr className="bg-[var(--bg-tertiary)]">
                        <th className="text-left px-2 py-1.5 font-semibold text-[var(--text-muted)]">Volume Type</th>
                        <th className="text-left px-2 py-1.5 font-semibold text-[var(--text-muted)] w-28">Estimated Count</th>
                        <th className="text-left px-2 py-1.5 font-semibold text-[var(--text-muted)]">Notes</th>
                      </tr></thead>
                      <tbody>
                        {[
                          { key: 'socure_volume', label: 'Records / applications in POC', defaultVal: (() => { const total = vendorCtries.reduce((sum, code) => { const cc = ((disc.country_config || {})[code]) || {}; return sum + (parseInt(cc.records) || 0) }, 0); return total > 0 ? String(total) : String(poc.record_count || '') })() },
                          { key: 'socure_volume_daily', label: 'Daily volume', defaultVal: '' },
                          { key: 'socure_volume_weekly', label: 'Weekly volume', defaultVal: '' },
                          { key: 'socure_volume_monthly', label: 'Monthly volume', defaultVal: '' },
                          { key: 'socure_volume_production', label: 'Expected production monthly volume', defaultVal: '' },
                        ].map(row => (
                          <tr key={row.key} className="border-t border-[var(--border-color)]">
                            <td className="px-2 py-1 text-[var(--text-secondary)]">{row.label}</td>
                            <td className="px-2 py-1"><input value={coord[row.key] || row.defaultVal} onChange={e => saveVendorCoord(vendorId, row.key, e.target.value)} className={`w-full ${ic} !text-[10px] !py-0.5`} placeholder="—" /></td>
                            <td className="px-2 py-1"><input value={coord[row.key + '_notes'] || ''} onChange={e => saveVendorCoord(vendorId, row.key + '_notes', e.target.value)} className={`w-full ${ic} !text-[10px] !py-0.5`} placeholder="Notes..." /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 4. Countries — auto-populated from POC countries */}
                <div>
                  <label className={lc}>4. Countries in Scope</label>
                  <div className="mt-0.5 text-[10px] text-[var(--text-secondary)]">
                    {vendorCtries.length > 0
                      ? vendorCtries.map(c => COUNTRIES.find(x => x.code === c)?.name || c).join(', ')
                      : <span className="text-[var(--text-muted)]">Add countries on the Request Form tab</span>}
                  </div>
                </div>

                {/* 5. Success Criteria */}
                <div className="space-y-1.5">
                  <label className={lc}>5. Success Criteria</label>
                  <p className="text-[9px] text-[var(--text-muted)]">Success criteria must be clearly defined before kickoff.</p>
                  <div className="rounded border border-[var(--border-color)] overflow-hidden">
                    <table className="w-full text-[10px]">
                      <thead><tr className="bg-[var(--bg-tertiary)]">
                        <th className="text-left px-2 py-1.5 font-semibold text-[var(--text-muted)]">Metric</th>
                        <th className="text-left px-2 py-1.5 font-semibold text-[var(--text-muted)] w-28">Target</th>
                        <th className="text-left px-2 py-1.5 font-semibold text-[var(--text-muted)]">Notes</th>
                      </tr></thead>
                      <tbody>
                        {[
                          { key: 'socure_target_pass_rate', label: 'Pass rate', placeholder: 'e.g. >70%', defaultVal: (() => { const targets = vendorCtries.map(code => { const cc = ((disc.country_config || {})[code]) || {}; const name = COUNTRIES.find(x => x.code === code)?.name || code; return cc.target_rate ? `${name}: ≥${cc.target_rate}%` : null }).filter(Boolean).join(', '); return targets || '' })() },
                          { key: 'socure_target_match_rate', label: 'Match / approval rate', placeholder: '', defaultVal: '' },
                          { key: 'socure_target_manual_review', label: 'Manual review rate', placeholder: '', defaultVal: '' },
                          { key: 'socure_target_false_positive', label: 'False positive tolerance', placeholder: '', defaultVal: '' },
                          { key: 'socure_target_turnaround', label: 'Turnaround / response time', placeholder: '', defaultVal: '' },
                        ].map(row => (
                          <tr key={row.key} className="border-t border-[var(--border-color)]">
                            <td className="px-2 py-1 text-[var(--text-secondary)]">{row.label}</td>
                            <td className="px-2 py-1"><input value={coord[row.key] || row.defaultVal} onChange={e => saveVendorCoord(vendorId, row.key, e.target.value)} className={`w-full ${ic} !text-[10px] !py-0.5`} placeholder={row.placeholder || '—'} /></td>
                            <td className="px-2 py-1"><input value={coord[row.key + '_notes'] || ''} onChange={e => saveVendorCoord(vendorId, row.key + '_notes', e.target.value)} className={`w-full ${ic} !text-[10px] !py-0.5`} placeholder="Notes..." /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div>
                    <label className="text-[9px] text-[var(--text-muted)]">What outcome would be considered a successful POC?</label>
                    <textarea value={coord.socure_success_outcome || coord.socure_success_criteria || ''} onChange={e => saveVendorCoord(vendorId, 'socure_success_outcome', e.target.value)}
                      rows={2} className={`mt-0.5 ${ic} !text-xs`} placeholder="e.g. Pass rate exceeds 70% across all markets..." />
                  </div>
                  <div>
                    <label className="text-[9px] text-[var(--text-muted)]">What outcome would be considered unsuccessful?</label>
                    <textarea value={coord.socure_unsuccessful_outcome || ''} onChange={e => saveVendorCoord(vendorId, 'socure_unsuccessful_outcome', e.target.value)}
                      rows={2} className={`mt-0.5 ${ic} !text-xs`} placeholder="e.g. Pass rate below current vendor, excessive manual review..." />
                  </div>
                </div>

                {/* 6. Data and PII Included */}
                <div className="space-y-1.5">
                  <label className={lc}>6. Data and PII Included</label>
                  <p className="text-[9px] text-[var(--text-muted)]">Confirm exactly what data elements will be provided as part of the POC.</p>
                  <div className="rounded border border-[var(--border-color)] overflow-hidden">
                    <table className="w-full text-[10px]">
                      <thead><tr className="bg-[var(--bg-tertiary)]">
                        <th className="text-left px-2 py-1.5 font-semibold text-[var(--text-muted)]">Data Element</th>
                        <th className="text-center px-2 py-1.5 font-semibold text-[var(--text-muted)] w-20">Included</th>
                        <th className="text-left px-2 py-1.5 font-semibold text-[var(--text-muted)]">Notes</th>
                      </tr></thead>
                      <tbody>
                        {[
                          { key: 'full_name', label: 'Full name', legacyPii: ['First Name', 'Sur Name'] },
                          { key: 'date_of_birth', label: 'Date of birth', legacyPii: ['Date of Birth'] },
                          { key: 'address', label: 'Address', legacyPii: ['Physical Address Line 1', 'Physical Address Line 2', 'City', 'State', 'Zip'] },
                          { key: 'email', label: 'Email', legacyPii: ['Email'] },
                          { key: 'phone_number', label: 'Phone number', legacyPii: ['Phone Number'] },
                          { key: 'government_id___document_data', label: 'Government ID / document data', legacyPii: ['National ID'] },
                          { key: 'ip_address___device_data', label: 'IP address / device data', legacyPii: [] },
                          { key: 'other', label: 'Other', legacyPii: [] },
                        ].map(row => {
                          const fieldKey = `socure_pii_${row.key}`
                          const legacyPii = (coord.socure_pii || []) as string[]
                          const legacyMatch = row.legacyPii.some(p => legacyPii.includes(p))
                          const val = coord[fieldKey] || (legacyMatch ? 'Yes' : '')
                          return (
                            <tr key={row.key} className="border-t border-[var(--border-color)]">
                              <td className="px-2 py-1 text-[var(--text-secondary)]">{row.label}</td>
                              <td className="px-2 py-1 text-center">
                                <select value={val} onChange={e => saveVendorCoord(vendorId, fieldKey, e.target.value)} className={`${ic} !text-[10px] !py-0.5 w-16 text-center`}>
                                  <option value="">—</option>
                                  <option value="Yes">Yes</option>
                                  <option value="No">No</option>
                                </select>
                              </td>
                              <td className="px-2 py-1"><input value={coord[fieldKey + '_notes'] || ''} onChange={e => saveVendorCoord(vendorId, fieldKey + '_notes', e.target.value)} className={`w-full ${ic} !text-[10px] !py-0.5`} placeholder="Notes..." /></td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 7. Funnel Placement */}
                <div className="space-y-1.5">
                  <label className={lc}>7. Funnel Placement</label>
                  <p className="text-[9px] text-[var(--text-muted)]">Confirm where this data sits in the customer journey.</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[9px] text-[var(--text-muted)]">Is the data top of funnel?</label>
                      <select value={coord.socure_funnel_top_of_funnel || ''} onChange={e => saveVendorCoord(vendorId, 'socure_funnel_top_of_funnel', e.target.value)} className={`mt-0.5 ${ic} !text-xs`}>
                        <option value="">Select...</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[9px] text-[var(--text-muted)]">If no, at what stage is the user entering the flow?</label>
                      <input value={coord.socure_funnel_stage || ''} onChange={e => saveVendorCoord(vendorId, 'socure_funnel_stage', e.target.value)}
                        className={`mt-0.5 ${ic} !text-xs`} placeholder="e.g. Post initial screening, step-up verification..." />
                    </div>
                    <div>
                      <label className="text-[9px] text-[var(--text-muted)]">New applicants, existing customers, or both?</label>
                      <select value={coord.socure_funnel_applicant_type || ''} onChange={e => saveVendorCoord(vendorId, 'socure_funnel_applicant_type', e.target.value)} className={`mt-0.5 ${ic} !text-xs`}>
                        <option value="">Select...</option>
                        <option value="New applicants">New applicants</option>
                        <option value="Existing customers">Existing customers</option>
                        <option value="Both">Both</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[9px] text-[var(--text-muted)]">Any pre-screening already performed?</label>
                      <input value={coord.socure_funnel_prescreening || ''} onChange={e => saveVendorCoord(vendorId, 'socure_funnel_prescreening', e.target.value)}
                        className={`mt-0.5 ${ic} !text-xs`} placeholder="e.g. Sumsub KYC, manual review..." />
                    </div>
                  </div>
                </div>

                {/* 8. KYC Scorecard Requirements */}
                <div className="space-y-1.5">
                  <label className={lc}>8. KYC Scorecard Requirements</label>
                  <p className="text-[9px] text-[var(--text-muted)]">Provide the KYC scorecard that will be used for evaluation.</p>
                  <div className="rounded border border-[var(--border-color)] overflow-hidden">
                    <table className="w-full text-[10px]">
                      <thead><tr className="bg-[var(--bg-tertiary)]">
                        <th className="text-left px-2 py-1.5 font-semibold text-[var(--text-muted)]">Requirement</th>
                        <th className="text-left px-2 py-1.5 font-semibold text-[var(--text-muted)] w-28">Response</th>
                        <th className="text-left px-2 py-1.5 font-semibold text-[var(--text-muted)]">Notes</th>
                      </tr></thead>
                      <tbody>
                        {[
                          { key: 'socure_scorecard_type', label: '1x1 or 2x2' },
                          { key: 'socure_scorecard_bands', label: 'Score bands / tiers defined' },
                          { key: 'socure_scorecard_passfail', label: 'Pass / fail threshold defined' },
                          { key: 'socure_scorecard_manual_review', label: 'Manual review threshold defined' },
                          { key: 'socure_scorecard_overrides', label: 'Exceptions / overrides documented' },
                        ].map(row => (
                          <tr key={row.key} className="border-t border-[var(--border-color)]">
                            <td className="px-2 py-1 text-[var(--text-secondary)]">{row.label}</td>
                            <td className="px-2 py-1"><input value={coord[row.key] || ''} onChange={e => saveVendorCoord(vendorId, row.key, e.target.value)} className={`w-full ${ic} !text-[10px] !py-0.5`} placeholder="—" /></td>
                            <td className="px-2 py-1"><input value={coord[row.key + '_notes'] || ''} onChange={e => saveVendorCoord(vendorId, row.key + '_notes', e.target.value)} className={`w-full ${ic} !text-[10px] !py-0.5`} placeholder="Notes..." /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 9. Additional Dependencies */}
                <div className="space-y-1.5">
                  <label className={lc}>9. Additional Dependencies or Assumptions</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[9px] text-[var(--text-muted)]">Integration dependencies</label>
                      <input value={coord.socure_dep_integration || ''} onChange={e => saveVendorCoord(vendorId, 'socure_dep_integration', e.target.value)}
                        className={`mt-0.5 ${ic} !text-xs`} placeholder="e.g. API, batch file, SDK..." />
                    </div>
                    <div>
                      <label className="text-[9px] text-[var(--text-muted)]">Data delivery format</label>
                      <input value={coord.socure_dep_data_format || ''} onChange={e => saveVendorCoord(vendorId, 'socure_dep_data_format', e.target.value)}
                        className={`mt-0.5 ${ic} !text-xs`} placeholder="e.g. CSV, JSON, SFTP..." />
                    </div>
                    <div>
                      <label className="text-[9px] text-[var(--text-muted)]">Testing environment needs</label>
                      <input value={coord.socure_dep_testing_env || ''} onChange={e => saveVendorCoord(vendorId, 'socure_dep_testing_env', e.target.value)}
                        className={`mt-0.5 ${ic} !text-xs`} placeholder="e.g. Sandbox, staging environment..." />
                    </div>
                    <div>
                      <label className="text-[9px] text-[var(--text-muted)]">Compliance / legal considerations</label>
                      <input value={coord.socure_dep_compliance || ''} onChange={e => saveVendorCoord(vendorId, 'socure_dep_compliance', e.target.value)}
                        className={`mt-0.5 ${ic} !text-xs`} placeholder="e.g. GDPR, data residency..." />
                    </div>
                  </div>
                  <div>
                    <label className="text-[9px] text-[var(--text-muted)]">Other assumptions</label>
                    <textarea value={coord.socure_dep_other || ''} onChange={e => saveVendorCoord(vendorId, 'socure_dep_other', e.target.value)}
                      rows={2} className={`mt-0.5 ${ic} !text-xs`} placeholder="Any other dependencies or assumptions..." />
                  </div>
                </div>

              </div>
            )}

            {/* Datazoo POC Intake Form */}
            {(vendorId.toLowerCase().includes('datazoo') || vendorId.toLowerCase() === 'dz') && (
              <div className="pt-2 border-t border-[var(--border-color)] space-y-2">
                <div className="flex items-center gap-2">
                  <h4 className="text-xs font-bold text-emerald-400">Datazoo POC Information Requirements</h4>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">Required by Datazoo</span>
                </div>

                {/* 1. End Customer, Use Case & POC Objective */}
                <div>
                  <label className={lc}>1. End Customer, Use Case & POC Objective</label>
                  <div className="grid grid-cols-2 gap-2 mt-0.5">
                    <div>
                      <span className="text-[9px] text-[var(--text-muted)]">End customer</span>
                      <input value={coord.dz_customer || poc.company_name || ''} onChange={e => saveVendorCoord(vendorId, 'dz_customer', e.target.value)} className={`${ic} !text-xs`} />
                    </div>
                    <div>
                      <span className="text-[9px] text-[var(--text-muted)]">POC due date</span>
                      <input type="date" value={coord.dz_due_date || ''} onChange={e => saveVendorCoord(vendorId, 'dz_due_date', e.target.value)} className={`${ic} !text-xs`} />
                    </div>
                  </div>
                  <div className="mt-1">
                    <span className="text-[9px] text-[var(--text-muted)]">How verification is used</span>
                    <div className="flex flex-wrap gap-1.5 mt-0.5">
                      {['Onboarding', 'Remediation', 'Ongoing Monitoring', 'Other'].map(u => {
                        const sel = ((coord.dz_use_case || []) as string[]).includes(u)
                        return <button key={u} type="button" onClick={() => {
                          const cur = (coord.dz_use_case || []) as string[]
                          saveVendorCoord(vendorId, 'dz_use_case', sel ? cur.filter((x: string) => x !== u) : [...cur, u])
                        }} className={`px-2 py-0.5 rounded text-[10px] border cursor-pointer ${sel ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' : 'text-[var(--text-muted)] border-[var(--border-color)]'}`}>{sel && '✓ '}{u}</button>
                      })}
                    </div>
                  </div>
                  <div className="mt-1">
                    <span className="text-[9px] text-[var(--text-muted)]">POC objective</span>
                    <div className="flex flex-wrap gap-1.5 mt-0.5">
                      {['Batch remediation', 'Pre-go-live performance assessment'].map(o => {
                        const sel = coord.dz_objective === o
                        return <button key={o} type="button" onClick={() => saveVendorCoord(vendorId, 'dz_objective', sel ? '' : o)}
                          className={`px-2 py-0.5 rounded text-[10px] border cursor-pointer ${sel ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' : 'text-[var(--text-muted)] border-[var(--border-color)]'}`}>{sel && '✓ '}{o}</button>
                      })}
                    </div>
                  </div>
                </div>

                {/* 2. Volumes & Geographic Scope */}
                <div>
                  <label className={lc}>2. Volumes & Geographic Scope</label>
                  <div className="grid grid-cols-2 gap-2 mt-0.5">
                    <div>
                      <span className="text-[9px] text-[var(--text-muted)]">Total records</span>
                      <input value={coord.dz_total_records || (() => vendorCtries.reduce((s, c) => s + (parseInt(((disc.country_config || {})[c] || {}).records) || 0), 0) || '')()}
                        onChange={e => saveVendorCoord(vendorId, 'dz_total_records', e.target.value)} className={`${ic} !text-xs`} />
                    </div>
                    <div>
                      <span className="text-[9px] text-[var(--text-muted)]">Countries</span>
                      <input value={coord.dz_countries || vendorCtries.map(c => COUNTRIES.find(x => x.code === c)?.name || c).join(', ')}
                        onChange={e => saveVendorCoord(vendorId, 'dz_countries', e.target.value)} className={`${ic} !text-xs`} />
                    </div>
                  </div>
                  <div className="mt-1">
                    <span className="text-[9px] text-[var(--text-muted)]">Volume per country</span>
                    <textarea value={coord.dz_volume_breakdown || (() => vendorCtries.map(c => {
                      const cc = ((disc.country_config || {})[c]) || {}
                      const name = COUNTRIES.find(x => x.code === c)?.name || c
                      return cc.records ? `${name}: ${cc.records}` : `${name}: TBD`
                    }).join('\n') || '')()}
                      onChange={e => saveVendorCoord(vendorId, 'dz_volume_breakdown', e.target.value)} rows={3} className={`mt-0.5 ${ic} !text-xs`} />
                  </div>
                </div>

                {/* 3. Data History & Match Requirements */}
                <div>
                  <label className={lc}>3. Data History & Match Requirements</label>
                  <div className="mt-0.5">
                    <span className="text-[9px] text-[var(--text-muted)]">Previously verified?</span>
                    <div className="flex gap-1.5 mt-0.5">
                      {['First time verification', 'Previously verified', 'Mix of both'].map(o => {
                        const sel = coord.dz_data_history === o
                        return <button key={o} type="button" onClick={() => saveVendorCoord(vendorId, 'dz_data_history', sel ? '' : o)}
                          className={`px-2 py-0.5 rounded text-[10px] border cursor-pointer ${sel ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' : 'text-[var(--text-muted)] border-[var(--border-color)]'}`}>{sel && '✓ '}{o}</button>
                      })}
                    </div>
                  </div>
                  <div className="mt-1">
                    <span className="text-[9px] text-[var(--text-muted)]">Data elements for matching</span>
                    <input value={coord.dz_match_elements || ''} onChange={e => saveVendorCoord(vendorId, 'dz_match_elements', e.target.value)}
                      className={`mt-0.5 ${ic} !text-xs`} placeholder="e.g. Name + Address, Name + DOB" />
                  </div>
                  <div className="mt-1">
                    <span className="text-[9px] text-[var(--text-muted)]">Matching approach</span>
                    <div className="flex gap-1.5 mt-0.5">
                      {['1x1', '2x2', 'Other'].map(o => {
                        const sel = coord.dz_match_approach === o
                        return <button key={o} type="button" onClick={() => saveVendorCoord(vendorId, 'dz_match_approach', sel ? '' : o)}
                          className={`px-2 py-0.5 rounded text-[10px] border cursor-pointer ${sel ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' : 'text-[var(--text-muted)] border-[var(--border-color)]'}`}>{sel && '✓ '}{o}</button>
                      })}
                    </div>
                  </div>
                </div>

                {/* 4. Success Criteria & Expected Outcomes */}
                <div>
                  <label className={lc}>4. Success Criteria & Expected Outcomes</label>
                  <textarea value={coord.dz_success_criteria || (() => {
                    const targets = vendorCtries.map(code => {
                      const cc = ((disc.country_config || {})[code]) || {}
                      const name = COUNTRIES.find(x => x.code === code)?.name || code
                      return cc.target_rate ? `${name}: target ≥ ${cc.target_rate}%` : null
                    }).filter(Boolean).join('\n')
                    return targets || ''
                  })()}
                    onChange={e => saveVendorCoord(vendorId, 'dz_success_criteria', e.target.value)}
                    rows={2} className={`mt-0.5 ${ic} !text-xs`} placeholder="What defines success? How will results be reviewed? Next steps?" />
                  <div className="mt-1">
                    <span className="text-[9px] text-[var(--text-muted)]">Next steps the POC supports</span>
                    <div className="flex gap-1.5 mt-0.5">
                      {['Remediation activity', 'Production implementation', 'Other'].map(o => {
                        const sel = coord.dz_next_steps === o
                        return <button key={o} type="button" onClick={() => saveVendorCoord(vendorId, 'dz_next_steps', sel ? '' : o)}
                          className={`px-2 py-0.5 rounded text-[10px] border cursor-pointer ${sel ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' : 'text-[var(--text-muted)] border-[var(--border-color)]'}`}>{sel && '✓ '}{o}</button>
                      })}
                    </div>
                  </div>
                </div>

                {/* 5. Known Data Quality Characteristics */}
                <div>
                  <label className={lc}>5. Known Data Quality Characteristics</label>
                  <div className="flex flex-wrap gap-1.5 mt-0.5">
                    {['Missing fields', 'Dated information', 'Inconsistent formatting', 'Aliases/nicknames', 'Recent movers', 'International address formats', 'PO Boxes', 'Multiple languages/scripts', 'Transliteration'].map(q => {
                      const sel = ((coord.dz_data_quality || []) as string[]).includes(q)
                      return <button key={q} type="button" onClick={() => {
                        const cur = (coord.dz_data_quality || []) as string[]
                        saveVendorCoord(vendorId, 'dz_data_quality', sel ? cur.filter((x: string) => x !== q) : [...cur, q])
                      }} className={`px-2 py-0.5 rounded text-[10px] border cursor-pointer ${sel ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' : 'text-[var(--text-muted)] border-[var(--border-color)]'}`}>{sel && '✓ '}{q}</button>
                    })}
                  </div>
                  <textarea value={coord.dz_data_quality_notes || ''} onChange={e => saveVendorCoord(vendorId, 'dz_data_quality_notes', e.target.value)}
                    rows={2} className={`mt-1 ${ic} !text-xs`} placeholder="Any additional data quality notes..." />
                </div>
              </div>
            )}
            </>
            )}

            {/* Draft Message */}
            <div className="pt-2 border-t border-[var(--border-color)]">
              <div className="flex items-center gap-2 mb-2">
                <label className={lc}>Draft Vendor Message</label>
                <button type="button" disabled={drafting === vendorId} onClick={() => draftMessage(vendorId)}
                  className="flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-medium bg-purple-500/10 text-purple-400 border border-purple-500/30 hover:bg-purple-500/20 cursor-pointer disabled:opacity-50 transition-colors">
                  {drafting === vendorId ? <><Loader2 size={12} className="animate-spin" /> Drafting...</> : '✨ Generate with AI'}
                </button>
                {vendorId === 'socure' && (
                  <button type="button" onClick={() => exportSocureIntakePDF(poc)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-medium bg-orange-500/10 text-orange-400 border border-orange-500/30 hover:bg-orange-500/20 cursor-pointer transition-colors">
                    📄 Export POC Intake Form
                  </button>
                )}
                {Object.keys(sampleFields).length === 0 && (
                  <span className="text-[9px] text-amber-400">Upload sample data above for better field-specific messaging</span>
                )}
              </div>
              {drafts[vendorId] && (
                <div className="space-y-2">
                  <textarea value={drafts[vendorId]} onChange={e => {
                    const val = e.target.value
                    setDrafts(prev => ({ ...prev, [vendorId]: val }))
                    saveVendorCoord(vendorId, 'draft_message', val)
                  }}
                    rows={12} className={`${ic} !text-xs font-mono`} />
                  <div className="flex gap-2">
                    <button type="button" onClick={() => copyToClipboard(vendorId)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-medium bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] cursor-pointer transition-colors">
                      {copied === vendorId ? '✓ Copied!' : '📋 Copy to Clipboard'}
                    </button>
                    <button type="button" onClick={() => draftMessage(vendorId)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-pointer transition-colors">
                      ↻ Regenerate
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Confirmation toggles */}
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => saveVendorCoord(vendorId, 'data_optimal', !coord.data_optimal)}
                className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium border cursor-pointer ${coord.data_optimal ? 'bg-green-500/10 text-green-400 border-green-500/30' : 'text-[var(--text-muted)] border-[var(--border-color)]'}`}>
                {coord.data_optimal ? '✓ Data confirmed optimal' : 'Data optimality not confirmed'}
              </button>
              <button type="button" onClick={() => saveVendorCoord(vendorId, 'no_charge_confirmed', !coord.no_charge_confirmed)}
                className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium border cursor-pointer ${coord.no_charge_confirmed ? 'bg-green-500/10 text-green-400 border-green-500/30' : 'text-[var(--text-muted)] border-[var(--border-color)]'}`}>
                {coord.no_charge_confirmed ? '✓ No charge confirmed' : 'Charge terms not confirmed'}
              </button>
              <button type="button" onClick={() => saveVendorCoord(vendorId, 'customer_price_confirmed', !coord.customer_price_confirmed)}
                className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium border cursor-pointer ${coord.customer_price_confirmed ? 'bg-green-500/10 text-green-400 border-green-500/30' : 'text-[var(--text-muted)] border-[var(--border-color)]'}`}>
                {coord.customer_price_confirmed ? '✓ Customer confirmed pricing' : 'Customer pricing not confirmed'}
              </button>
            </div>
            </>}
          </Card>
        )
      })}
    </div>
  )
}

// ─── POC Client Guide ────────────────────────────────────────────────────
const POC_GUIDE_CONTENT = `Proof of Concept (POC) — What to Expect

FrankieOne runs POCs to demonstrate identity verification match rates across multiple vendors and countries. Here's how the process works.


1. Secure Data Transfer

The first step is setting up a secure method to transfer the data files that will be used in the POC. There are a few options:

  - SFTP — FrankieOne provides a secure SFTP server. You'll need to send us an SSH public key and your external IP address so we can set up access for you.
  - Cloud storage — You set up a secure bucket on your side and grant FrankieOne access.
  - Direct file transfer — You send data files directly as CSVs via a secure channel (e.g. encrypted email).

Whichever method works best for you — we'll coordinate to make sure the transfer is secure and straightforward.

If using SFTP, please send FrankieOne:
  - Your SSH public key (typically found at ~/.ssh/id_rsa.pub or C:\Users\YourUsername\.ssh\id_rsa.pub)
  - Your external IP address (the IP you'll be connecting from)

If you don't already have an SSH key pair, your IT team can generate one for you. Once we receive these details, we'll set up your access and provide you with the SFTP server address and username.


2. Data Validation

Please ensure that the data you provide is an accurate representation of the data you have obtained from your customers. The quality and representativeness of the sample directly impacts the usefulness of the POC results. If the data is not representative of your real customer base, the match rates we see in the POC may not reflect what you'd see in production.


3. Define the Terms

Send FrankieOne information about what you'd like to test during the POC:

  - What are your ideal match rates?
  - Which countries do you need to verify customers in?
  - What should the match criteria be? For example:
      - Name and Date of Birth
      - Name and Address
      - A combination of both
  - Are there any other criteria that would be required to consider a result a genuine match?

FrankieOne will confirm these details back to you via email. Please review and confirm that email so we're aligned before we proceed.


4. Timelines

Generally speaking, POCs take at least two weeks from the point we receive your data and confirmation. We typically approach multiple vendors simultaneously, and coordinating across all of them takes time. We appreciate your patience during this process.


5. Vendor Coordination & Follow-ups

During the POC, we may hear back from vendors with questions about the data or the requirements. If that happens, we may send you additional requests for clarification on their behalf. Quick responses help keep things on track.


6. Results & Presentation

Once all of the data comes back from the vendors, FrankieOne will process and analyse it. This includes:

  - Calculating pass rates for each vendor individually.
  - Simulating cascades — if you're looking at hitting multiple vendors through FrankieOne, we'll project combined pass rates based on each vendor's individual results.

After processing, we'll create a presentation summarising our findings and book a meeting to walk you through the results and recommendations.


Frequently Asked Questions — SFTP

Q: Is the SFTP endpoint shared or dedicated to my organisation?
The SFTP endpoint (sftp.frankie.one for production, sftp.uat.frankie.one for UAT, port 22) is shared infrastructure. However, each customer is logically isolated through:
  - A unique username per customer
  - A dedicated S3 bucket per customer
  - IP whitelisting — security groups restrict inbound access to only your declared egress CIDRs
  - Per-customer SSH keys — only your registered public key can authenticate your user
So while the hostname is shared, access is fully scoped: you can only reach your own bucket and cannot see other customers' data.

Q: What is the SSH key management policy?
You generate your own SSH keypair and share only the public key with FrankieOne. Key rotation is on-demand — there is no mandatory rotation cadence, but keys should be rotated immediately if a compromise is suspected.

Q: What is the exact scope of the IP whitelist?
The IP whitelist is per-customer and per-direction. It controls which source IPs are permitted to connect inbound to FrankieOne's SFTP server on port 22. Whitelisting is applied separately for both UAT and production environments. You will need to provide FrankieOne with your egress IP addresses (or CIDR ranges) so we can configure access.

Q: Where can I find FrankieOne's encryption policy?
You can find our encryption policy on the FrankieOne Trust Center.


Frequently Asked Questions — Privacy & Data

Q: What is FrankieOne's designated privacy/data protection email address (e.g. for DPA purposes)?
Privacy inquiries and DPA-related correspondence should be directed to: privacy@frankieone.com

Q: Can FrankieOne provide a written deletion attestation confirming that all transferred data will be deleted after the evaluation is complete?
Yes. FrankieOne's Implementations Team will provide written confirmation that all POC data has been deleted once the evaluation is complete. This is managed by the Implementations Team as part of the standard POC wind-down process.
`

export function PocClientGuide({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [copied, setCopied] = useState(false)

  const copyToClipboard = () => {
    navigator.clipboard.writeText(POC_GUIDE_CONTENT).then(() => {
      setCopied(true)
      showToast('Guide copied to clipboard', 'success')
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const exportPdf = async () => {
    const { jsPDF } = await import('jspdf')
    const doc = new jsPDF({ unit: 'mm', format: 'a4' })
    const pageWidth = doc.internal.pageSize.getWidth()
    const margin = 20
    const usable = pageWidth - margin * 2
    let y = 25

    const addWrapped = (text: string, fontSize: number, opts?: { bold?: boolean; color?: [number, number, number]; spacing?: number }) => {
      doc.setFontSize(fontSize)
      doc.setFont('helvetica', opts?.bold ? 'bold' : 'normal')
      if (opts?.color) doc.setTextColor(...opts.color); else doc.setTextColor(40, 40, 40)
      const lines = doc.splitTextToSize(text, usable)
      const lineHeight = fontSize * 0.45
      for (const line of lines) {
        if (y + lineHeight > 280) { doc.addPage(); y = 20 }
        doc.text(line, margin, y)
        y += lineHeight
      }
      y += (opts?.spacing ?? 2)
    }

    // Title
    addWrapped('Proof of Concept (POC)', 18, { bold: true, color: [0, 120, 180], spacing: 2 })
    addWrapped('What to Expect', 14, { bold: true, color: [80, 80, 80], spacing: 6 })

    addWrapped('FrankieOne runs POCs to demonstrate identity verification match rates across multiple vendors and countries. Here\'s how the process works.', 10, { spacing: 8 })

    const sections: { title: string; body: string }[] = [
      { title: '1. Secure Data Transfer', body: 'The first step is setting up a secure method to transfer the data files that will be used in the POC. There are a few options:\n\n\u2022  SFTP \u2014 FrankieOne provides a secure SFTP server. You\'ll need to send us an SSH public key and your external IP address so we can set up access for you.\n\u2022  Cloud storage \u2014 You set up a secure bucket on your side and grant FrankieOne access.\n\u2022  Direct file transfer \u2014 You send data files directly as CSVs via a secure channel (e.g. encrypted email).\n\nWhichever method works best for you \u2014 we\'ll coordinate to make sure the transfer is secure and straightforward.\n\nIf using SFTP, please send FrankieOne:\n\u2022  Your SSH public key (typically found at ~/.ssh/id_rsa.pub)\n\u2022  Your external IP address (the IP you\'ll be connecting from)\n\nIf you don\'t already have an SSH key pair, your IT team can generate one for you. Once we receive these details, we\'ll set up your access and provide you with the SFTP server address and username.' },
      { title: '2. Data Validation', body: 'Please ensure that the data you provide is an accurate representation of the data you have obtained from your customers. The quality and representativeness of the sample directly impacts the usefulness of the POC results. If the data is not representative of your real customer base, the match rates we see in the POC may not reflect what you\'d see in production.' },
      { title: '3. Define the Terms', body: 'Send FrankieOne information about what you\'d like to test during the POC:\n\n\u2022  What are your ideal match rates?\n\u2022  Which countries do you need to verify customers in?\n\u2022  What should the match criteria be? (e.g. Name + DOB, Name + Address, or both)\n\u2022  Are there any other criteria required to consider a result a genuine match?\n\nFrankieOne will confirm these details back to you via email. Please review and confirm that email so we\'re aligned before we proceed.' },
      { title: '4. Timelines', body: 'Generally speaking, POCs take at least two weeks from the point we receive your data and confirmation. We typically approach multiple vendors simultaneously, and coordinating across all of them takes time. We appreciate your patience during this process.' },
      { title: '5. Vendor Coordination & Follow-ups', body: 'During the POC, we may hear back from vendors with questions about the data or the requirements. If that happens, we may send you additional requests for clarification on their behalf. Quick responses help keep things on track.' },
      { title: '6. Results & Presentation', body: 'Once all of the data comes back from the vendors, FrankieOne will process and analyse it. This includes:\n\n\u2022  Calculating pass rates for each vendor individually.\n\u2022  Simulating cascades \u2014 if you\'re looking at hitting multiple vendors through FrankieOne, we\'ll project combined pass rates based on each vendor\'s individual results.\n\nAfter processing, we\'ll create a presentation summarising our findings and book a meeting to walk you through the results and recommendations.' },
      { title: 'Frequently Asked Questions \u2014 SFTP', body: 'Q: Is the SFTP endpoint shared or dedicated to my organisation?\nThe SFTP endpoint (sftp.frankie.one for production, sftp.uat.frankie.one for UAT, port 22) is shared infrastructure. However, each customer is logically isolated through:\n\u2022  A unique username per customer\n\u2022  A dedicated S3 bucket per customer\n\u2022  IP whitelisting \u2014 security groups restrict inbound access to only your declared egress CIDRs\n\u2022  Per-customer SSH keys \u2014 only your registered public key can authenticate your user\nSo while the hostname is shared, access is fully scoped: you can only reach your own bucket and cannot see other customers\' data.\n\nQ: What is the SSH key management policy?\nYou generate your own SSH keypair and share only the public key with FrankieOne. Key rotation is on-demand \u2014 there is no mandatory rotation cadence, but keys should be rotated immediately if a compromise is suspected.\n\nQ: What is the exact scope of the IP whitelist?\nThe IP whitelist is per-customer and per-direction. It controls which source IPs are permitted to connect inbound to FrankieOne\'s SFTP server on port 22. Whitelisting is applied separately for both UAT and production environments. You will need to provide FrankieOne with your egress IP addresses (or CIDR ranges) so we can configure access.\n\nQ: Where can I find FrankieOne\'s encryption policy?\nYou can find our encryption policy on the FrankieOne Trust Center.' },
      { title: 'Frequently Asked Questions \u2014 Privacy & Data', body: 'Q: What is FrankieOne\'s designated privacy/data protection email address (e.g. for DPA purposes)?\nPrivacy inquiries and DPA-related correspondence should be directed to: privacy@frankieone.com\n\nQ: Can FrankieOne provide a written deletion attestation confirming that all transferred data will be deleted after the evaluation is complete?\nYes. FrankieOne\'s Implementations Team will provide written confirmation that all POC data has been deleted once the evaluation is complete. This is managed by the Implementations Team as part of the standard POC wind-down process.' },
    ]

    for (const s of sections) {
      addWrapped(s.title, 12, { bold: true, color: [0, 120, 180], spacing: 3 })
      addWrapped(s.body, 10, { spacing: 8 })
    }

    // Footer
    y += 4
    doc.setDrawColor(200, 200, 200)
    doc.line(margin, y, pageWidth - margin, y)
    y += 6
    addWrapped('FrankieOne — Identity Verification Infrastructure', 9, { color: [140, 140, 140] })

    doc.save('FrankieOne_POC_Guide.pdf')
    showToast('PDF exported', 'success')
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative w-full max-w-2xl bg-[var(--bg-primary)] border-l border-[var(--border-color)] shadow-2xl overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-[var(--bg-primary)] border-b border-[var(--border-color)] px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-cyan-400" />
            <h2 className="text-base font-semibold text-[var(--text-primary)]">POC Guide for Clients</h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={copyToClipboard} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)] border border-[var(--border-color)] hover:bg-[var(--bg-secondary)] transition-colors">
              <Copy size={14} /> {copied ? 'Copied!' : 'Copy'}
            </button>
            <button onClick={exportPdf} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/20 transition-colors">
              <FileDown size={14} /> Export PDF
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-colors"><X size={18} /></button>
          </div>
        </div>
        <div className="px-6 py-5 space-y-6 text-sm text-[var(--text-secondary)]">
          <div>
            <h1 className="text-xl font-bold text-[var(--text-primary)]">Proof of Concept (POC)</h1>
            <p className="text-base text-[var(--text-muted)] mt-1">What to Expect</p>
            <p className="mt-3">FrankieOne runs POCs to demonstrate identity verification match rates across multiple vendors and countries. Here's how the process works.</p>
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-bold text-cyan-400">1. Secure Data Transfer</h3>
            <p>The first step is setting up a secure method to transfer the data files that will be used in the POC. There are a few options:</p>
            <ul className="list-disc pl-5 space-y-1 text-[var(--text-muted)]">
              <li><strong>SFTP</strong> — FrankieOne provides a secure SFTP server. You'll need to send us an SSH public key and your external IP address so we can set up access for you.</li>
              <li><strong>Cloud storage</strong> — You set up a secure bucket on your side and grant FrankieOne access.</li>
              <li><strong>Direct file transfer</strong> — You send data files directly as CSVs via a secure channel (e.g. encrypted email).</li>
            </ul>
            <p className="text-[var(--text-muted)]">Whichever method works best for you — we'll coordinate to make sure the transfer is secure and straightforward.</p>

            <div className="mt-2 rounded border border-cyan-500/30 bg-cyan-500/5 p-3 space-y-1.5">
              <p className="text-xs font-semibold text-cyan-400">If using SFTP, please send FrankieOne:</p>
              <ul className="list-disc pl-5 space-y-0.5 text-xs text-[var(--text-muted)]">
                <li>Your <strong>SSH public key</strong> (typically found at <code className="text-cyan-400">~/.ssh/id_rsa.pub</code> or <code className="text-cyan-400">C:\Users\YourUsername\.ssh\id_rsa.pub</code>)</li>
                <li>Your <strong>external IP address</strong> (the IP you'll be connecting from)</li>
              </ul>
              <p className="text-[10px] text-[var(--text-muted)] mt-1">If you don't already have an SSH key pair, your IT team can generate one for you. Once we receive these details, we'll set up your access and provide you with the SFTP server address and username.</p>
            </div>
          </div>

          <div className="space-y-1">
            <h3 className="text-sm font-bold text-cyan-400">2. Data Validation</h3>
            <p>Please ensure that the data you provide is an accurate representation of the data you have obtained from your customers. The quality and representativeness of the sample directly impacts the usefulness of the POC results. If the data is not representative of your real customer base, the match rates we see in the POC may not reflect what you'd see in production.</p>
          </div>

          <div className="space-y-1">
            <h3 className="text-sm font-bold text-cyan-400">3. Define the Terms</h3>
            <p>Send FrankieOne information about what you'd like to test during the POC:</p>
            <ul className="list-disc pl-5 space-y-1 text-[var(--text-muted)]">
              <li>What are your ideal match rates?</li>
              <li>Which countries do you need to verify customers in?</li>
              <li>What should the match criteria be? For example:
                <ul className="list-disc pl-5 mt-1 space-y-0.5">
                  <li>Name and Date of Birth</li>
                  <li>Name and Address</li>
                  <li>A combination of both</li>
                </ul>
              </li>
              <li>Are there any other criteria that would be required to consider a result a genuine match?</li>
            </ul>
            <p className="mt-2">FrankieOne will confirm these details back to you via email. Please review and confirm that email so we're aligned before we proceed.</p>
          </div>

          <div className="space-y-1">
            <h3 className="text-sm font-bold text-cyan-400">4. Timelines</h3>
            <p>Generally speaking, POCs take at least two weeks from the point we receive your data and confirmation. We typically approach multiple vendors simultaneously, and coordinating across all of them takes time. We appreciate your patience during this process.</p>
          </div>

          <div className="space-y-1">
            <h3 className="text-sm font-bold text-cyan-400">5. Vendor Coordination & Follow-ups</h3>
            <p>During the POC, we may hear back from vendors with questions about the data or the requirements. If that happens, we may send you additional requests for clarification on their behalf. Quick responses help keep things on track.</p>
          </div>

          <div className="space-y-1">
            <h3 className="text-sm font-bold text-cyan-400">6. Results & Presentation</h3>
            <p>Once all of the data comes back from the vendors, FrankieOne will process and analyse it. This includes:</p>
            <ul className="list-disc pl-5 space-y-1 text-[var(--text-muted)]">
              <li>Calculating pass rates for each vendor individually.</li>
              <li>Simulating cascades — if you're looking at hitting multiple vendors through FrankieOne, we'll project combined pass rates based on each vendor's individual results.</li>
            </ul>
            <p className="mt-2">After processing, we'll create a presentation summarising our findings and book a meeting to walk you through the results and recommendations.</p>
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-bold text-cyan-400">Frequently Asked Questions — SFTP</h3>

            <div className="rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 space-y-2">
              <p className="text-xs font-semibold text-[var(--text-primary)]">Q: Is the SFTP endpoint shared or dedicated to my organisation?</p>
              <p className="text-xs text-[var(--text-muted)]">The SFTP endpoint (<code className="text-cyan-400">sftp.frankie.one</code> for production, <code className="text-cyan-400">sftp.uat.frankie.one</code> for UAT, port 22) is shared infrastructure. However, each customer is logically isolated through:</p>
              <ul className="list-disc pl-5 space-y-0.5 text-xs text-[var(--text-muted)]">
                <li>A unique <strong>username</strong> per customer</li>
                <li>A dedicated <strong>S3 bucket</strong> per customer</li>
                <li><strong>IP whitelisting</strong> — security groups restrict inbound access to only your declared egress CIDRs</li>
                <li><strong>Per-customer SSH keys</strong> — only your registered public key can authenticate your user</li>
              </ul>
              <p className="text-xs text-[var(--text-muted)]">So while the hostname is shared, access is fully scoped: you can only reach your own bucket and cannot see other customers' data.</p>
            </div>

            <div className="rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 space-y-2">
              <p className="text-xs font-semibold text-[var(--text-primary)]">Q: What is the SSH key management policy?</p>
              <p className="text-xs text-[var(--text-muted)]">You generate your own SSH keypair and share only the public key with FrankieOne. Key rotation is on-demand — there is no mandatory rotation cadence, but keys should be rotated immediately if a compromise is suspected.</p>
            </div>

            <div className="rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 space-y-2">
              <p className="text-xs font-semibold text-[var(--text-primary)]">Q: What is the exact scope of the IP whitelist?</p>
              <p className="text-xs text-[var(--text-muted)]">The IP whitelist is per-customer and per-direction. It controls which source IPs are permitted to connect inbound to FrankieOne's SFTP server on port 22. Whitelisting is applied separately for both UAT and production environments. You will need to provide FrankieOne with your egress IP addresses (or CIDR ranges) so we can configure access.</p>
            </div>

            <div className="rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 space-y-2">
              <p className="text-xs font-semibold text-[var(--text-primary)]">Q: Where can I find FrankieOne's encryption policy?</p>
              <p className="text-xs text-[var(--text-muted)]">You can find our encryption policy on the FrankieOne Trust Center.</p>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-bold text-cyan-400">Frequently Asked Questions — Privacy & Data</h3>

            <div className="rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 space-y-2">
              <p className="text-xs font-semibold text-[var(--text-primary)]">Q: What is FrankieOne's designated privacy/data protection email address (e.g. for DPA purposes)?</p>
              <p className="text-xs text-[var(--text-muted)]">Privacy inquiries and DPA-related correspondence should be directed to: <a href="mailto:privacy@frankieone.com" className="text-cyan-400 hover:underline">privacy@frankieone.com</a></p>
            </div>

            <div className="rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 space-y-2">
              <p className="text-xs font-semibold text-[var(--text-primary)]">Q: Can FrankieOne provide a written deletion attestation confirming that all transferred data will be deleted after the evaluation is complete?</p>
              <p className="text-xs text-[var(--text-muted)]">Yes. FrankieOne's Implementations Team will provide written confirmation that all POC data has been deleted once the evaluation is complete. This is managed by the Implementations Team as part of the standard POC wind-down process.</p>
            </div>
          </div>

          <div className="pt-4 border-t border-[var(--border-color)] text-xs text-[var(--text-muted)]">
            FrankieOne — Identity Verification Infrastructure
          </div>
        </div>
      </div>
    </div>
  )
}

export default PocTool
